<template>
  <div class="cashbook-page bankbook-test-page">
    <!-- 저장 완료 토스트 -->
    <Transition name="toast">
      <div v-if="showSaveToast" class="save-toast">
        <v-icon size="18" color="white">mdi-check-circle</v-icon>
        <span>저장되었습니다</span>
      </div>
    </Transition>
    
    <!-- 로딩 -->
    <div v-if="isLoading" class="cashbook-loading">
      <v-progress-circular indeterminate color="primary" size="48" />
      <p>통장내역 데이터 로딩 중...</p>
    </div>

    <div class="adjust-shell" v-else>
      <header class="work-header">
        <!-- 상단 스트립: 연월 · 통장 · 액션 -->
        <div class="work-top">
          <div class="wt-ym" data-tour="bb-test-ym">
            <button type="button" class="wt-ym-nav" @click="changeMonth(-1)" title="이전 달">
              <v-icon size="18">mdi-chevron-left</v-icon>
            </button>
            <button type="button" class="wt-ym-value" @click="openMonthPicker">
              <span class="wt-ym-y">{{ currentYear }}</span><span class="wt-ym-sep">.</span><span class="wt-ym-m">{{ currentMonth }}</span>
              <input
                type="month"
                ref="monthPickerRef"
                :value="`${currentYear}-${currentMonth}`"
                @change="onMonthPickerChange"
                class="hidden-month-picker"
              />
            </button>
            <button type="button" class="wt-ym-nav" @click="changeMonth(1)" title="다음 달">
              <v-icon size="18">mdi-chevron-right</v-icon>
            </button>
          </div>

          <div class="wt-banks" data-tour="bb-test-banks">
            <button
              type="button"
              class="wt-bank all"
              :class="{ active: selectedBankCode === null }"
              @click="selectBank(null)"
            >
              <v-icon size="13">mdi-view-grid-outline</v-icon>
              <span>전체</span>
            </button>
            <button
              v-for="bank in activeBankListForModal"
              :key="bank.mb_code"
              type="button"
              class="wt-bank"
              :class="{ active: selectedBankCode === bank.mb_code, closed: isBankClosed(bank.mb_code) }"
              @click="selectBank(bank.mb_code)"
            >
              <v-icon size="13">{{ isBankClosed(bank.mb_code) ? 'mdi-lock' : 'mdi-bank' }}</v-icon>
              <span>{{ bank.mb_alias }}</span>
            </button>
            <button type="button" class="wt-bank-add" data-tour="bb-test-bank-manage" @click="openBankManageDialog" title="통장 관리">
              <v-icon size="14">mdi-cog-outline</v-icon>
            </button>
          </div>

          <div class="wt-actions">
            <BankbookFeatureSpecPanel :spec="bankbookFeatureSpec" :guide-host="bankbookGuideHost" />
            <div class="wt-closing-slot" data-tour="bb-test-closing">
              <button
                v-if="canOpenClosing"
                type="button"
                class="wt-action primary"
                @click="openClosingDialog"
              >
                <v-icon size="14">mdi-lock-check</v-icon>
                이 달 마감
              </button>
              <div v-else-if="closingStatus?.isClosed" class="wt-closed-badge">
                <v-icon size="14">mdi-lock</v-icon>
                마감됨
              </div>
              <span v-else class="wt-closing-idle" title="통장 선택·내역이 있으면 마감할 수 있습니다">마감</span>
            </div>
            <v-menu
              v-if="hasMoreMenuItems"
              location="bottom end"
              :close-on-content-click="true"
            >
              <template #activator="{ props }">
                <button v-bind="props" type="button" class="wt-more" title="더보기" data-tour="bb-test-more">
                  <v-icon size="18">mdi-dots-horizontal</v-icon>
                </button>
              </template>
              <v-list density="compact" class="wt-more-menu">
                <v-list-item
                  v-if="closingStatus?.isClosed && isAdmin"
                  @click="confirmUnclose"
                  :disabled="isClosing"
                >
                  <template #prepend><v-icon size="18">mdi-lock-open-outline</v-icon></template>
                  <v-list-item-title>마감 해제</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="isSuperAdmin && selectedBankCode" @click="generateTestData" :disabled="isGeneratingTestData">
                  <template #prepend><v-icon size="18">mdi-flask-outline</v-icon></template>
                  <v-list-item-title>테스트 자료 생성</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>

        <!-- 작업 단계 (계정조정·자금원천은 통장내역 우측에서 처리) -->
        <nav class="work-steps" aria-label="작업 단계" data-tour="bb-test-steps">
          <button
            type="button"
            class="work-step"
            data-tour="bb-test-raw-tab"
            :class="{
              active: workSection === 'raw',
              todo: hasNoBankbookData || pendingAdjustCount > 0 || unfundedCount > 0,
              done: !hasNoBankbookData && pendingAdjustCount === 0 && unfundedCount === 0
            }"
            @click="goStep('raw')"
          >
            <span class="ws-num">1</span>
            <span class="ws-body">
              <span class="ws-name">통장내역</span>
              <span v-if="hasNoBankbookData" class="ws-meta">업로드 필요</span>
              <span v-else-if="pendingAdjustCount === 0 && unfundedCount === 0" class="ws-meta done">완료</span>
            </span>
          </button>
          <button
            type="button"
            class="work-step"
            data-tour="bb-test-collection-tab"
            :class="{
              active: isCollectionStep,
              todo: !hasNoBankbookData && collectionPendingCount > 0,
              done: !hasNoBankbookData && collectionPendingCount === 0,
              disabled: hasNoBankbookData
            }"
            :disabled="hasNoBankbookData"
            :title="hasNoBankbookData ? '통장내역을 먼저 업로드해주세요' : ''"
            @click="goStep('collection')"
          >
            <span class="ws-num">2</span>
            <span class="ws-body">
              <span class="ws-name">수납</span>
              <span v-if="hasNoBankbookData" class="ws-meta">-</span>
              <span v-else-if="collectionPendingCount === 0" class="ws-meta done">완료</span>
            </span>
          </button>
          <button
            type="button"
            class="work-step"
            data-tour="bb-test-receipt-tab"
            :class="{
              active: isReceiptStep,
              todo: !hasNoBankbookData && receiptPendingCount > 0,
              done: !hasNoBankbookData && receiptPendingCount === 0,
              disabled: hasNoBankbookData
            }"
            :disabled="hasNoBankbookData"
            :title="hasNoBankbookData ? '통장내역을 먼저 업로드해주세요' : ''"
            @click="goStep('receipt')"
          >
            <span class="ws-num">3</span>
            <span class="ws-body">
              <span class="ws-name">영수증</span>
              <span v-if="hasNoBankbookData" class="ws-meta">-</span>
              <span v-else-if="receiptPendingCount === 0" class="ws-meta done">완료</span>
            </span>
          </button>
          <button
            type="button"
            class="work-step"
            data-tour="bb-test-analysis-tab"
            :class="{
              active: isAnalysisReferenceStep,
              disabled: hasNoBankbookData
            }"
            :disabled="hasNoBankbookData"
            :title="hasNoBankbookData ? '통장내역을 먼저 업로드해주세요' : '정리 내역 보기'"
            @click="goStep('analysis-reference')"
          >
            <span class="ws-num">4</span>
            <span class="ws-body">
              <span class="ws-name">정리 내역 보기</span>
            </span>
          </button>
        </nav>
      </header>

    <!-- ═════ 좌측 리모컨 + 우측 컨텐츠 (모든 탭 공통 레이아웃) ═════ -->
    <div class="cashbook-layout">
      <!-- 고정 사이드바 (영수증 탭은 매칭 화면이 전체 영역을 씀) -->
      <aside v-if="!isReceiptStep && !isAnalysisReferenceStep" class="cashbook-sidebar">
        <!-- 페이지 타이틀 배지 -->
        <div class="sidebar-title-badge">
          <v-icon size="14">mdi-book-open-page-variant</v-icon>
          <span>{{ sidebarTitle }}</span>
        </div>

        <!-- 수입/지출 요약: 항목 목록이 있는 탭 공통 -->
        <div v-if="showItemSidebarTools" class="sidebar-summary" data-tour="bb-test-summary">
          <!-- 이월금 -->
          <div class="summary-item carryover">
            <div class="summary-header">
              <v-icon size="14" color="#6366f1">mdi-arrow-right-bold</v-icon>
              <span class="label">이월금</span>
            </div>
            <span class="value prev">{{ formatNumber(prevMonthBalance) }}원</span>
          </div>
          <div class="summary-divider"></div>
          <!-- 수입 -->
          <div class="summary-item income">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">수입</span>
            </div>
            <span class="value">{{ formatNumber(summaryData.income) }}원</span>
          </div>
          <!-- 지출 -->
          <div class="summary-item expense">
            <div class="summary-header">
              <span class="dot"></span>
              <span class="label">지출</span>
            </div>
            <span class="value">{{ formatNumber(summaryData.expense) }}원</span>
          </div>
          <div class="summary-divider"></div>
          <!-- 누적잔액 -->
          <div class="summary-item balance" :class="{ warning: totalBalance < 0 }">
            <div class="summary-header">
              <v-icon size="14" :color="totalBalance >= 0 ? '#1e3a5f' : '#4b5563'">
                {{ totalBalance >= 0 ? 'mdi-check-circle' : 'mdi-alert-circle-outline' }}
              </v-icon>
              <span class="label">누적잔액</span>
            </div>
            <span class="value">{{ formatNumber(totalBalance) }}원</span>
          </div>
        </div>

        <!-- 인건비비율 (잔액 요약과 분리) -->
        <button
          v-if="showItemSidebarTools"
          type="button"
          class="sidebar-labor-ratio"
          data-tour="bb-test-labor"
          :class="{
            met: laborRatioSummary.met,
            warn: laborRatioSummary.hasIncome && !laborRatioSummary.met,
            empty: !laborRatioSummary.hasIncome,
          }"
          :title="laborRatioSummary.hint"
          @click="laborRatioModalOpen = true"
        >
          <div class="lr-top">
            <span class="lr-title">인건비비율</span>
            <span v-if="laborRatioSummary.cumulativeLabel" class="lr-period">{{ laborRatioSummary.cumulativeLabel }}</span>
            <span v-if="laborRatioSummary.hasIncome" class="lr-badge">
              {{ laborRatioSummary.met ? '충족' : '미충족' }}
            </span>
            <span v-else class="lr-badge muted">계산불가</span>
          </div>
          <div class="lr-bar" aria-hidden="true">
            <span
              class="lr-bar-fill"
              :style="{ width: laborRatioSummary.barPct + '%' }"
            ></span>
          </div>
          <div class="lr-nums">
            <template v-if="laborRatioSummary.hasIncome">
              <span class="lr-actual">{{ laborRatioSummary.ratioText }}%</span>
              <span class="lr-base">기준 {{ laborRatioSummary.requiredText }}%</span>
            </template>
            <span v-else class="lr-empty">인건비수입 없음</span>
          </div>
        </button>

        <!-- 조건: 통장내역 (표시·분개·검색을 한 섹션) -->
        <div v-if="usesSharedLedger" class="sidebar-conditions" data-tour="bb-test-conditions">
          <div class="cond-head">
            <v-icon size="14" color="#64748b">mdi-filter-variant</v-icon>
            <span>조건</span>
            <button
              v-if="hasActiveLedgerConditions"
              type="button"
              class="cond-reset"
              title="조건 초기화"
              @click="clearAllSearch"
            >
              초기화
            </button>
          </div>

          <div class="cond-row">
            <span class="cond-label">보기</span>
            <div class="cond-opts">
              <button
                type="button"
                class="cond-opt"
                :class="{ on: ledgerDisplayMode === 'cashbook' }"
                @click="ledgerDisplayMode = 'cashbook'"
              >현금출납부</button>
              <button
                type="button"
                class="cond-opt"
                :class="{ on: ledgerDisplayMode === 'upload' }"
                @click="ledgerDisplayMode = 'upload'"
              >통장 원본</button>
            </div>
          </div>

          <div class="cond-row">
            <span class="cond-label">범위</span>
            <div class="cond-opts">
              <button
                type="button"
                class="cond-opt"
                :class="{ on: !filterSplitOnly }"
                @click="filterSplitOnly = false"
              >전체</button>
              <button
                type="button"
                class="cond-opt"
                :class="{ on: filterSplitOnly }"
                @click="filterSplitOnly = true"
              >분개만 <em v-if="splitFilterCount">{{ splitFilterCount }}</em></button>
            </div>
          </div>

          <div class="cond-row stacked">
            <span class="cond-label">기간</span>
            <div class="date-range-inputs day-only">
              <input
                type="number"
                class="day-input"
                v-model.number="filterDayStart"
                :min="1"
                :max="filterDayEnd || lastDayOfMonth"
                placeholder="1"
              />
              <span class="day-unit">일</span>
              <span class="date-separator">~</span>
              <input
                type="number"
                class="day-input"
                v-model.number="filterDayEnd"
                :min="filterDayStart || 1"
                :max="lastDayOfMonth"
                :placeholder="String(lastDayOfMonth)"
              />
              <span class="day-unit">일</span>
            </div>
          </div>

          <div class="cond-row stacked">
            <span class="cond-label">계정</span>
            <div class="search-input-wrapper">
              <input
                type="text"
                class="search-input"
                v-model="searchScode"
                placeholder="코드·이름"
              />
              <button v-if="searchScode" type="button" class="search-clear" @click="searchScode = ''">
                <v-icon size="12">mdi-close</v-icon>
              </button>
            </div>
          </div>

          <div class="cond-row stacked">
            <span class="cond-label">적요</span>
            <div class="search-input-wrapper">
              <input
                type="text"
                class="search-input"
                v-model="searchMemo"
                placeholder="검색"
              />
              <button v-if="searchMemo" type="button" class="search-clear" @click="searchMemo = ''">
                <v-icon size="12">mdi-close</v-icon>
              </button>
            </div>
          </div>
        </div>


        <!-- 희망이음 업로드 섹션 (계정조정 마감 후 표시, 통장 선택 시만) -->
        <div v-if="closingStatus?.isClosed && selectedBankCode" class="sidebar-closing-section" data-tour="bb-test-heumieum">
          <div class="closing-header">
            <span>희망이음</span>
            <span class="closing-status-text" :class="{ 
              requested: heumieumStatus?.status === 'requested',
              closed: heumieumStatus?.status === 'completed' 
            }">
              {{ heumieumStatusText }}
            </span>
          </div>
          <div v-if="heumieumStatus?.requestAt" class="closing-info">
            <small>{{ heumieumStatus.status === 'completed' ? '완료' : '요청' }}: {{ formatClosingDate(heumieumStatus.requestAt) }}</small>
          </div>
          <div v-if="heumieumStatus?.completeMemo" class="closing-info">
            <small>{{ heumieumStatus.completeMemo }}</small>
          </div>
          <div class="closing-actions">
            <!-- 업로드 요청 버튼 (미요청 상태) -->
            <button 
              v-if="!heumieumStatus?.status"
              class="closing-btn"
              @click="requestHeumieumUpload"
              :disabled="isRequestingHeumieum"
            >
              업로드 요청
            </button>
            <!-- 요청 취소 버튼 (요청중 상태) -->
            <button 
              v-if="heumieumStatus?.status === 'requested'"
              class="closing-btn secondary"
              @click="cancelHeumieumUpload"
              :disabled="isRequestingHeumieum"
            >
              요청취소
            </button>
            <!-- 완료 상태 표시 -->
            <div v-if="heumieumStatus?.status === 'completed'" class="closing-complete">
              <small>업로드 완료</small>
            </div>
          </div>
        </div>

        <!-- 탭별 기능 (조건 아래 · 카드형 정리) -->
        <div v-if="workSection === 'raw' || isCollectionStep" class="sidebar-actions-panel">
          <div class="sap-head">
            <v-icon size="14" color="#64748b">mdi-flash-outline</v-icon>
            <span>기능</span>
          </div>

          <!-- 통장내역 -->
          <template v-if="workSection === 'raw'">
            <div class="sap-block">
              <div class="sap-label">자료</div>
              <div class="sap-grid duo">
                <button
                  type="button"
                  class="sap-btn"
                  data-tour="bb-test-upload-quick"
                  @click="openUploadDialog"
                  :disabled="isBankbookUploadDisabled"
                  title="통장내역 업로드"
                >
                  <v-icon size="14">mdi-upload</v-icon>
                  <span>업로드</span>
                </button>
                <button
                  type="button"
                  class="sap-btn"
                  data-tour="bb-test-settlement"
                  @click="settlementModalOpen = true"
                  title="세입·세출 결산 (예산/결산/증감)"
                >
                  <v-icon size="14">mdi-table-large</v-icon>
                  <span>결산</span>
                </button>
              </div>
            </div>

            <div
              v-if="!isConfirmed && !closingStatus?.isClosed && filteredCashbookList.length > 0"
              class="sap-block"
            >
              <button
                type="button"
                class="sap-btn danger"
                data-tour="bb-test-delete-all"
                @click="deleteAllDialogOpen = true"
                :title="`모두삭제 (${filteredCashbookList.length}건)`"
              >
                <v-icon size="14">mdi-delete-sweep</v-icon>
                <span>모두삭제 {{ filteredCashbookList.length }}건</span>
              </button>
            </div>
          </template>

          <!-- 수납 -->
          <template v-else-if="isCollectionStep">
            <div class="sap-block">
              <div class="sap-label">수납</div>
              <button
                type="button"
                class="sap-btn"
                data-tour="bb-test-collection-excel"
                @click="collectionExcelModalOpen = true"
                :disabled="isCollectionExcelDisabled"
                title="수납관리 엑셀업로드"
              >
                <v-icon size="14">mdi-file-excel</v-icon>
                <span>엑셀</span>
              </button>
            </div>
          </template>
        </div>

        <!-- 이달의 메모 (사이드바 하단 고정) -->
        <div class="sidebar-month-memo" data-tour="bb-test-memo" :class="{ open: monthMemoOpen, filled: !!monthMemoText.trim() }">
          <button
            type="button"
            class="month-memo-toggle"
            @click="monthMemoOpen = !monthMemoOpen"
          >
            <span class="month-memo-left">
              <v-icon size="14">mdi-notebook-outline</v-icon>
              <span>이달의 메모</span>
              <em v-if="monthMemoText.trim()" class="month-memo-dot" aria-hidden="true"></em>
            </span>
            <span class="month-memo-right">
              <span v-if="monthMemoSaveState === 'saving'" class="month-memo-status">저장중</span>
              <span v-else-if="monthMemoSaveState === 'saved'" class="month-memo-status ok">저장됨</span>
              <span v-else-if="monthMemoSaveState === 'error'" class="month-memo-status err">실패</span>
              <v-icon size="16">{{ monthMemoOpen ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
            </span>
          </button>
          <div v-show="monthMemoOpen" class="month-memo-body">
            <textarea
              v-model="monthMemoText"
              class="month-memo-input"
              maxlength="1000"
              rows="5"
              placeholder="이 달 작업 메모를 남겨두세요."
              :disabled="isMonthMemoLoading || closingStatus?.isClosed"
              @input="onMonthMemoInput"
            ></textarea>
            <div class="month-memo-footer">
              <span class="month-memo-count">{{ monthMemoText.length }}/1000</span>
              <button
                type="button"
                class="month-memo-save"
                :disabled="isMonthMemoLoading || monthMemoSaveState === 'saving' || closingStatus?.isClosed"
                @click="saveMonthMemoNow"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      </aside>
      
      <!-- 우측: 메인 컨텐츠 (탭별 뷰) -->
      <main
        class="cashbook-main-content"
        :class="{
          'is-panel': workSection === 'raw' || isCollectionStep || isReceiptStep || isAnalysisReferenceStep,
          'has-shared-ledger': usesSharedLedger,
        }"
      >
        <!-- ── 공통 통장내역 + 탭별 작업 (raw / adjust / funding) ── -->
        <div v-if="usesSharedLedger" class="shared-split">
          <!-- 시작 가이드: 이 달 데이터가 아예 없을 때 원장+작업대 대신 전체 폭 사용 -->
          <section v-if="showStartGuide" class="start-guide">
            <div class="sg-head">
              <div class="sg-badge">
                <v-icon size="34">mdi-notebook-edit-outline</v-icon>
              </div>
              <p class="sg-title">통장내역 정리를 시작해 볼까요?</p>
              <p class="sg-sub">{{ currentYear }}년 {{ currentMonth }}월 · 통장을 등록하고 거래내역을 올리면 시작됩니다</p>
            </div>
            <div class="sg-desc">
              <div class="sg-desc-row">
                <span class="sg-desc-label">목적</span>
                <p>수납·영수증·마감·회계보고서의 기준이 되는 수입·지출 원장 만들기</p>
              </div>
              <div class="sg-desc-row">
                <span class="sg-desc-label">준비물</span>
                <p>통장 등록 · 은행 거래내역 파일(엑셀·CSV)</p>
              </div>
              <div class="sg-desc-row">
                <span class="sg-desc-label">쓰이는 곳</span>
                <p>수납(본인부담금) · 영수증 매칭 · 이 달 마감 · 회계보고서</p>
              </div>
            </div>
            <ol class="sg-steps">
              <li :class="{ done: bankList.length > 0, current: bankList.length === 0 }">
                <span class="sg-step-num">1</span>
                <div class="sg-icon">
                  <v-icon size="26">mdi-bank-outline</v-icon>
                </div>
                <strong>통장 등록</strong>
                <p>시설에서 사용하는 통장을 먼저 등록합니다</p>
                <button
                  v-if="bankList.length === 0"
                  type="button"
                  class="sg-btn"
                  @click="openBankManageDialog"
                >
                  <v-icon size="15">mdi-cog-outline</v-icon> 통장 관리 열기
                </button>
                <span v-else class="sg-done-label">
                  <v-icon size="15">mdi-check-circle</v-icon> 등록 완료
                </span>
              </li>
              <li :class="{ current: bankList.length > 0, dim: bankList.length === 0 }">
                <span class="sg-step-num">2</span>
                <div class="sg-icon">
                  <v-icon size="26">mdi-upload-outline</v-icon>
                </div>
                <strong>통장내역 업로드</strong>
                <p>은행 거래내역 엑셀·CSV를 올리거나 직접 입력합니다</p>
                <button
                  v-if="bankList.length > 0"
                  type="button"
                  class="sg-btn primary"
                  :disabled="isBankbookUploadDisabled"
                  @click="openUploadDialog"
                >
                  <v-icon size="15">mdi-upload</v-icon> 통장내역 업로드
                </button>
              </li>
            </ol>
            <p class="sg-after">
              <v-icon size="14">mdi-information-outline</v-icon>
              업로드가 끝나면 이어서 계정 지정 → 수납·영수증 정리 → 마감 순으로 진행됩니다
            </p>
          </section>

          <section v-show="!showStartGuide" class="shared-ledger" data-tour="bb-test-ledger">
            <!-- 수입/지출 전환 (공통 목록 탭) -->
            <div class="sl-tabs" role="tablist">
              <button
                type="button"
                class="sl-tab is-in"
                :class="{ on: ledgerListTab === 'in' }"
                @click="setLedgerListTab('in')"
              >
                수입
                <em v-if="ledgerTabInBadge">{{ ledgerTabInBadge }}</em>
              </button>
              <button
                type="button"
                class="sl-tab is-out"
                :class="{ on: ledgerListTab === 'out' }"
                @click="setLedgerListTab('out')"
              >
                지출
                <em v-if="ledgerTabOutBadge">{{ ledgerTabOutBadge }}</em>
              </button>
            </div>

            <div v-if="sharedLedgerRows.length === 0" class="raw-empty">
              <v-icon size="40" color="#94a3b8">mdi-inbox-outline</v-icon>
              <p>{{ sharedLedgerEmptyMessage }}</p>
            </div>

            <div v-else class="cashbook-table-wrapper shared-ledger-table-wrap">
              <table class="cashbook-table shared-ledger-table">
                <thead>
                  <tr>
                    <th class="th-check">
                      <button
                        type="button"
                        class="row-check header-check"
                        :class="{
                          on: isAllVisibleLedgerSelected,
                          partial: isSomeVisibleLedgerSelected && !isAllVisibleLedgerSelected,
                        }"
                        :disabled="!selectableVisibleLedgerIds.length"
                        :title="isAllVisibleLedgerSelected ? '선택 해제' : '모두 선택'"
                        :aria-label="isAllVisibleLedgerSelected ? '선택 해제' : '모두 선택'"
                        @click.stop="toggleSelectAllVisibleLedger"
                      >
                        <v-icon size="14">
                          {{
                            isAllVisibleLedgerSelected
                              ? 'mdi-checkbox-marked'
                              : isSomeVisibleLedgerSelected
                                ? 'mdi-minus-box'
                                : 'mdi-checkbox-blank-outline'
                          }}
                        </v-icon>
                      </button>
                    </th>
                    <th class="th-date sortable" :class="{ on: sortKey === 'cb_order' }" @click="toggleSort('cb_order')">
                      날짜
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('cb_order') }}</v-icon>
                    </th>
                    <th class="th-gubun">구분</th>
                    <th class="th-memo sortable" :class="{ on: sortKey === 'cb_memo' }" @click="toggleSort('cb_memo')">
                      적요
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('cb_memo') }}</v-icon>
                    </th>
                    <th class="th-price sortable" :class="{ on: sortKey === 'cb_price' }" @click="toggleSort('cb_price')">
                      금액
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('cb_price') }}</v-icon>
                    </th>
                    <th class="th-scode sortable" :class="{ on: sortKey === 'cb_scode' }" @click="toggleSort('cb_scode')">
                      계정
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('cb_scode') }}</v-icon>
                    </th>
                    <th class="th-rel-acc sortable" :class="{ on: sortKey === 'cb_rel_acc' }" @click="toggleSort('cb_rel_acc')">
                      {{ ledgerListTab === 'in' ? '수급자' : '상대계정' }}
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('cb_rel_acc') }}</v-icon>
                    </th>
                    <th class="th-funding sortable" :class="{ on: sortKey === 'cb_funding' }" @click="toggleSort('cb_funding')">
                      자금원천
                      <v-icon size="14" class="sort-icon">{{ getSortIcon('cb_funding') }}</v-icon>
                    </th>
                    <th v-if="selectedBankCode === null" class="th-bank">통장</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="row in sharedLedgerRows" :key="row.key">
                    <!-- 일반 행 / 출납부형 분개 자식 -->
                    <tr
                      v-if="row.kind === 'item'"
                      :class="{
                        'row-picked': isSharedRowPicked(row.item),
                        'row-closed': !isItemEditable(row.item),
                        'is-clickable': true,
                      }"
                      @click="onSharedLedgerClick(row.item)"
                    >
                      <td class="td-check" @click.stop="onSharedLedgerClick(row.item)">
                        <span
                          class="row-check"
                          :class="{
                            on: isSharedRowPicked(row.item),
                            disabled: !isItemEditable(row.item),
                          }"
                          aria-hidden="true"
                        >
                          <v-icon size="14">{{ isSharedRowPicked(row.item) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
                        </span>
                      </td>
                      <td class="td-date">{{ formatDate(row.item.cb_date) }}</td>
                      <td class="td-gubun">
                        <span
                          class="gubun-badge"
                          :class="row.item.cb_gubun === 'IN' ? 'income' : 'expense'"
                        >
                          {{ row.item.cb_gubun === 'IN' ? '수입' : '지출' }}
                        </span>
                      </td>
                      <td class="td-memo">
                        <div class="memo-container">
                          <span
                            v-if="row.item.cb_memo && row.item.cb_memo.trim()"
                            class="memo-text"
                            :title="row.item.cb_memo"
                          >{{ row.item.cb_memo }}</span>
                          <span v-else class="memo-missing">
                            <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                            미설정
                          </span>
                        </div>
                      </td>
                      <td class="td-price" :class="row.item.cb_gubun === 'IN' ? 'income' : 'expense'">
                        {{ formatNumber(row.item.cb_price) }}원
                      </td>
                      <td class="td-scode">
                        <span v-if="hasLedgerScode(row.item)" class="scode-name" :class="row.item.cb_gubun === 'IN' ? 'is-in' : 'is-out'">{{ ledgerScodeLabel(row.item) }}</span>
                        <span v-else class="scode-missing">
                          <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                          미설정
                        </span>
                      </td>
                      <td class="td-rel-acc" @click.stop>
                        <template v-if="row.item.cb_gubun === 'OUT'">
                          <span v-if="hasLedgerRel(row.item)" class="rel-acc-name is-in">{{ ledgerRelLabel(row.item) }}</span>
                          <span v-else class="rel-acc-missing">
                            <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                            미설정
                          </span>
                        </template>
                        <template v-else-if="isCollectionTarget(row.item) && row.item.cb_idx">
                          <v-menu
                            v-if="beneficiaryMatches[row.item.cb_idx]"
                            location="bottom start"
                            :close-on-content-click="true"
                          >
                            <template #activator="{ props: menuProps }">
                              <button
                                type="button"
                                v-bind="menuProps"
                                class="ben-match-link matched"
                                :title="`${beneficiaryMatches[row.item.cb_idx]?.cb_name} · 수급자 옵션`"
                              >
                                <span class="ben-match-name">{{ beneficiaryMatches[row.item.cb_idx]?.cb_name }}</span>
                              </button>
                            </template>
                            <v-list density="compact" class="ben-match-menu">
                              <v-list-item @click="openCollectionForBeneficiary(beneficiaryMatches[row.item.cb_idx]?.cb_name || '')">
                                <template #prepend>
                                  <v-icon size="18" color="#0d9488">mdi-cash-register</v-icon>
                                </template>
                                <v-list-item-title>수납 탭에서 보기</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="changeBeneficiaryMatch(row.item)">
                                <template #prepend>
                                  <v-icon size="18" color="#1565c0">mdi-account-switch</v-icon>
                                </template>
                                <v-list-item-title>수급자 변경</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="removeBeneficiaryMatch(row.item)">
                                <template #prepend>
                                  <v-icon size="18" color="#c62828">mdi-link-off</v-icon>
                                </template>
                                <v-list-item-title>매칭 해제</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                          <button
                            v-else
                            type="button"
                            class="ben-match-link need"
                            title="수급자가 아직 지정되지 않았습니다. 클릭하여 지정"
                            @click="openBeneficiaryMatchPopover(row.item)"
                          >
                            <span class="ben-match-name">미연결</span>
                          </button>
                        </template>
                        <span v-else class="rel-acc-na">-</span>
                      </td>
                      <td class="td-funding">
                        <span v-if="row.item.cb_funding" class="funding-name">{{ getFundingName(row.item.cb_funding) }}</span>
                        <span v-else class="funding-missing">
                          <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                          미설정
                        </span>
                      </td>
                      <td v-if="selectedBankCode === null" class="td-bank">
                        <span class="bank-badge">{{ getBankAlias(row.item.cb_bank_code) }}</span>
                      </td>
                    </tr>

                    <!-- 통장원본형: 분개 묶음 (접힌 원본) -->
                    <template v-else-if="row.kind === 'group'">
                      <tr
                        class="row-split-group is-clickable"
                        :class="{
                          'row-picked': isSharedGroupPicked(row.group),
                          expanded: isSplitGroupExpanded(row.group.parentIdx),
                        }"
                        @click="onSharedGroupClick(row.group)"
                      >
                        <td class="td-check" @click.stop="onSharedGroupClick(row.group)">
                          <span
                            class="row-check"
                            :class="{ on: isSharedGroupPicked(row.group) }"
                            aria-hidden="true"
                          >
                            <v-icon size="14">{{ isSharedGroupPicked(row.group) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
                          </span>
                        </td>
                        <td class="td-date">
                          <button
                            type="button"
                            class="split-expand-btn"
                            :class="{ open: isSplitGroupExpanded(row.group.parentIdx) }"
                            :title="isSplitGroupExpanded(row.group.parentIdx) ? '분개 접기' : '분개 펼치기'"
                            @click.stop="toggleSplitGroup(row.group.parentIdx)"
                          >
                            <v-icon size="18">
                              {{ isSplitGroupExpanded(row.group.parentIdx) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                            </v-icon>
                            <span class="split-expand-text">
                              분개 {{ row.group.children.length }}
                            </span>
                          </button>
                          <span class="split-group-date">{{ formatDate(row.group.cb_date) }}</span>
                        </td>
                        <td class="td-gubun">
                          <span
                            class="gubun-badge"
                            :class="row.group.cb_gubun === 'IN' ? 'income' : 'expense'"
                          >
                            {{ row.group.cb_gubun === 'IN' ? '수입' : '지출' }}
                          </span>
                        </td>
                        <td class="td-memo">
                          <div class="memo-container">
                            <span
                              v-if="row.group.cb_memo && row.group.cb_memo.trim()"
                              class="memo-text"
                              :title="row.group.cb_memo"
                            >{{ row.group.cb_memo }}</span>
                            <span v-else class="memo-missing">
                              <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                              미설정
                            </span>
                          </div>
                        </td>
                        <td class="td-price" :class="row.group.cb_gubun === 'IN' ? 'income' : 'expense'">
                          {{ formatNumber(row.group.cb_price) }}원
                        </td>
                        <td class="td-scode">
                          <button
                            type="button"
                            class="split-expand-link"
                            :class="{ open: isSplitGroupExpanded(row.group.parentIdx) }"
                            @click.stop="toggleSplitGroup(row.group.parentIdx)"
                          >
                            {{ isSplitGroupExpanded(row.group.parentIdx) ? '접기' : '펼치기' }}
                          </button>
                        </td>
                        <td class="td-rel-acc">
                          <span class="rel-acc-na">-</span>
                        </td>
                        <td class="td-funding">
                          <span v-if="row.group.cb_funding" class="funding-name">{{ getFundingName(row.group.cb_funding) }}</span>
                          <span v-else class="funding-missing">
                            <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                            미설정
                          </span>
                        </td>
                        <td v-if="selectedBankCode === null" class="td-bank">
                          <span class="bank-badge">{{ getBankAlias(row.group.cb_bank_code) }}</span>
                        </td>
                      </tr>
                      <tr
                        v-for="child in (isSplitGroupExpanded(row.group.parentIdx) ? row.group.children : [])"
                        :key="`split-child-${child.cb_idx}`"
                        class="row-split-detail is-clickable"
                        :class="{
                          'row-picked': isSharedRowPicked(child),
                          'row-closed': !isItemEditable(child),
                        }"
                        @click="onSharedLedgerClick(child)"
                      >
                        <td class="td-check" @click.stop="onSharedLedgerClick(child)">
                          <span
                            class="row-check"
                            :class="{
                              on: isSharedRowPicked(child),
                              disabled: !isItemEditable(child),
                            }"
                            aria-hidden="true"
                          >
                            <v-icon size="14">{{ isSharedRowPicked(child) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
                          </span>
                        </td>
                        <td class="td-date">
                          <span class="detail-indent">└</span>
                        </td>
                        <td class="td-gubun">
                          <span
                            class="gubun-badge"
                            :class="child.cb_gubun === 'IN' ? 'income' : 'expense'"
                          >
                            {{ child.cb_gubun === 'IN' ? '수입' : '지출' }}
                          </span>
                        </td>
                        <td class="td-memo">
                          <div class="memo-container">
                            <span
                              v-if="child.cb_memo && child.cb_memo.trim()"
                              class="memo-text"
                              :title="child.cb_memo"
                            >{{ child.cb_memo }}</span>
                            <span v-else class="memo-missing">
                              <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                              미설정
                            </span>
                          </div>
                        </td>
                        <td class="td-price" :class="child.cb_gubun === 'IN' ? 'income' : 'expense'">
                          {{ formatNumber(child.cb_price) }}원
                        </td>
                        <td class="td-scode">
                          <span v-if="hasLedgerScode(child)" class="scode-name" :class="child.cb_gubun === 'IN' ? 'is-in' : 'is-out'">{{ ledgerScodeLabel(child) }}</span>
                          <span v-else class="scode-missing">
                            <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                            미설정
                          </span>
                        </td>
                        <td class="td-rel-acc" @click.stop>
                          <template v-if="child.cb_gubun === 'OUT'">
                            <span v-if="hasLedgerRel(child)" class="rel-acc-name is-in">{{ ledgerRelLabel(child) }}</span>
                            <span v-else class="rel-acc-missing">
                              <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                              미설정
                            </span>
                          </template>
                          <template v-else-if="isCollectionTarget(child) && child.cb_idx">
                            <v-menu
                              v-if="beneficiaryMatches[child.cb_idx]"
                              location="bottom start"
                              :close-on-content-click="true"
                            >
                              <template #activator="{ props: menuProps }">
                                <button
                                  type="button"
                                  v-bind="menuProps"
                                  class="ben-match-link matched"
                                  :title="`${beneficiaryMatches[child.cb_idx]?.cb_name} · 수급자 옵션`"
                                >
                                  <span class="ben-match-name">{{ beneficiaryMatches[child.cb_idx]?.cb_name }}</span>
                                </button>
                              </template>
                              <v-list density="compact" class="ben-match-menu">
                                <v-list-item @click="openCollectionForBeneficiary(beneficiaryMatches[child.cb_idx]?.cb_name || '')">
                                  <template #prepend>
                                    <v-icon size="18" color="#0d9488">mdi-cash-register</v-icon>
                                  </template>
                                  <v-list-item-title>수납 탭에서 보기</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="changeBeneficiaryMatch(child)">
                                  <template #prepend>
                                    <v-icon size="18" color="#1565c0">mdi-account-switch</v-icon>
                                  </template>
                                  <v-list-item-title>수급자 변경</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="removeBeneficiaryMatch(child)">
                                  <template #prepend>
                                    <v-icon size="18" color="#c62828">mdi-link-off</v-icon>
                                  </template>
                                  <v-list-item-title>매칭 해제</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                            <button
                              v-else
                              type="button"
                              class="ben-match-link need"
                              title="수급자가 아직 지정되지 않았습니다. 클릭하여 지정"
                              @click="openBeneficiaryMatchPopover(child)"
                            >
                              <span class="ben-match-name">미연결</span>
                            </button>
                          </template>
                          <span v-else class="rel-acc-na">-</span>
                        </td>
                        <td class="td-funding">
                          <span v-if="child.cb_funding" class="funding-name">{{ getFundingName(child.cb_funding) }}</span>
                          <span v-else class="funding-missing">
                            <v-icon size="14" color="#ca8a04">mdi-alert</v-icon>
                            미설정
                          </span>
                        </td>
                        <td v-if="selectedBankCode === null" class="td-bank">
                          <span class="bank-badge">{{ getBankAlias(child.cb_bank_code) }}</span>
                        </td>
                      </tr>
                    </template>
                  </template>
                </tbody>
              </table>
            </div>
          </section>

          <aside v-show="!showStartGuide" class="shared-work">
            <!-- 1건 선택: 적요·금액 수정 -->
            <div
              v-if="singleSelectedItem"
              class="sw-quick-edit"
              :class="{ open: quickEditOpen }"
              data-tour="bb-test-quick-edit"
            >
              <button
                type="button"
                class="sw-quick-head"
                :aria-expanded="quickEditOpen"
                @click="quickEditOpen = !quickEditOpen"
              >
                <span class="sw-quick-head-left">
                  <p class="sw-quick-lbl">적요 수정</p>
                  <span class="sw-quick-meta">
                    {{ formatDate(singleSelectedItem.cb_date) }}
                    · {{ singleSelectedItem.cb_gubun === 'IN' ? '수입' : '지출' }}
                  </span>
                </span>
                <v-icon size="18" class="sw-quick-chevron">
                  {{ quickEditOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </button>
              <div v-show="quickEditOpen" class="sw-quick-body">
                <label class="sw-quick-field">
                  <span>적요</span>
                  <input
                    ref="quickEditMemoRef"
                    v-model="quickEditMemo"
                    type="text"
                    :disabled="!isItemEditable(singleSelectedItem) || closingStatus?.isClosed || isQuickEditSaving"
                    @keydown.enter.prevent="saveQuickEdit"
                  />
                </label>
                <div class="sw-quick-amount">
                  <span>금액</span>
                  <div class="sw-quick-amount-row">
                    <div class="sw-quick-gubun" role="group" aria-label="수입 지출">
                      <button
                        type="button"
                        :class="{ on: quickEditGubun === 'IN' }"
                        :disabled="!isItemEditable(singleSelectedItem) || closingStatus?.isClosed || isQuickEditSaving"
                        @click="quickEditGubun = 'IN'"
                      >수입</button>
                      <button
                        type="button"
                        :class="{ on: quickEditGubun === 'OUT' }"
                        :disabled="!isItemEditable(singleSelectedItem) || closingStatus?.isClosed || isQuickEditSaving"
                        @click="quickEditGubun = 'OUT'"
                      >지출</button>
                    </div>
                    <input
                      v-model="quickEditPriceText"
                      type="text"
                      inputmode="text"
                      :disabled="!isItemEditable(singleSelectedItem) || closingStatus?.isClosed || isQuickEditSaving"
                      @input="onQuickEditPriceInput"
                      @keydown.enter.prevent="saveQuickEdit"
                    />
                  </div>
                </div>
                <div class="sw-quick-actions">
                  <button
                    type="button"
                    class="fd-btn primary"
                    :disabled="!isItemEditable(singleSelectedItem) || closingStatus?.isClosed || isQuickEditSaving || !quickEditDirty"
                    @click="saveQuickEdit"
                  >
                    <v-progress-circular v-if="isQuickEditSaving" indeterminate size="14" width="2" color="white" />
                    <span v-else>저장</span>
                  </button>
                  <button
                    type="button"
                    class="fd-btn danger-ghost"
                    :disabled="!isItemEditable(singleSelectedItem) || closingStatus?.isClosed"
                    @click="confirmDelete(singleSelectedItem)"
                  >
                    삭제
                  </button>
                </div>
              </div>
            </div>
            <div v-else-if="ledgerSelectedIds.length > 1" class="sw-multi-hint">
              <span>고르면 선택한 항목에 바로 반영됩니다</span>
              <button
                type="button"
                class="sl-sel-btn danger"
                :disabled="closingStatus?.isClosed || !deletableSelectedCount"
                @click="confirmDeleteSelected"
              >
                선택 삭제
              </button>
            </div>

            <div class="sw-subtabs" role="tablist" data-tour="bb-test-account">
              <button
                type="button"
                class="sw-subtab"
                :class="{ on: rawWorkTab === 'account', done: isAccountTabDone }"
                @click="openLedgerAccountTab"
              >
                <span class="sw-subtab-name">계정</span>
                <v-icon v-if="isAccountTabDone" class="sw-subtab-check" size="14">mdi-check</v-icon>
              </button>
              <button
                v-if="ledgerListTab === 'out'"
                type="button"
                class="sw-subtab"
                data-tour="bb-test-rel"
                :class="{ on: rawWorkTab === 'rel', done: isRelTabDone }"
                @click="rawWorkTab = 'rel'"
              >
                <span class="sw-subtab-name">상대계정</span>
                <v-icon v-if="isRelTabDone" class="sw-subtab-check" size="14">mdi-check</v-icon>
              </button>
              <button
                type="button"
                class="sw-subtab"
                data-tour="bb-test-split"
                :class="{ on: rawWorkTab === 'split', disabled: isSplitTabDisabled }"
                :disabled="isSplitTabDisabled"
                :title="isSplitTabDisabled ? '분개는 항목을 하나만 선택했을 때 사용할 수 있습니다' : '선택한 항목 분개'"
                @click="rawWorkTab = 'split'"
              >
                <span class="sw-subtab-name">분개</span>
              </button>
              <button
                type="button"
                class="sw-subtab"
                data-tour="bb-test-funding"
                :class="{ on: rawWorkTab === 'funding', done: isFundingTabDone }"
                @click="rawWorkTab = 'funding'"
              >
                <span class="sw-subtab-name">자금원천</span>
                <v-icon v-if="isFundingTabDone" class="sw-subtab-check" size="14">mdi-check</v-icon>
              </button>
            </div>

            <div
              v-if="rawWorkTab === 'account' || rawWorkTab === 'rel' || rawWorkTab === 'funding'"
              class="sw-funding-strip"
            >
              <span class="sw-funding-strip-lbl">자금원천</span>
              <div v-if="fundingSelectedItems.length" class="fd-pick-stack">
                <button
                  v-for="f in fundingCodeList"
                  :key="f.code"
                  type="button"
                  class="fd-mini"
                  :class="{ on: adjustWorkbenchRef?.draftFunding === f.code }"
                  :disabled="isAutoFundingProcessing || closingStatus?.isClosed"
                  @click="adjustWorkbenchRef?.pickFunding(f.code)"
                >{{ f.name }}</button>
              </div>
              <p v-else class="sw-hint">항목을 고르면 여기 자금원천이 바로 반영됩니다.</p>
            </div>

            <!-- 계정 / 상대계정 -->
            <div v-show="rawWorkTab === 'account' || rawWorkTab === 'rel'" class="sw-pane">
              <BankbookAdjustWorkbench
                ref="adjustWorkbenchRef"
                hide-list
                :adjust-focus="rawWorkTab === 'rel' ? 'rel' : 'account'"
                :incomes="workbenchIncomes"
                :pending-items="pendingAdjustList"
                :done-items="doneAdjustList"
                :pending-incomes="pendingIncomeList"
                :done-incomes="doneIncomeList"
                :selected-scode="selectedIncomeScode"
                :expense-codes="adjustExpenseCodes.length ? adjustExpenseCodes : expenseCodeList"
                :income-codes="incomeCodeList"
                :ledger-items="selectedBankCashbookList"
                :bank-names="bankNameMap"
                :show-bank="selectedBankCode === null"
                :loading="isAdjustStateLoading"
                :is-editable="isItemEditable"
                :member-code="memberCode"
                :biz-gubun="selectedBizGubun"
                :year="currentYear"
                :is-admin="isAdmin"
                @select-income="onIncomeBalanceSelect"
                @assign-batch="onWorkbenchAssignBatch"
                @assign-expense-account="onWorkbenchAssignExpenseAccount"
                @assign-rel="onWorkbenchAssignRel"
                @assign-funding="onWorkbenchAssignFunding"
                @unassign="onWorkbenchUnassign"
                @assign-income="onWorkbenchAssignIncome"
                @unassign-income="onWorkbenchUnassignIncome"
                @selection-change="onAdjustSelectionChange"
                @update:list-tab="onWorkbenchListTab"
                @open-learn="openDictionaryManageModal"
                @open-pairs="accountPairModalOpen = true"
                @open-match="openMatchDialog"
              />
            </div>

            <!-- 자금원천 -->
            <div v-show="rawWorkTab === 'funding'" class="sw-funding sw-pane" data-tour="bb-test-funding">
              <div class="fd-head">
                <div v-if="unfundedCount > 0" class="fd-status">
                  <strong>{{ unfundedCount }}</strong>건 남음
                  <span v-if="fundingInferableCount > 0">· 기준 {{ fundingInferableCount }}건</span>
                </div>
                <span v-else class="fd-status muted">미설정 없음</span>
                <button
                  type="button"
                  class="wb-gear"
                  data-tour="bb-test-funding-settings"
                  @click="openMatchDialog('funding')"
                >자동화 적용</button>
              </div>
              <p v-if="!fundingSelectedItems.length" class="sw-hint">표에서 항목을 고르면 위 자금원천이 바로 반영됩니다.</p>
            </div>

            <!-- 분개: 항목 1건 선택 시 (수입·지출) -->
            <div v-show="rawWorkTab === 'split'" class="sw-split sw-pane">
              <template v-if="splitTargetItem">
                <div class="sw-focus">
                  <p class="sw-focus-lbl">{{ isSplitChildItem(splitTargetItem) ? '분개된 항목' : '분개 대상' }}</p>
                  <p class="sw-focus-memo">{{ splitTargetItem.cb_memo || '-' }}</p>
                  <p class="sw-focus-meta">
                    {{ formatDate(splitTargetItem.cb_date) }}
                    · {{ splitTargetItem.cb_gubun === 'IN' ? '수입' : '지출' }}
                    · {{ formatNumber(splitTargetItem.cb_price) }}원
                  </p>
                  <p v-if="hasLedgerScode(splitTargetItem)" class="sw-focus-meta">
                    계정 {{ ledgerScodeLabel(splitTargetItem) }}
                  </p>
                </div>
                <template v-if="isSplitChildItem(splitTargetItem)">
                  <button
                    type="button"
                    class="fd-btn danger sw-full"
                    :disabled="!isItemEditable(splitTargetItem) || closingStatus?.isClosed || isMergingSplit"
                    @click="mergeSplitTarget"
                  >
                    <v-icon size="16">mdi-call-merge</v-icon>
                    {{ isMergingSplit ? '합치는 중…' : '분개 합치기' }}
                  </button>
                  <button
                    type="button"
                    class="fd-btn primary sw-full"
                    :disabled="!isItemEditable(splitTargetItem) || closingStatus?.isClosed || isMergingSplit"
                    @click="openSplitModal(splitTargetItem)"
                  >
                    <v-icon size="16">mdi-call-split</v-icon>
                    분개 수정
                  </button>
                  <p class="sw-hint">합치면 분개 항목이 사라지고 원본 한 건으로 돌아갑니다.</p>
                </template>
                <template v-else>
                  <button
                    type="button"
                    class="fd-btn primary sw-full"
                    :disabled="!isItemEditable(splitTargetItem) || closingStatus?.isClosed"
                    @click="openSplitModal(splitTargetItem)"
                  >
                    <v-icon size="16">mdi-call-split</v-icon>
                    분개하기
                  </button>
                  <p class="sw-hint">원본 금액을 여러 계정으로 나눕니다. 합계는 원본과 같아야 합니다.</p>
                </template>
              </template>
              <p v-else class="sw-hint">분개할 항목을 하나만 선택하세요.</p>
            </div>

          </aside>
        </div>

        <!-- ── 수납 탭: 수납관리 본문 ── -->
        <section v-else-if="isCollectionStep" class="collection-embed-view" data-tour="bb-test-collection">
          <!-- 수납 시작 가이드: 수납 데이터가 아예 없을 때 -->
          <div v-if="showCollectionGuide" class="start-guide">
            <div class="sg-head">
              <div class="sg-badge">
                <v-icon size="34">mdi-cash-register</v-icon>
              </div>
              <p class="sg-title">수납 정리를 시작해 볼까요?</p>
              <p class="sg-sub">{{ currentYear }}년 {{ currentMonth }}월 · 수급자별 청구와 입금을 맞추는 단계입니다</p>
            </div>
            <div class="sg-desc">
              <div class="sg-desc-row">
                <span class="sg-desc-label">목적</span>
                <p>수급자별 받을 돈(청구)과 들어온 돈(입금)을 맞춰 미수금 관리</p>
              </div>
              <div class="sg-desc-row">
                <span class="sg-desc-label">준비물</span>
                <p>수급자 등록 (엑셀 업로드 시 미등록 수급자는 자동 추가)</p>
              </div>
              <div class="sg-desc-row">
                <span class="sg-desc-label">쓰이는 곳</span>
                <p>입금 자동 반영 · 미수금 이력 · 누적 미수 (전달 미수는 다음 입금에서 우선 충당)</p>
              </div>
            </div>
            <p class="sg-warn">
              <v-icon size="14">mdi-alert-outline</v-icon>
              미수금 관리는 여기서 · 통장에 입금된 돈은 통장내역에서 관리하세요
            </p>
            <ol class="sg-steps">
              <li class="current">
                <span class="sg-step-num">1</span>
                <div class="sg-icon">
                  <v-icon size="26">mdi-file-excel-outline</v-icon>
                </div>
                <strong>수납관리 엑셀 업로드</strong>
                <p>수급자·청구 내역을 엑셀로 한 번에 등록합니다</p>
                <button
                  type="button"
                  class="sg-btn primary"
                  :disabled="isCollectionExcelDisabled"
                  @click="collectionExcelModalOpen = true"
                >
                  <v-icon size="15">mdi-upload</v-icon> 엑셀 업로드
                </button>
              </li>
              <li>
                <span class="sg-step-num">2</span>
                <div class="sg-icon">
                  <v-icon size="26">mdi-account-link-outline</v-icon>
                </div>
                <strong>통장내역에서 수급자 연결</strong>
                <p>본인부담금수입(112) 항목에 수급자를 지정합니다</p>
                <button type="button" class="sg-btn" @click="goStep('raw')">
                  <v-icon size="15">mdi-arrow-left</v-icon> 통장내역으로 이동
                </button>
              </li>
            </ol>
            <p class="sg-after">
              <v-icon size="14">mdi-information-outline</v-icon>
              둘 중 편한 방법으로 등록하면 입금 매칭과 미수금 정리가 이어집니다
            </p>
          </div>
          <BeneficiaryManageModal
            v-show="!showCollectionGuide"
            ref="beneficiaryEmbedRef"
            embedded
            :member-code="memberCode"
            :year="currentYear"
            :month="currentMonth"
            :initial-search-name="beneficiaryModalSearchName"
            :deposit-data="beneficiaryModalDepositData"
            :biz-gubun="selectedBizGubun"
            @beneficiary-changed="onBeneficiaryChanged"
            @collection-deleted="onCollectionDeleted"
            @charge-updated="onChargeUpdated"
          />
        </section>

        <!-- ── 영수증 탭: stepu형 편집·매칭 (자르기/캔버스) ── -->
        <section v-else-if="isReceiptStep" class="receipt-embed-view" data-tour="bb-test-receipt">
          <!-- 영수증 시작 가이드: 매칭 작업이 하나도 안 됐을 때 -->
          <div v-if="showReceiptGuide" class="start-guide">
            <div class="sg-head">
              <div class="sg-badge">
                <v-icon size="34">mdi-receipt-text-outline</v-icon>
              </div>
              <p class="sg-title">영수증 정리를 시작해 볼까요?</p>
              <p class="sg-sub">{{ currentYear }}년 {{ currentMonth }}월 · 통장 지출과 영수증을 맞추는 단계입니다</p>
            </div>
            <div class="sg-desc">
              <div class="sg-desc-row">
                <span class="sg-desc-label">목적</span>
                <p>지출 증빙 확보 — 회계 감사·보조금 정산 대비</p>
              </div>
              <div class="sg-desc-row">
                <span class="sg-desc-label">준비물</span>
                <p>이 달 지출 정리(통장내역 탭) · 영수증 이미지 (날짜가 달라도 매칭 가능)</p>
              </div>
              <div class="sg-desc-row">
                <span class="sg-desc-label">쓰이는 곳</span>
                <p>지출별 증빙 저장 · 회계보고서 · 감사 자료 (여러 장 묶음 가능)</p>
              </div>
            </div>
            <template v-if="receiptExpenseCount === 0">
              <p class="sg-note">
                이 달 지출 내역이 없어 매칭할 대상이 없습니다.<br>
                먼저 통장내역에서 지출을 정리해 주세요.
              </p>
              <button type="button" class="sg-btn" @click="goStep('raw')">
                <v-icon size="15">mdi-arrow-left</v-icon> 통장내역으로 이동
              </button>
            </template>
            <template v-else>
              <ol class="sg-steps">
                <li>
                  <span class="sg-step-num">1</span>
                  <div class="sg-icon">
                    <v-icon size="26">mdi-camera-outline</v-icon>
                  </div>
                  <strong>영수증 올리기</strong>
                  <p>스캔하거나 찍은 영수증 사진을 등록합니다</p>
                </li>
                <li>
                  <span class="sg-step-num">2</span>
                  <div class="sg-icon">
                    <v-icon size="26">mdi-cursor-default-click-outline</v-icon>
                  </div>
                  <strong>통장 지출 선택</strong>
                  <p>맞출 지출 항목을 고릅니다. 날짜가 달라도 됩니다</p>
                </li>
                <li>
                  <span class="sg-step-num">3</span>
                  <div class="sg-icon">
                    <v-icon size="26">mdi-link-variant</v-icon>
                  </div>
                  <strong>매칭</strong>
                  <p>「매칭」 버튼으로 둘을 연결합니다</p>
                </li>
              </ol>
              <button type="button" class="sg-btn primary sg-start-btn" @click="receiptGuideDismissed = true">
                <v-icon size="15">mdi-play</v-icon> 영수증 작업 시작하기
              </button>
              <p class="sg-after">
                <v-icon size="14">mdi-information-outline</v-icon>
                이 달 지출 {{ receiptExpenseCount }}건이 매칭 대상입니다
              </p>
            </template>
          </div>
          <ReceiptMatchingDialog
            v-show="!showReceiptGuide"
            embedded
            :cashbook-item="selectedCashbookForMatching"
            :m-code="memberCode"
            :biz-gubun="selectedBizGubun"
            :account-codes="accountCodes"
            :current-year="currentYear"
            :current-month="currentMonth"
            @saved="handleMatchingSaved"
          />
        </section>

        <!-- ── 계정별내역 ── -->
        <section v-else-if="isAnalysisReferenceStep" class="analysis-reference-embed-view" data-tour="bb-test-analysis">
          <AccountingAnalysisReferenceEmbed
            :member-code="String(memberCode)"
            :year="currentYear"
            :month="currentMonth"
            :biz-gubun="selectedBizGubun"
            :bank-code="selectedBankCode"
          />
        </section>

      </main>
    </div>

    </div>
    
    <v-snackbar
      v-model="guideToast.show"
      :timeout="2400"
      :color="guideToast.color"
      location="top"
      multi-line
    >
      {{ guideToast.message }}
    </v-snackbar>

    <!-- 통장내역 업로드 다이얼로그 -->
    <PgmBankbookUpload 
      v-model="uploadDialogOpen"
      :member-code="memberCode"
      :current-year="currentYear"
      :current-month="currentMonth"
      :biz-gubun="selectedBizGubun"
      :income-code-list="incomeCodeList"
      :expense-code-list="expenseCodeList"
      :bank-list="bankList"
      :selected-bank-code="selectedBankCode"
      @saved="handleUploadSaved"
      @upload-complete="handleUploadComplete"
    />
    
    <!-- 업로드 완료 알림 스낵바 -->
    <v-snackbar
      v-for="(notification, idx) in uploadNotifications"
      :key="notification.id"
      v-model="notification.show"
      :timeout="5000"
      :color="notification.success ? 'success' : 'error'"
      location="top right"
      :style="{ marginTop: `${idx * 70}px` }"
      class="upload-notification"
    >
      <div class="notification-content">
        <v-icon :icon="notification.success ? 'mdi-check-circle' : 'mdi-alert-circle'" class="mr-2" />
        <div class="notification-text">
          <strong>{{ notification.bankAlias }}</strong>
          <span v-if="notification.success">{{ notification.fileName }} ({{ notification.count }}건 등록)</span>
          <span v-else>{{ notification.message }}</span>
        </div>
      </div>
    </v-snackbar>
    
    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialogOpen" max-width="400">
      <div class="delete-modal">
        <div class="delete-modal-header">
          <div class="delete-icon">
            <v-icon size="28" color="white">mdi-alert</v-icon>
          </div>
          <h3>삭제 확인</h3>
          <p>이 작업은 되돌릴 수 없습니다</p>
          <button
            type="button"
            class="modal-header-close"
            aria-label="닫기"
            :disabled="isDeleting"
            @click="deleteDialogOpen = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="delete-modal-body">
          <p class="delete-message">
            <template v-if="deleteTargets.length > 1">
              선택한 <strong>{{ deleteTargets.length }}건</strong>을 삭제하시겠습니까?
            </template>
            <template v-else>
              선택한 현금출납 항목을 삭제하시겠습니까?
            </template>
          </p>
          <div class="delete-info" v-if="deleteTargets.length === 1 && selectedItem">
            <div class="info-row">
              <span class="info-label">날짜</span>
              <span class="info-value">{{ formatDate(selectedItem.cb_date) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">구분</span>
              <span class="info-value" :class="selectedItem.cb_gubun === 'IN' ? 'income' : 'expense'">
                {{ selectedItem.cb_gubun === 'IN' ? '수입' : '지출' }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">금액</span>
              <span class="info-value">{{ formatNumber(selectedItem.cb_price) }}원</span>
            </div>
            <div class="info-row" v-if="selectedItem.cb_memo">
              <span class="info-label">적요</span>
              <span class="info-value">{{ selectedItem.cb_memo }}</span>
            </div>
          </div>
          <div class="delete-summary" v-else-if="deleteTargets.length > 1">
            <div class="summary-row">
              <span class="label">수입</span>
              <span class="value income">{{ deleteTargets.filter(i => i.cb_gubun === 'IN').length }}건</span>
            </div>
            <div class="summary-row">
              <span class="label">지출</span>
              <span class="value expense">{{ deleteTargets.filter(i => i.cb_gubun === 'OUT').length }}건</span>
            </div>
          </div>
        </div>
        <div class="delete-modal-footer">
          <button type="button" class="btn-cancel" @click="deleteDialogOpen = false">취소</button>
          <button 
            type="button" 
            class="btn-delete" 
            @click="deleteItem"
            :disabled="isDeleting"
          >
            <v-progress-circular v-if="isDeleting" indeterminate size="18" width="2" color="white" />
            <span v-else>삭제하기</span>
          </button>
        </div>
      </div>
    </v-dialog>
    
    <!-- 모두삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteAllDialogOpen" max-width="450" persistent>
      <div class="delete-modal delete-all-modal">
        <div class="delete-modal-header">
          <div class="delete-icon danger">
            <v-icon size="28" color="white">mdi-delete-sweep</v-icon>
          </div>
          <h3>일괄 삭제 확인</h3>
          <p>이 작업은 되돌릴 수 없습니다</p>
          <button
            type="button"
            class="modal-header-close"
            aria-label="닫기"
            :disabled="isDeleting"
            @click="deleteAllDialogOpen = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="delete-modal-body">
          <p class="delete-message">
            현재 필터링된 <strong class="count">{{ filteredCashbookList.length }}건</strong>의 항목을 모두 삭제하시겠습니까?
          </p>
          <div class="delete-summary">
            <div class="summary-row">
              <span class="label">수입 항목</span>
              <span class="value income">{{ filteredCashbookList.filter(i => i.cb_gubun === 'IN').length }}건</span>
            </div>
            <div class="summary-row">
              <span class="label">지출 항목</span>
              <span class="value expense">{{ filteredCashbookList.filter(i => i.cb_gubun === 'OUT').length }}건</span>
            </div>
          </div>
          <div class="warning-box">
            <v-icon size="16" color="#6b7280">mdi-alert-circle-outline</v-icon>
            <span>삭제 후 복구가 불가능합니다. 신중하게 결정해주세요.</span>
          </div>
        </div>
        <div class="delete-modal-footer">
          <button type="button" class="btn-cancel" @click="deleteAllDialogOpen = false">취소</button>
          <button 
            type="button" 
            class="btn-delete danger" 
            @click="deleteAllItems"
            :disabled="isDeleting"
          >
            <v-progress-circular v-if="isDeleting" indeterminate size="18" width="2" color="white" />
            <span v-else>{{ filteredCashbookList.length }}건 모두 삭제</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 계정조정 마감 다이얼로그 -->
    <v-dialog v-model="closingDialogOpen" max-width="500" persistent>
      <div class="closing-modal">
        <div class="closing-modal-header">
          <div class="closing-icon">
            <v-icon size="28" color="white">mdi-check-decagram</v-icon>
          </div>
          <h3>계정조정 마감</h3>
          <p>{{ currentYear }}년 {{ currentMonth }}월</p>
          <button
            type="button"
            class="modal-header-close"
            aria-label="닫기"
            :disabled="isClosing"
            @click="closingDialogOpen = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="closing-modal-body">
          <!-- 유효성 검사 중 -->
          <div v-if="isValidating" class="validation-loading">
            <v-progress-circular indeterminate size="32" color="primary" />
            <p>마감 가능 여부 확인 중...</p>
          </div>
          
          <!-- 유효성 검사 실패 -->
          <div v-else-if="validationResult && !validationResult.valid" class="validation-failed">
            <div class="validation-alert">
              <v-icon size="20" color="#dc2626">mdi-alert-circle</v-icon>
              <span>{{ validationResult.message }}</span>
            </div>
            <div v-if="validationResult.details && validationResult.details.items && validationResult.details.items.length > 0" class="validation-items">
              <p class="items-label">미조정 항목 (최대 10건 표시):</p>
              <ul>
                <li v-for="item in (validationResult.details?.items || [])" :key="item.cb_idx">
                  <span class="item-date">{{ item.date }}</span>
                  <span class="item-gubun" :class="item.gubun === '수입' ? 'income' : 'expense'">{{ item.gubun }}</span>
                  <span class="item-memo">{{ item.memo || '-' }}</span>
                  <span class="item-price">{{ formatNumber(item.price) }}원</span>
                </li>
              </ul>
            </div>
          </div>
          
          <!-- 유효성 검사 통과 -->
          <div v-else-if="validationResult?.valid" class="validation-passed">
            <div class="validation-success">
              <v-icon size="20" color="#16a34a">mdi-check-circle</v-icon>
              <span>모든 항목의 계정조정이 완료되었습니다.</span>
            </div>
            <div class="closing-summary">
              <div class="summary-row">
                <span class="label">총 항목 수</span>
                <span class="value">{{ selectedBankCashbookList.length }}건</span>
              </div>
              <div class="summary-row">
                <span class="label">수입</span>
                <span class="value income">{{ summaryData.incomeCount }}건 / {{ formatNumber(summaryData.income) }}원</span>
              </div>
              <div class="summary-row">
                <span class="label">지출</span>
                <span class="value expense">{{ summaryData.expenseCount }}건 / {{ formatNumber(summaryData.expense) }}원</span>
              </div>
            </div>
            <div class="warning-box info">
              <v-icon size="16" color="#6b7280">mdi-information-outline</v-icon>
              <span>마감 후에는 통장내역을 수정하거나 삭제할 수 없습니다.</span>
            </div>
          </div>
        </div>
        <div class="closing-modal-footer">
          <button type="button" class="btn-cancel" @click="closingDialogOpen = false">취소</button>
          <button 
            v-if="validationResult?.valid"
            type="button" 
            class="btn-closing" 
            @click="executeClosing"
            :disabled="isClosing"
          >
            <v-progress-circular v-if="isClosing" indeterminate size="18" width="2" color="white" />
            <span v-else>마감 처리</span>
          </button>
          <button 
            v-else-if="validationResult && !validationResult.valid"
            type="button" 
            class="btn-retry" 
            @click="runValidation"
          >
            <v-icon size="16">mdi-refresh</v-icon>
            다시 확인
          </button>
        </div>
      </div>
    </v-dialog>

    <AccountPairManageModal
      v-model="accountPairModalOpen"
      :year="currentYear"
      :income-codes="incomeCodeList"
      :expense-codes="expenseCodeList"
      @changed="loadAdjustState"
    />

    <BankbookMatchDialog
      v-model="matchDialogOpen"
      :kind="matchKind"
      :member-code="memberCode"
      :year="currentYear"
      :month="currentMonth"
      :biz-gubun="selectedBizGubun || ''"
      :bank-code="selectedBankCode || ''"
      :closed-locked="!!closingStatus?.isClosed"
      :income-codes="incomeCodeList"
      :expense-codes="expenseCodeList"
      :cb-idxs="matchTargetIds"
      @applied="loadCashbookData"
    />

    <LaborRatioDetailModal
      v-model="laborRatioModalOpen"
      :member-code="String(memberCode)"
      :year="currentYear"
      :month="currentMonth"
      :biz-gubun="selectedBizGubun || undefined"
      :bank-code="selectedBankCode"
    />

    <ClosingSettlementModal
      v-model="settlementModalOpen"
      :member-code="String(memberCode)"
      :year="currentYear"
      :month="currentMonth"
      :biz-gubun="selectedBizGubun || undefined"
      :bank-code="selectedBankCode"
    />

    <!-- 적요→계정 패턴 관리 -->
    <v-dialog v-model="dictionaryManageModalOpen" max-width="720" persistent>
      <div class="dictionary-manage-modal pattern-manage-modal" data-tour="bb-test-patterns-dialog">
        <div class="modal-header learning">
          <div class="header-icon">
            <v-icon size="24" color="white">mdi-book-search-outline</v-icon>
          </div>
          <div class="header-text">
            <h3>기억된 적요·계정</h3>
            <p>예전에 지정해 두어 추천에 쓰이는 적요와 계정입니다. 잘못된 것만 삭제하세요.</p>
          </div>
          <button class="close-btn" @click="dictionaryManageModalOpen = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <div class="modal-body pattern-manage-body">
          <div class="pattern-tabs" role="tablist">
            <button
              type="button"
              class="pattern-tab"
              :class="{ on: selectedDictTable === 'income' }"
              @click="selectDictTable('income')"
            >
              수입
              <em v-if="dictionaryStats">{{ formatNumber(dictionaryStats.income) }}</em>
            </button>
            <button
              type="button"
              class="pattern-tab"
              :class="{ on: selectedDictTable === 'expense' }"
              @click="selectDictTable('expense')"
            >
              지출
              <em v-if="dictionaryStats">{{ formatNumber(dictionaryStats.expense) }}</em>
            </button>
          </div>

          <div class="pattern-toolbar">
            <label class="pattern-search">
              <v-icon size="16">mdi-magnify</v-icon>
              <input
                v-model.trim="dictSearchQuery"
                type="search"
                placeholder="키워드·계정명 검색"
                autocomplete="off"
              />
            </label>
            <span class="pattern-count">
              <template v-if="isDictDetailLoading">불러오는 중…</template>
              <template v-else>{{ filteredDictDetailList.length }}건</template>
            </span>
          </div>

          <div v-if="isDictDetailLoading" class="detail-loading">
            <v-progress-circular indeterminate size="24" width="2" color="primary" />
            <span>목록을 불러오는 중…</span>
          </div>

          <div v-else-if="filteredDictDetailList.length > 0" class="dict-list">
            <div
              v-for="item in filteredDictDetailList"
              :key="item.cd_idx"
              class="dict-item"
            >
              <span class="dict-keyword" :title="item.cd_keyword">{{ item.cd_keyword }}</span>
              <v-icon size="12" color="#94a3b8">mdi-arrow-right</v-icon>
              <span class="dict-scode" :title="item.ac_name || item.cd_scode">{{ item.ac_name || item.cd_scode }}</span>
              <span class="dict-count">{{ item.cd_count }}회</span>
              <button type="button" class="dict-delete" title="삭제" @click="deleteDictItem(item)">
                <v-icon size="14">mdi-close</v-icon>
              </button>
            </div>
          </div>

          <div v-else class="detail-empty">
            <v-icon size="36" color="#cbd5e1">mdi-book-search-outline</v-icon>
            <p v-if="dictSearchQuery">검색 결과가 없습니다.</p>
            <p v-else>아직 기억된 {{ selectedDictTable === 'income' ? '수입' : '지출' }} 적요가 없습니다.</p>
            <p class="detail-empty-hint">우측에서 계정을 지정하면 적요가 쌓입니다. (자동화 일괄 채우기는 쌓지 않습니다)</p>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="dictionaryManageModalOpen = false">
            닫기
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 수급자 매칭 팝오버 -->
    <v-dialog v-model="beneficiaryMatchDialogOpen" max-width="360">
      <div class="beneficiary-match-dialog">
        <div class="match-dialog-header">
          <v-icon size="20" color="#0d9488">mdi-account-search</v-icon>
          <span>수급자 매칭</span>
          <button class="close-btn" @click="closeBeneficiaryMatchDialog">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="match-dialog-body">
          <div class="match-item-info" v-if="matchTargetItem">
            <span class="match-date">{{ formatDate(matchTargetItem.cb_date) }}</span>
            <span class="match-memo">{{ matchTargetItem.cb_memo || '(적요 없음)' }}</span>
            <span class="match-price">{{ formatNumber(matchTargetItem.cb_price) }}원</span>
          </div>
          
          <!-- 수급자 검색/선택 -->
          <div class="match-search-area">
            <v-autocomplete
              v-model="selectedBeneficiaryForMatch"
              :items="availableBeneficiaryList"
              item-title="displayName"
              item-value="cb_idx"
              variant="outlined"
              density="compact"
              hide-details
              placeholder="수급자 검색 또는 선택"
              clearable
              class="beneficiary-search-input"
              :no-data-text="beneficiaryList.length === 0 ? '수납관리에 등록된 수급자가 없습니다' : '수급자가 없습니다'"
            >
              <template #item="{ item, props }">
                <v-list-item v-bind="props">
                  <template #prepend>
                    <v-icon size="18" color="#0d9488">mdi-account</v-icon>
                  </template>
                  <template #subtitle>
                    <span class="beneficiary-item-info">
                      <span v-if="item.raw.cb_ltc_no" class="ltc-no">{{ item.raw.cb_ltc_no }}</span>
                      <span v-if="item.raw.totalCharge" class="charge-amount">청구합계: {{ formatNumber(item.raw.totalCharge) }}원</span>
                    </span>
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
          </div>
          
        </div>
        <div class="match-dialog-footer">
          <button class="btn-cancel" @click="closeBeneficiaryMatchDialog">취소</button>
          <button 
            class="btn-confirm" 
            @click="saveBeneficiaryMatch"
            :disabled="!selectedBeneficiaryForMatch"
          >
            매칭 저장
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- 분개(Split) 모달 -->
    <SplitModal
      v-model="splitModalOpen"
      :cb-idx="splitTargetCbIdx"
      :year="currentYear"
      :account-codes="[...incomeCodeList, ...expenseCodeList].map(c => ({ scode: c.scode, sname: c.sname, gubun: c.gubun, displayName: `${c.scode} ${c.sname}` }))"
      :bank-list="bankList"
      @saved="onSplitSaved"
    />

    <!-- 수납관리 엑셀 업로드 모달 -->
    <CollectionExcelUploadModal
      v-model="collectionExcelModalOpen"
      :member-code="memberCode"
      :year="parseInt(String(currentYear))"
      :month="parseInt(String(currentMonth))"
      :biz-gubun="selectedBizGubun"
      @uploaded="onCollectionExcelUploaded"
    />
    
    <!-- 통장 관리 다이얼로그 -->
    <v-dialog v-model="bankManageDialogOpen" max-width="700" persistent>
      <div class="bank-manage-modal" data-tour="bb-test-bank-manage-dialog">
        <div class="modal-header bank">
          <div class="header-icon">
            <v-icon size="24" color="white">mdi-bank</v-icon>
          </div>
          <div class="header-text">
            <h3>통장 관리</h3>
            <p>시설의 통장을 등록하고 관리합니다</p>
          </div>
          <button class="help-btn" @click="showBankGuide = !showBankGuide" title="통장 안내">
            <v-icon size="18">mdi-help-circle-outline</v-icon>
          </button>
          <button class="close-btn" @click="bankManageDialogOpen = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <div class="modal-body">
          <!-- 통장 안내 가이드 -->
          <div v-if="showBankGuide" class="bank-guide-section">
            <div class="guide-header">
              <v-icon size="18" color="#1565c0">mdi-information</v-icon>
              <span>통장의 명칭과 기능</span>
              <button class="guide-close" @click="showBankGuide = false">
                <v-icon size="14">mdi-close</v-icon>
              </button>
            </div>
            <div class="guide-content">
              <div class="guide-item">
                <div class="guide-title">
                  <span class="guide-number">1</span>
                  <strong>사업비</strong>
                </div>
                <p>전체적인 운영을 위한 수입·지출 통장</p>
              </div>
              <div class="guide-item">
                <div class="guide-title">
                  <span class="guide-number">2</span>
                  <strong>보조비</strong>
                </div>
                <p>지자체나 국가기관에서 지급하는 보조금</p>
                <p class="guide-sub">주로 생계보조비가 주이고, 각종(공기청정기, 시설보조 등) 지자체 보조금 사업으로 다양하게 이루어질 수 있습니다.</p>
              </div>
              <div class="guide-item">
                <div class="guide-title">
                  <span class="guide-number">3</span>
                  <strong>후원금</strong>
                </div>
                <p>후원금을 받는 경우 후원금 통장으로 운영</p>
              </div>
              <div class="guide-item">
                <div class="guide-title">
                  <span class="guide-number">4</span>
                  <strong>비급여</strong>
                </div>
                <p>수급자 또는 보호자들이 납부하는 본인부담금과 식재료비, 이미용비, 상급침실이용료, 기타비급여수입 별도 관리하기 위한 통장</p>
              </div>
              <div class="guide-notice">
                <v-icon size="14" color="#f59e0b">mdi-alert</v-icon>
                <span>기타 다른 용도의 통장을 만들 수 있으나, 너무 늘리는 것은 관리하기 어려워 추천하지 않습니다.</span>
              </div>
            </div>
          </div>
          
          <!-- 통장 목록 -->
          <div class="bank-list-section">
            <div class="section-header">
              <h4>
                <v-icon size="16">mdi-format-list-bulleted</v-icon>
                등록된 통장 ({{ bankList.length }}개)
              </h4>
              <button class="btn-add-bank" @click="onAddBankClick">
                <v-icon size="14">mdi-plus</v-icon>
                통장 추가
              </button>
            </div>
            
            <!-- 사용중인 통장 -->
            <div v-if="activeBankListForModal.length > 0" class="bank-list">
              <div class="bank-group-header active">
                <v-icon size="14" color="#059669">mdi-check-circle</v-icon>
                <span>사용중 ({{ activeBankListForModal.length }})</span>
              </div>
              <div 
                v-for="bank in activeBankListForModal" 
                :key="bank.mb_idx"
                class="bank-item"
              >
                <div class="bank-main">
                  <div class="bank-icon-wrapper active">
                    <v-icon size="20" color="#059669">mdi-bank</v-icon>
                  </div>
                  <div class="bank-info">
                    <div class="bank-top">
                      <span class="bank-alias">{{ bank.mb_alias }}</span>
                      <span 
                        v-if="bank.mb_biz_gubun" 
                        class="biz-tag"
                        :style="{ backgroundColor: getBizColor(bank.mb_biz_gubun), color: 'white' }"
                      >{{ bank.mb_biz_gubun }}</span>
                      <span class="status-tag using">사용중</span>
                    </div>
                    <div class="bank-bottom">
                      <span class="bank-name">{{ bank.mb_bank_name }}</span>
                      <span class="account-no">{{ formatAccountNo(bank.mb_account_no) }}</span>
                    </div>
                    <div class="bank-period">
                      <v-icon size="12" color="#94a3b8">mdi-calendar-range</v-icon>
                      <span>{{ formatBankPeriod(bank) }}</span>
                    </div>
                  </div>
                </div>
                <div class="bank-actions">
                  <button class="action-btn edit" @click="editBank(bank)" title="수정">
                    <v-icon size="14">mdi-pencil</v-icon>
                  </button>
                  <button class="action-btn delete" @click="confirmDeleteBank(bank)" title="삭제">
                    <v-icon size="14">mdi-delete</v-icon>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- 종료된 통장 (접기/펼치기) -->
            <div v-if="inactiveBankListForModal.length > 0" class="bank-list inactive-section">
              <div class="bank-group-header inactive" @click="showInactiveBanks = !showInactiveBanks">
                <v-icon size="14" color="#94a3b8">mdi-archive</v-icon>
                <span>종료됨 ({{ inactiveBankListForModal.length }})</span>
                <v-icon size="16" class="expand-icon" :class="{ expanded: showInactiveBanks }">
                  mdi-chevron-down
                </v-icon>
              </div>
              <template v-if="showInactiveBanks">
                <div 
                  v-for="bank in inactiveBankListForModal" 
                  :key="bank.mb_idx"
                  class="bank-item inactive"
                >
                  <div class="bank-main">
                    <div class="bank-icon-wrapper inactive">
                      <v-icon size="20" color="#94a3b8">mdi-bank</v-icon>
                    </div>
                    <div class="bank-info">
                      <div class="bank-top">
                        <span class="bank-alias">{{ bank.mb_alias }}</span>
                        <span 
                          v-if="bank.mb_biz_gubun" 
                          class="biz-tag faded"
                          :style="{ backgroundColor: getBizColor(bank.mb_biz_gubun), opacity: 0.5 }"
                        >{{ bank.mb_biz_gubun }}</span>
                        <span class="status-tag ended">{{ getBankStatusText(bank) }}</span>
                      </div>
                      <div class="bank-bottom">
                        <span class="bank-name">{{ bank.mb_bank_name }}</span>
                        <span class="account-no">{{ formatAccountNo(bank.mb_account_no) }}</span>
                      </div>
                      <div class="bank-period">
                        <v-icon size="12" color="#94a3b8">mdi-calendar-range</v-icon>
                        <span>{{ formatBankPeriod(bank) }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="bank-actions">
                    <button class="action-btn edit" @click="editBank(bank)" title="수정">
                      <v-icon size="14">mdi-pencil</v-icon>
                    </button>
                    <button class="action-btn delete" @click="confirmDeleteBank(bank)" title="삭제">
                      <v-icon size="14">mdi-delete</v-icon>
                    </button>
                  </div>
                </div>
              </template>
            </div>
            
            <!-- 통장 없음 -->
            <div v-if="bankList.length === 0 && !showBankForm" class="no-banks">
              <v-icon size="48" color="#cbd5e1">mdi-bank-off</v-icon>
              <p>등록된 통장이 없습니다.</p>
              <p class="sub">통장 추가 버튼을 눌러 등록해주세요.</p>
            </div>
          </div>
          
          <!-- 통장 등록/수정 폼 -->
          <div v-if="showBankForm" ref="bankFormSectionRef" class="bank-form-section">
            <div class="section-header">
              <h4>
                <v-icon size="16">{{ editingBank ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                {{ editingBank ? '통장 수정' : '통장 등록' }}
              </h4>
              <button class="btn-close-form" @click="showBankForm = false">
                <v-icon size="14">mdi-close</v-icon>
              </button>
            </div>
            
            <div class="bank-form">
              <div class="form-row">
                <div class="form-group required">
                  <label>통장 별칭</label>
                  <input 
                    type="text" 
                    v-model="bankFormData.mb_alias"
                    placeholder="예: 본인부담금통장, 생계비통장"
                    class="form-input"
                  />
                </div>
              </div>
              
              <div class="form-row two-cols">
                <div class="form-group required">
                  <label>은행명</label>
                  <input 
                    type="text" 
                    v-model="bankFormData.mb_bank_name"
                    placeholder="예: 국민은행, 신한은행"
                    class="form-input"
                  />
                </div>
                <div class="form-group required">
                  <label>계좌번호</label>
                  <input 
                    type="text" 
                    v-model="bankFormData.mb_account_no"
                    placeholder="계좌번호 입력"
                    class="form-input"
                  />
                </div>
              </div>
              
              <!-- 사업구분 선택 (다중 사업구분 시설만 표시) -->
              <div v-if="facilityBizGubunList.length > 1" class="form-row">
                <div class="form-group">
                  <label>사업구분 <span class="required">*</span></label>
                  <div class="biz-gubun-select">
                    <label 
                      v-for="biz in facilityBizGubunList" 
                      :key="biz"
                      class="biz-radio-item"
                      :class="{ selected: bankFormData.mb_biz_gubun === biz }"
                    >
                      <input 
                        type="radio" 
                        :value="biz"
                        v-model="bankFormData.mb_biz_gubun"
                        name="bankBizGubun2"
                      />
                      <span 
                        class="radio-label"
                        :style="{ 
                          backgroundColor: bankFormData.mb_biz_gubun === biz ? getBizColor(biz) : 'transparent',
                          color: bankFormData.mb_biz_gubun === biz ? 'white' : getBizColor(biz),
                          borderColor: getBizColor(biz)
                        }"
                      >{{ biz }}</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label>용도 설명</label>
                  <input 
                    type="text" 
                    v-model="bankFormData.mb_purpose"
                    placeholder="이 통장의 용도를 간략히 설명"
                    class="form-input"
                  />
                </div>
              </div>
              
              <div class="form-row two-cols">
                <div class="form-group required">
                  <label>사용 시작월</label>
                  <input 
                    type="month" 
                    v-model="bankFormData.mb_start_date"
                    class="form-input"
                  />
                </div>
                <div class="form-group">
                  <label>사용 종료월</label>
                  <input 
                    type="month" 
                    v-model="bankFormData.mb_end_date"
                    :min="bankFormData.mb_start_date || undefined"
                    class="form-input"
                  />
                </div>
              </div>
              
              <div class="form-actions">
                <button type="button" class="btn-cancel" @click="showBankForm = false">취소</button>
                <button 
                  type="button" 
                  class="btn-save"
                  @click="saveBank"
                  :disabled="!canSaveBank || isSavingBank"
                >
                  <v-progress-circular v-if="isSavingBank" indeterminate size="16" width="2" color="white" />
                  <span v-else>{{ editingBank ? '수정' : '등록' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-cancel" @click="bankManageDialogOpen = false">
            닫기
          </button>
        </div>
      </div>
    </v-dialog>
    
    <!-- 통장 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteBankDialogOpen" max-width="400">
      <div class="delete-modal">
        <div class="delete-modal-header">
          <div class="delete-icon">
            <v-icon size="28" color="white">mdi-alert</v-icon>
          </div>
          <h3>통장 삭제 확인</h3>
          <p>이 작업은 되돌릴 수 없습니다</p>
          <button
            type="button"
            class="modal-header-close"
            aria-label="닫기"
            :disabled="isDeletingBank"
            @click="deleteBankDialogOpen = false"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
        <div class="delete-modal-body">
          <p class="delete-message">선택한 통장을 삭제하시겠습니까?</p>
          <div class="delete-info" v-if="bankToDelete">
            <div class="info-row">
              <span class="info-label">통장 별칭</span>
              <span class="info-value">{{ bankToDelete.mb_alias }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">은행</span>
              <span class="info-value">{{ bankToDelete.mb_bank_name }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">계좌번호</span>
              <span class="info-value">{{ formatAccountNo(bankToDelete.mb_account_no) }}</span>
            </div>
          </div>
        </div>
        <div class="delete-modal-footer">
          <button type="button" class="btn-cancel" @click="deleteBankDialogOpen = false">취소</button>
          <button 
            type="button" 
            class="btn-delete" 
            @click="deleteBank"
            :disabled="isDeletingBank"
          >
            <v-progress-circular v-if="isDeletingBank" indeterminate size="18" width="2" color="white" />
            <span v-else>삭제하기</span>
          </button>
        </div>
      </div>
    </v-dialog>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, inject, nextTick, type Ref } from 'vue';
import { useMemberYearMonthOptional } from '@/composables/useMemberYearMonth';
import { usePageTabNav } from '@/composables/usePageTabNav';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { bankbookApi, beneficiaryApi, collectionApi, closingApi } from '@/services/api';
import PgmBankbookUpload from '@/components/bankbook/PgmBankbookUpload.vue';
import ReceiptMatchingDialog from '@/components/bankbook/ReceiptMatchingDialog.vue';
import BeneficiaryManageModal from '@/components/bankbook/BeneficiaryManageModal.vue';
import CollectionExcelUploadModal from '@/components/bankbook/CollectionExcelUploadModal.vue';
import SplitModal from '@/components/bankbook/SplitModal.vue';
import AccountPairManageModal from '@/components/bankbook/AccountPairManageModal.vue';
import LaborRatioDetailModal from '@/components/bankbook/LaborRatioDetailModal.vue';
import ClosingSettlementModal from '@/components/bankbook/ClosingSettlementModal.vue';
import BankbookAdjustWorkbench from '@/components/bankbook/BankbookAdjustWorkbench.vue';
import BankbookFeatureSpecPanel from '@/components/bankbook/BankbookFeatureSpecPanel.vue';
import BankbookMatchDialog from '@/components/bankbook/BankbookMatchDialog.vue';
import AccountingAnalysisReferenceEmbed from '@/components/bankbook/AccountingAnalysisReferenceEmbed.vue';
import { BANKBOOK_TEST_FEATURE_SPEC } from '@/constants/bankbookTestFeatureSpec';
import type { BankbookGuideHost, BankbookGuidePrepareResult } from '@/constants/bankbookTestFeatureSpec';
import { buildBankbookLearnItems } from '@/utils/bankbookMemoLearn';
import type {
  BankbookAutoKind,
  BankbookAutoActionState,
  BankbookAutoPreviewRow,
} from '@/components/bankbook/BankbookAutoDialog.vue';

const bankbookFeatureSpec = BANKBOOK_TEST_FEATURE_SPEC;

interface IncomeBalanceItem {
  scode: string;
  sname: string;
  remaining: number;
  totalIncome: number;
  usedExpense: number;
  paired: boolean;
  allowedExpenses: Array<{ scode: string; sname: string }>;
}

interface PickerGroup {
  incomeScode: string;
  incomeSname: string;
  remaining: number;
  paired: boolean;
  expenses: Array<{ scode: string; sname: string }>;
}

const store = useStore();
const route = useRoute();

// 관리자 여부 (role_level 60 이상) — 페이지 내 기능용
const isAdmin = computed(() => (store.state.user?.roleLevel || 0) >= 60);

// 최고관리자 여부 (role_level 100)
const isSuperAdmin = computed(() => (store.state.user?.roleLevel || 0) >= 100);

/** 상단 더보기: 마감 해제·테스트 자료만 (짝/패턴은 우측 계정 톱니) */
const hasMoreMenuItems = computed(() =>
  (!!closingStatus.value?.isClosed && isAdmin.value)
  || (isSuperAdmin.value && !!selectedBankCode.value)
);

// ============================================
// 인터페이스 정의
// ============================================

interface RawDataValue {
  value: string;
  isMemo: boolean;
}

interface CashbookData {
  cb_idx?: number;
  cb_parent_idx?: number | null;  // 분개된 항목인 경우 원본 idx
  m_code?: number;
  cb_gubun: string;
  cb_scode?: string;
  cb_funding?: string;
  cb_memo: string;
  cb_raw_data?: Record<string, RawDataValue> | string | null;
  cb_price: number;
  cb_date: string;
  cb_order?: number;  // 같은 날짜 내 거래 순서
  cb_rel_acc?: string;
  cb_need_check?: string;
  cb_bank_name?: string;
  cb_bank_balance?: number;
  cb_bank_code?: string;
  cb_confirmed?: number;
  cb_source?: string;
  cb_wdate?: string;
  cb_rdate?: string;
  cb_receipt_matched?: number;
  crm_code?: number;
  split_count?: number;
  is_split_item?: number;  // 분개 항목 여부 (1이면 분개 항목)
  parent_idx?: number | null;  // cb_parent_idx와 동일
  cb_matched_beneficiary_idx?: number | null;  // 수동 매칭된 수급자 idx
}

// 통장 인터페이스
interface BankData {
  mb_idx: number;
  m_code: number;
  mb_code: string;
  mb_alias: string;
  mb_bank_name: string;
  mb_account_no: string;
  mb_purpose?: string;
  mb_biz_gubun?: string;
  mb_sort_order: number;
  mb_is_active: number;
  mb_start_date?: string;
  mb_end_date?: string;
  mb_wdate?: string;
  mb_rdate?: string;
}

// 계정코드 인터페이스 (caion_finance_code 테이블 기반)
interface AccountCode {
  cfc_idx: number;
  gubun: 'IN' | 'OUT';  // 수입/지출 구분
  gubun_name: string;   // '수입' | '지출'
  lcode: string;        // 대분류 코드
  lname: string;        // 대분류명
  mcode: string;        // 중분류 코드
  mname: string;        // 중분류명
  scode: string;        // 소분류 코드 (계정코드)
  sname: string;        // 소분류명 (계정명)
  cfc_hope_scode?: string;
  cfc_funding?: string;
}

interface DictItem {
  cd_idx: number;
  m_code?: number;
  cd_gubun: string;
  cd_keyword: string;
  cd_scode: string;
  cd_count: number;
  ac_name?: string;
}

// ============================================
// Props & Inject
// ============================================

const memberData = inject<any>('memberData');
const bizGubunColorMap = inject<any>('bizGubunColorMap');
const selectedBizGubunRef = inject<Ref<string>>('selectedBizGubun');

// 현재 선택된 사업구분 (computed로 안전하게 접근)
const selectedBizGubun = computed(() => selectedBizGubunRef?.value || '');

// 시설 ID (m_code: 내부 PK, m_longterm_code: 시설번호)
const memberCode = computed(() => {
  return route.params.memberCode as string || memberData?.value?.m_code || 0;
});

// 시설번호 (m_longterm_code) - cashbook에 저장할 때 사용
const longtermCode = computed(() => {
  return memberData?.value?.m_longterm_code || '';
});

// ============================================
// 상태 정의
// ============================================

const isLoading = ref(true);
const isSaving = ref(false);
const isDeleting = ref(false);
const isGeneratingTestData = ref(false);

// 마감 관련 상태
const closingDialogOpen = ref(false);
const isClosing = ref(false);
const isValidating = ref(false);
const closingStatus = ref<{
  isClosed: boolean;
  currentLevel: number;
  currentClosing?: {
    cc_idx: number;
    cc_status: string;
    cc_closed_at: string;
    statusName: string;
  };
} | null>(null);
const validationResult = ref<{
  valid: boolean;
  message?: string;
  details?: {
    count: number;
    items: Array<{
      cb_idx: number;
      gubun: string;
      date: string;
      memo: string;
      price: number;
    }>;
  };
} | null>(null);

// 희망이음 업로드 관련 상태
const isRequestingHeumieum = ref(false);
const heumieumStatus = ref<{
  status: 'requested' | 'completed' | null;
  requestAt?: string;
  completedAt?: string;
  completeMemo?: string;
  displayName?: string;
} | null>(null);

// 희망이음 상태 텍스트
const heumieumStatusText = computed(() => {
  if (!heumieumStatus.value?.status) return '미요청';
  if (heumieumStatus.value.status === 'requested') return '요청중';
  if (heumieumStatus.value.status === 'completed') return '완료';
  return '미요청';
});

// 통장별 마감 요약 상태 (전체 선택 시)
const closingSummary = ref<{
  total: number;
  closed: number;
  unclosed: number;
  allClosed: boolean;
  banks: Array<{
    mb_code: string;
    mb_alias: string;
    mb_bank_name: string;
    isClosed: boolean;
    closedAt: string | null;
  }>;
} | null>(null);

const cashbookList = ref<CashbookData[]>([]);
const accountCodes = ref<AccountCode[]>([]);
const highlightedIdxList = ref<number[]>([]);
const showSaveToast = ref(false);

// 자금원천 코드 목록
const fundingCodeList = [
  { code: '04', name: '자부담' },
  { code: '05', name: '후원금' },
  { code: '06', name: '수익사업' },
  { code: '07', name: '보조금' }
];

interface FundingInferSettings {
  enabled: boolean;
  useRelAccountFunding: boolean;
  expenseNameRules: Array<{ name: string; funding: string }>;
  incomeFunding: Record<string, string>;
}

const defaultFundingInferSettings = (): FundingInferSettings => ({
  enabled: true,
  useRelAccountFunding: true,
  expenseNameRules: [
    { name: '생계비', funding: '04' },
    { name: '생계비(보조금)', funding: '07' },
  ],
  incomeFunding: {},
});

const fundingInferSettings = ref<FundingInferSettings>(defaultFundingInferSettings());
const fundingSettingsOpen = ref(false);
const matchDialogOpen = ref(false);
const matchKind = ref<'account' | 'rel' | 'funding'>('account');
const matchTargetIds = ref<number[]>([]);
const openMatchDialog = (kind: 'account' | 'rel' | 'funding') => {
  matchKind.value = kind;
  matchTargetIds.value = [...ledgerSelectedIds.value];
  matchDialogOpen.value = true;
};
const fundingSettingSaveState = ref<'idle' | 'saving' | 'saved' | 'error'>('idle');
const fundingIncomeSearch = ref('');
let fundingSettingSaveTimer: ReturnType<typeof setTimeout> | null = null;
let fundingSettingSavedTimer: ReturnType<typeof setTimeout> | null = null;

// 자금원천 자동부여 관련
const isAutoFundingProcessing = ref(false);

// 상대계정 자동부여 관련
const isAutoRelAccProcessing = ref(false);
const isAutoBeneficiaryMatching = ref(false);

// 자동화 모달 (계정·상대계정·자금원천·본인부담금)
const autoDialogOpen = ref(false);
const autoPreviewKind = ref<BankbookAutoKind | null>(null);
const autoPreviewRows = ref<BankbookAutoPreviewRow[]>([]);
const autoPreviewLoading = ref(false);
const autoPreviewError = ref('');
const autoApplying = ref(false);
const autoAccountReadyCount = ref(0);
const autoAccountCountLoading = ref(false);
const autoAccountDisabled = ref(false);
const autoAccountDisabledReason = ref('');
const autoRelReadyCount = ref(0);
const autoRelCountLoading = ref(false);
const isAutoAccountProcessing = ref(false);
const autoPendingFocus = ref<BankbookAutoKind | null>(null);
/** 미리보기에서 확정된 계정·상대계정 패치 (적용 시 표에 보인 값만 저장) */
const autoFillPreviewPatches = ref<Array<{ cbIdx: number; patch: Record<string, string> }>>([]);
/** 본인부담금 미리보기 매칭 결과 */
const autoBenPreviewMatches = ref<Record<string, { cb_idx: number; cb_name: string }>>({});

// 수납관리 엑셀 업로드 모달
const collectionExcelModalOpen = ref(false);
const beneficiaryEmbedRef = ref<{ refresh: () => Promise<void>; isCollectionEmpty?: boolean } | null>(null);

// 수급자 매칭 결과 { [cb_idx]: { cb_idx, cb_name, matchType } }
const beneficiaryMatches = ref<Record<number, { cb_idx: number; cb_name: string; matchType: string }>>({});

// 분개 내역 펼침 상태 { [cb_idx]: ChargeItem[] }
const expandedCollectionRows = ref<Set<number>>(new Set());
const collectionDetails = ref<Record<number, { scode: string; sname: string; charge: number; memo: string }[]>>({});

// 수납관리 모달
const beneficiaryModalSearchName = ref('');
// 통장내역 본인부담금 매칭 데이터 (수금관리에서 입금 정보 표시용)
const beneficiaryModalDepositData = ref<Record<number, { amount: number; date: string }>>({});

// 수급자 목록 (매칭용)
const beneficiaryList = ref<{ cb_idx: number; cb_name: string; cb_ltc_no?: string; cb_status?: string; cc_charge?: number; cc_idx?: number; totalCharge?: number }[]>([]);

// 인라인 수정 관련
const editingRowIdx = ref<number | null>(null);
const inlineEditData = ref<CashbookData>({
  cb_gubun: '',
  cb_scode: '',
  cb_funding: '',
  cb_memo: '',
  cb_price: 0,
  cb_date: '',
  cb_rel_acc: ''
});
const isInlineSaving = ref(false);

// 날짜 관련 (시설 공통 sessionStorage — 다른 리모컨 페이지와 연동)
const memberCodeForYm = computed(() => String(memberCode.value || ''));
const memberYearMonth = useMemberYearMonthOptional(memberCodeForYm);
const currentYear = computed(() => String(memberYearMonth.selectedYear.value));
const currentMonth = computed(() => String(memberYearMonth.selectedMonth.value).padStart(2, '0'));

// 해당 월의 마지막 일자
const lastDayOfMonth = computed(() => {
  return new Date(parseInt(currentYear.value), parseInt(currentMonth.value), 0).getDate();
});

// 월 선택기
const monthPickerRef = ref<HTMLInputElement | null>(null);

// 필터
const filterNeedCheck = ref(false);
const filterReceiptMatch = ref('all');  // 영수증 매칭 필터: all, matched, unmatched
/** 통장내역: 분개된 항목만 (분개 자식 / 원본 그룹) */
const filterSplitOnly = ref(false);
/** 통장내역 표시: cashbook=현금출납부(분개 펼침), upload=통장원본(접힘) */
const ledgerDisplayMode = ref<'cashbook' | 'upload'>('cashbook');
const expandedSplitParents = ref<Record<number, boolean>>({});
const searchScode = ref('');
const searchMemo = ref('');
const filterDayStart = ref<number | null>(null);  // 일자 필터 시작일 (1~31)
const filterDayEnd = ref<number | null>(null);    // 일자 필터 종료일 (1~31)

/** 이달의 메모 (기관·연월) */
const MONTH_MEMO_MAX = 1000;
const monthMemoOpen = ref(false);
const monthMemoText = ref('');
const monthMemoLoadedKey = ref('');
const isMonthMemoLoading = ref(false);
const monthMemoSaveState = ref<'idle' | 'saving' | 'saved' | 'error'>('idle');
let monthMemoSaveTimer: ReturnType<typeof setTimeout> | null = null;
let monthMemoStatusTimer: ReturnType<typeof setTimeout> | null = null;

// 보기 모드 (normal: 일반, grouped: 계정별 그룹화) — 레거시 잔존, 통장내역 보기와 별개
const viewMode = ref<'normal' | 'grouped'>('normal');
// raw = 통장내역, summary = 수납/영수증 임베드 컨테이너, analysis-reference = 계정별내역
const workSection = ref<'raw' | 'adjust' | 'funding' | 'summary' | 'analysis-reference'>('raw');
// summary 안에서 작업 큐: 수납 / 영수증
const workQueue = ref<'collection' | 'receipt' | null>(null);

// ─── 미설정만 보기 (scode | rel_acc | funding)
const activeMissingFilter = ref<'scode' | 'rel_acc' | 'funding' | null>(null);

// 사이드바 상단 배지 타이틀 (현재 탭 이름)
const sidebarTitle = computed(() => {
  if (workSection.value === 'raw') return '통장내역';
  if (workSection.value === 'adjust') return '계정조정';
  if (workSection.value === 'funding') return '자금원천';
  if (workQueue.value === 'collection') return '수납';
  if (workQueue.value === 'receipt') return '영수증';
  if (workSection.value === 'analysis-reference') return '정리 내역 보기';
  return '통장내역';
});

const isCollectionStep = computed(() =>
  workSection.value === 'summary' && workQueue.value === 'collection'
);

const isReceiptStep = computed(() =>
  workSection.value === 'summary' && workQueue.value === 'receipt'
);

const isAnalysisReferenceStep = computed(() =>
  workSection.value === 'analysis-reference'
);

// ─── 이 달(또는 선택된 통장)에 통장내역이 없는가?
// 없으면 계정조정/자금원천/수납영수증/정리내역 탭은 모두 소용없음 → 잠금
const hasNoBankbookData = computed(() => selectedBankCashbookList.value.length === 0);

const bankbookStepKey = computed(() => {
  if (isCollectionStep.value) return 'collection';
  if (isReceiptStep.value) return 'receipt';
  if (isAnalysisReferenceStep.value) return 'analysis-reference';
  return 'raw';
});

const bankbookPageTabs = computed(() => [
  { key: 'raw', title: '통장내역' },
  { key: 'collection', title: '수납', disabled: hasNoBankbookData.value },
  { key: 'receipt', title: '영수증', disabled: hasNoBankbookData.value },
  { key: 'analysis-reference', title: '정리 내역 보기', disabled: hasNoBankbookData.value },
]);
// 시작 가이드: 이 달 데이터가 아예 없을 때 원장+작업대 대신 전체 폭 가이드 표시
const showStartGuide = computed(() => workSection.value === 'raw' && !isLoading.value && hasNoBankbookData.value);

const accountPairModalOpen = ref(false);
const laborRatioModalOpen = ref(false);
const settlementModalOpen = ref(false);
const selectedIncomeScode = ref<string | null>(null);
const isAdjustStateLoading = ref(false);
const adjustIncomes = ref<IncomeBalanceItem[]>([]);
const adjustExpenseCodes = ref<Array<{ scode: string; sname: string }>>([]);

// 계정별 그룹화 펼침 상태 (scode -> boolean)
const expandedScodes = ref<Record<string, boolean>>({});

// 정렬 (기본값: cb_order 오름차순 = 통장내역 원본 순서)
const sortKey = ref<string>('cb_order');
const sortOrder = ref<'asc' | 'desc'>('asc');

// 다이얼로그
const uploadDialogOpen = ref(false);
const deleteDialogOpen = ref(false);
const deleteAllDialogOpen = ref(false);
const dictionaryManageModalOpen = ref(false);

// 업로드 완료 알림
interface UploadNotification {
  id: number;
  show: boolean;
  bankCode: string;
  bankAlias: string;
  fileName: string;
  count: number;
  success: boolean;
  message?: string;
}
const uploadNotifications = ref<UploadNotification[]>([]);
let notificationIdCounter = 0;

// 삭제 대상
const selectedItem = ref<CashbookData | null>(null);

// AI 학습 관련
const newKeywordInput = ref('');
const isAddingKeyword = ref(false);

// 딕셔너리 관리
const dictionaryStats = ref<{ income: number; expense: number; relmap: number } | null>(null);
const selectedDictTable = ref<string | null>(null);
const dictDetailList = ref<DictItem[]>([]);
const dictSearchQuery = ref('');
const isDictDetailLoading = ref(false);

const filteredDictDetailList = computed(() => {
  const q = dictSearchQuery.value.trim().toLowerCase();
  if (!q) return dictDetailList.value;
  return dictDetailList.value.filter((item) => {
    const keyword = String(item.cd_keyword || '').toLowerCase();
    const name = String(item.ac_name || item.cd_scode || '').toLowerCase();
    return keyword.includes(q) || name.includes(q);
  });
});

// 수급자 정보 변경 시 호출
const onBeneficiaryChanged = async () => {
  await loadBeneficiaryList();
  await matchBeneficiaries();
};

// 수납 데이터 삭제 시 호출
const onCollectionDeleted = async () => {
  // 수납관리 모달의 depositData 업데이트만 (매칭은 새로고침하지 않음)
  beneficiaryModalDepositData.value = collectDepositData();
};

// 청구금액 변경 시 호출 - 입금 재분배
const onChargeUpdated = async (cbIdx: number) => {
  // 해당 수급자에 매칭된 통장내역이 있는지 확인
  const hasMatch = Object.values(beneficiaryMatches.value).some(m => m.cb_idx === cbIdx);
  
  if (hasMatch) {
    // 입금 재분배 실행
    await syncDepositToCollection();
    // depositData 업데이트 (모달에서 새 데이터 로드 시 반영)
    beneficiaryModalDepositData.value = collectDepositData();
  }
};

// 수납관리 엑셀 업로드 완료 시 호출
const onCollectionExcelUploaded = async () => {
  await loadBeneficiaryList();
  await loadCashbookData();
  await matchBeneficiaries();
  // 임베드 수납 목록이 depositData/API 기준으로 다시 그려지도록
  beneficiaryModalDepositData.value = collectDepositData();
  await nextTick();
  await beneficiaryEmbedRef.value?.refresh();
};

// 수급자 매칭 다이얼로그
const beneficiaryMatchDialogOpen = ref(false);
const matchTargetItem = ref<CashbookData | null>(null);
const selectedBeneficiaryForMatch = ref<number | null>(null);

// 이월금
const prevMonthBalance = ref(0);

// 영수증 매칭 관련
const selectedCashbookForMatching = ref<CashbookData | null>(null);

// 분개(Split) 모달 관련
const splitModalOpen = ref(false);
const splitTargetCbIdx = ref<number | null>(null);
const isMergingSplit = ref(false);

const openSplitModal = (item: CashbookData) => {
  if (!item.cb_idx) return;
  // 이미 분개된 자식이면 원본(parent) 기준으로 수정
  const parentIdx = isSplitChildItem(item) ? getSplitParentIdx(item) : Number(item.cb_idx);
  if (!parentIdx) return;
  splitTargetCbIdx.value = parentIdx;
  splitModalOpen.value = true;
};

const mergeSplitTarget = async () => {
  const item = splitTargetItem.value;
  if (!item?.cb_idx || !isSplitChildItem(item) || isMergingSplit.value) return;
  if (closingStatus.value?.isClosed) {
    alert('마감된 달에는 분개를 합칠 수 없습니다.');
    return;
  }
  const parentIdx = getSplitParentIdx(item);
  if (!parentIdx) return;
  if (!confirm('분개된 항목을 합쳐 원본 한 건으로 되돌릴까요?')) return;

  isMergingSplit.value = true;
  try {
    const res = await bankbookApi.clearSplits(parentIdx);
    if (!res.data?.success) {
      throw new Error(res.data?.message || '분개 합치기에 실패했습니다.');
    }
    ledgerSelectedIds.value = [];
    await loadCashbookData();
    alert(res.data?.message || '분개를 합쳤습니다.');
  } catch (error: any) {
    alert(error.response?.data?.message || error.message || '분개 합치기 중 오류가 발생했습니다.');
  } finally {
    isMergingSplit.value = false;
  }
};

const onSplitSaved = async () => {
  await loadCashbookData();
};

// 통장 관련
const bankList = ref<BankData[]>([]);
const selectedBankCode = ref<string | null>(null);
const bankManageDialogOpen = ref(false);
const showBankForm = ref(false);
const showInactiveBanks = ref(false); // 종료된 통장 섹션 펼침 여부
const editingBank = ref<BankData | null>(null);
const bankFormData = ref({
  mb_alias: '',
  mb_bank_name: '',
  mb_account_no: '',
  mb_purpose: '',
  mb_biz_gubun: '',
  mb_start_date: '',
  mb_end_date: ''
});
const isSavingBank = ref(false);
const deleteBankDialogOpen = ref(false);
const bankToDelete = ref<BankData | null>(null);
const bankFormSectionRef = ref<HTMLElement | null>(null);
const showBankGuide = ref(false);
const isDeletingBank = ref(false);


// ============================================
// Computed
// ============================================

// 시설의 활성화된 사업구분 목록 (콤마 구분 문자열 파싱)
const facilityBizGubunList = computed(() => {
  const raw = memberData?.value?.m_biz_gubun;
  if (!raw) return [];
  return raw.split(',').map((s: string) => s.trim()).filter(Boolean);
});

// 통장 관리 모달용: 현재 연월 기준 사용중인 통장
const activeBankListForModal = computed(() => {
  return bankList.value.filter(bank => isBankActiveForCurrentMonth(bank));
});

// 통장 관리 모달용: 종료된 통장 (현재 연월에 사용 불가)
const inactiveBankListForModal = computed(() => {
  return bankList.value.filter(bank => !isBankActiveForCurrentMonth(bank));
});

// 사업구분 색상 가져오기
const getBizColor = (bizGubun: string) => {
  return bizGubunColorMap?.value?.[bizGubun] || '#607d8b';
};

// 선택된 통장 정보
const selectedBank = computed(() => {
  if (!selectedBankCode.value) return null;
  return bankList.value.find(b => b.mb_code === selectedBankCode.value) || null;
});

// 통장 폼 저장 가능 여부
const canSaveBank = computed(() => {
  return (bankFormData.value.mb_alias || '').trim() && 
         (bankFormData.value.mb_bank_name || '').trim() && 
         (bankFormData.value.mb_account_no || '').trim();
});

// 수입 계정코드 리스트
const incomeCodeList = computed(() => {
  return accountCodes.value.filter(ac => ac.gubun === 'IN');
});

// 지출 계정코드 리스트
const expenseCodeList = computed(() => {
  return accountCodes.value.filter(ac => ac.gubun === 'OUT');
});

const expensePickerGroups = computed<PickerGroup[]>(() => {
  const expenseCodes = adjustExpenseCodes.value.length
    ? adjustExpenseCodes.value
    : expenseCodeList.value.map((c) => ({ scode: c.scode, sname: c.sname }));
  // 잔액 무관하게 수입 계정 전체 표시 — 잔액 0 이하는 선택지에서 경고 표시
  return adjustIncomes.value
    .map((inc) => ({
      incomeScode: inc.scode,
      incomeSname: inc.sname,
      remaining: inc.remaining,
      paired: inc.paired,
      expenses: inc.paired && inc.allowedExpenses?.length
        ? inc.allowedExpenses
        : expenseCodes,
    }));
});

// 선택된 통장의 데이터 (필터 적용 전, 통장 선택만 적용)
const selectedBankCashbookList = computed(() => {
  if (!selectedBankCode.value) {
    return cashbookList.value;
  }
  return cashbookList.value.filter(item => item.cb_bank_code === selectedBankCode.value);
});

// 통장내역 탭 날짜 포맷은 formatDate 공용 사용

// ─── 공통 통장내역 고정 레이아웃 (통장내역에서 계정조정·자금원천) ───
const usesSharedLedger = computed(() => workSection.value === 'raw');

/** 항목 목록 탭: 요약·기간/계정/적요 검색 기본 표시 */
const showItemSidebarTools = computed(() => usesSharedLedger.value);

const adjustWorkbenchRef = ref<{
  switchTab: (tab: 'in' | 'out') => void;
  toggleRow: (item: CashbookData) => void;
  toggleIncomeRow: (item: CashbookData) => void;
  openSettings?: () => void;
  closeSettings?: () => void;
  setExpenseSelection?: (ids: number[]) => void;
  setIncomeSelection?: (ids: number[]) => void;
  draftFunding?: string | null;
  canSubmitAdjust?: boolean;
  applyAdjust?: () => void;
  pickFunding?: (funding: string) => void;
} | null>(null);

/** 통장내역 우측: 계정 | 상대계정(지출만) | 분개 | 자금원천 */
const rawWorkTab = ref<'account' | 'rel' | 'funding' | 'split'>('account');
const ledgerListTab = ref<'in' | 'out'>('in');
const ledgerSelectedIds = ref<number[]>([]);

const isSplitTabDisabled = computed(() => ledgerSelectedIds.value.length !== 1);

const splitTargetItem = computed(() => {
  if (ledgerSelectedIds.value.length !== 1) return null;
  const id = ledgerSelectedIds.value[0];
  return selectedBankCashbookList.value.find((item) => item.cb_idx === id) || null;
});

const singleSelectedItem = computed(() => splitTargetItem.value);

const selectedLedgerItems = computed(() =>
  selectedBankCashbookList.value.filter(
    (item) => item.cb_idx != null && ledgerSelectedIds.value.includes(item.cb_idx)
  )
);

const deletableSelectedCount = computed(() =>
  selectedLedgerItems.value.filter((item) => isItemEditable(item)).length
);

const deleteTargets = ref<CashbookData[]>([]);
const quickEditMemo = ref('');
const quickEditPriceText = ref('');
const quickEditGubun = ref<'IN' | 'OUT'>('IN');
const quickEditBaseline = ref<{ memo: string; price: number; gubun: 'IN' | 'OUT' }>({
  memo: '',
  price: 0,
  gubun: 'IN',
});
const isQuickEditSaving = ref(false);
const quickEditMemoRef = ref<HTMLInputElement | null>(null);
/** 적요·금액 편집 패널 — 1건 선택 시 펼침 */
const quickEditOpen = ref(false);

const formatPriceInput = (n: number) => {
  const v = Math.trunc(Number(n) || 0);
  if (!v) return '';
  const text = Math.abs(v).toLocaleString('ko-KR');
  return v < 0 ? `-${text}` : text;
};

const parsePriceInput = (value: string) => {
  const raw = String(value || '').replace(/,/g, '').trim();
  if (!raw || raw === '-') return 0;
  const neg = raw.startsWith('-');
  const digits = raw.replace(/[^\d]/g, '');
  const n = parseInt(digits, 10) || 0;
  return neg ? -n : n;
};

const quickEditDirty = computed(() => {
  const price = parsePriceInput(quickEditPriceText.value);
  return (
    String(quickEditMemo.value || '') !== String(quickEditBaseline.value.memo || '')
    || price !== (Number(quickEditBaseline.value.price) || 0)
    || quickEditGubun.value !== quickEditBaseline.value.gubun
  );
});

const syncQuickEditFromSelection = () => {
  const item = singleSelectedItem.value;
  if (!item) {
    quickEditMemo.value = '';
    quickEditPriceText.value = '';
    quickEditGubun.value = 'IN';
    quickEditBaseline.value = { memo: '', price: 0, gubun: 'IN' };
    return;
  }
  const memo = item.cb_memo || '';
  const price = Math.trunc(Number(item.cb_price) || 0);
  const gubun: 'IN' | 'OUT' = item.cb_gubun === 'OUT' ? 'OUT' : 'IN';
  quickEditMemo.value = memo;
  quickEditPriceText.value = formatPriceInput(price);
  quickEditGubun.value = gubun;
  quickEditBaseline.value = { memo, price, gubun };
};

watch(singleSelectedItem, (item) => {
  syncQuickEditFromSelection();
  quickEditOpen.value = !!item;
}, { immediate: true });

const onQuickEditPriceInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  const raw = input.value.replace(/,/g, '');
  const neg = raw.trim().startsWith('-');
  const digits = raw.replace(/[^\d]/g, '');
  if (!digits) {
    quickEditPriceText.value = neg ? '-' : '';
    return;
  }
  const n = parseInt(digits, 10) || 0;
  quickEditPriceText.value = `${neg ? '-' : ''}${n.toLocaleString('ko-KR')}`;
};

const saveQuickEdit = async () => {
  const item = singleSelectedItem.value;
  if (!item?.cb_idx || !quickEditDirty.value) return;
  if (closingStatus.value?.isClosed || !isItemEditable(item)) {
    alert('마감된 통장내역은 수정할 수 없습니다.\n마감 해제 후 수정해주세요.');
    return;
  }

  const memo = String(quickEditMemo.value || '').trim();
  const price = parsePriceInput(quickEditPriceText.value);
  if (!price) {
    alert('금액을 입력하세요.');
    return;
  }
  const gubun = quickEditGubun.value;
  const gubunChanged = gubun !== item.cb_gubun;
  isQuickEditSaving.value = true;
  try {
    await bankbookApi.update(item.cb_idx, {
      cb_memo: memo,
      cb_price: price,
      cb_gubun: gubun,
      ...(gubunChanged ? { cb_scode: '', cb_rel_acc: '' } : {}),
    });
    const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
    if (idx !== -1) {
      cashbookList.value[idx] = {
        ...cashbookList.value[idx],
        cb_memo: memo,
        cb_price: price,
        cb_gubun: gubun,
        ...(gubunChanged ? { cb_scode: '', cb_rel_acc: '' } : {}),
      };
    }
    quickEditBaseline.value = { memo, price, gubun };
    if (gubunChanged && item.cb_idx) {
      const nextTab = gubun === 'IN' ? 'in' : 'out';
      ledgerListTab.value = nextTab;
      ledgerSelectedIds.value = [item.cb_idx];
      await nextTick();
      adjustWorkbenchRef.value?.switchTab(nextTab);
      syncWorkbenchSelection();
    }
    showSaveToast.value = true;
    setTimeout(() => { showSaveToast.value = false; }, 2000);
    await loadAdjustState();
  } catch (error) {
    console.error('적요·금액 저장 오류:', error);
    alert('저장에 실패했습니다.');
  } finally {
    isQuickEditSaving.value = false;
  }
};

const sortByDateOrder = (a: CashbookData, b: CashbookData) => {
  const aT = a.cb_date ? new Date(a.cb_date).getTime() : 0;
  const bT = b.cb_date ? new Date(b.cb_date).getTime() : 0;
  if (aT !== bT) return aT - bT;
  return (Number(a.cb_order) || 0) - (Number(b.cb_order) || 0);
};

const compareSharedLedger = (a: CashbookData, b: CashbookData) => {
  const dir = sortOrder.value === 'asc' ? 1 : -1;
  const key = sortKey.value;
  let cmp = 0;
  if (key === 'cb_order' || key === 'cb_date') {
    cmp = sortByDateOrder(a, b);
  } else if (key === 'cb_price') {
    cmp = (Number(a.cb_price) || 0) - (Number(b.cb_price) || 0);
  } else if (key === 'cb_memo') {
    cmp = String(a.cb_memo || '').localeCompare(String(b.cb_memo || ''), 'ko');
  } else if (key === 'cb_scode') {
    cmp = ledgerScodeLabel(a).localeCompare(ledgerScodeLabel(b), 'ko');
  } else if (key === 'cb_rel_acc') {
    const aLabel = a.cb_gubun === 'IN'
      ? (a.cb_idx ? (beneficiaryMatches.value[a.cb_idx]?.cb_name || '') : '')
      : ledgerRelLabel(a);
    const bLabel = b.cb_gubun === 'IN'
      ? (b.cb_idx ? (beneficiaryMatches.value[b.cb_idx]?.cb_name || '') : '')
      : ledgerRelLabel(b);
    cmp = aLabel.localeCompare(bLabel, 'ko');
  } else if (key === 'cb_funding') {
    cmp = getFundingName(a.cb_funding).localeCompare(getFundingName(b.cb_funding), 'ko');
  } else {
    cmp = String((a as unknown as Record<string, unknown>)[key] || '')
      .localeCompare(String((b as unknown as Record<string, unknown>)[key] || ''), 'ko');
  }
  if (cmp !== 0) return cmp * dir;
  return sortByDateOrder(a, b);
};

const isSplitChildItem = (item: CashbookData) => {
  if (Number(item.is_split_item) === 1) return true;
  const pid = item.parent_idx ?? item.cb_parent_idx;
  return pid != null && Number(pid) > 0;
};

const getSplitParentIdx = (item: CashbookData) => {
  const pid = item.parent_idx ?? item.cb_parent_idx;
  return pid != null ? Number(pid) : 0;
};

interface SharedLedgerGroup {
  parentIdx: number;
  cb_date: string;
  cb_gubun: string;
  cb_price: number;
  cb_memo: string;
  cb_funding?: string;
  cb_bank_code?: string;
  children: CashbookData[];
}

type SharedLedgerRow =
  | { kind: 'item'; key: string; item: CashbookData }
  | { kind: 'group'; key: string; group: SharedLedgerGroup };

const applyLedgerSearch = (list: CashbookData[]) => {
  let next = list;
  if (filterSplitOnly.value) {
    next = next.filter(isSplitChildItem);
  }
  if (searchMemo.value) {
    const q = searchMemo.value.toLowerCase();
    next = next.filter((item) => item.cb_memo && item.cb_memo.toLowerCase().includes(q));
  }
  if (searchScode.value) {
    const q = searchScode.value.toLowerCase();
    next = next.filter((item) => {
      const name = getScodeDisplayName(item.cb_scode, item.cb_gubun);
      return (item.cb_scode && item.cb_scode.toLowerCase().includes(q))
        || name.toLowerCase().includes(q);
    });
  }
  if (filterDayStart.value || filterDayEnd.value) {
    const startDay = filterDayStart.value || 1;
    const endDay = filterDayEnd.value || lastDayOfMonth.value;
    next = next.filter((item) => {
      const day = new Date(item.cb_date).getDate();
      return day >= startDay && day <= endDay;
    });
  }
  return next;
};

const ledgerTabInBadge = computed(() =>
  selectedBankCashbookList.value.filter((item) => item.cb_gubun === 'IN').length
);

const ledgerTabOutBadge = computed(() =>
  selectedBankCashbookList.value.filter((item) => item.cb_gubun === 'OUT').length
);

/** 분개 필터 배지: 분개 원본(parent) 건수 */
const splitFilterCount = computed(() => {
  const parents = new Set<number>();
  for (const item of selectedBankCashbookList.value) {
    if (!isSplitChildItem(item)) continue;
    const pid = getSplitParentIdx(item);
    if (pid) parents.add(pid);
  }
  return parents.size;
});

/** 통장내역 조건 섹션: 초기화 버튼 표시 여부 */
const hasActiveLedgerConditions = computed(() =>
  filterSplitOnly.value
  || !!searchScode.value
  || !!searchMemo.value
  || filterDayStart.value != null
  || filterDayEnd.value != null
  || ledgerDisplayMode.value !== 'cashbook'
);

/** 출납부형 기준 평탄 목록 (선택·작업용) */
const sharedLedgerItems = computed(() => {
  const wantIn = ledgerListTab.value === 'in';
  let list = selectedBankCashbookList.value.filter((item) =>
    wantIn ? item.cb_gubun === 'IN' : item.cb_gubun === 'OUT'
  );
  return applyLedgerSearch(list).sort(compareSharedLedger);
});

const buildUploadGroup = (parentIdx: number, children: CashbookData[]): SharedLedgerGroup => {
  const sorted = [...children].sort(sortByDateOrder);
  const first = sorted[0];
  const fundings = [...new Set(sorted.map((c) => c.cb_funding || '').filter(Boolean))];
  return {
    parentIdx,
    cb_date: first?.cb_date || '',
    cb_gubun: first?.cb_gubun || 'OUT',
    cb_price: sorted.reduce((sum, c) => sum + Math.abs(Number(c.cb_price) || 0), 0),
    cb_memo: first?.cb_memo || '',
    cb_funding: fundings.length === 1 ? fundings[0] : undefined,
    cb_bank_code: first?.cb_bank_code,
    children: sorted,
  };
};

/** 화면 표시용 행 (출납부형=평탄 / 통장원본=그룹) */
const sharedLedgerRows = computed((): SharedLedgerRow[] => {
  const items = sharedLedgerItems.value;
  if (ledgerDisplayMode.value === 'cashbook') {
    return items.map((item) => ({
      kind: 'item' as const,
      key: `item-${item.cb_idx}`,
      item,
    }));
  }

  const groups = new Map<number, CashbookData[]>();
  const singles: CashbookData[] = [];
  for (const item of items) {
    const pid = getSplitParentIdx(item);
    if (pid > 0) {
      const arr = groups.get(pid) || [];
      arr.push(item);
      groups.set(pid, arr);
    } else {
      singles.push(item);
    }
  }

  const rows: SharedLedgerRow[] = [];
  for (const item of singles) {
    rows.push({ kind: 'item', key: `item-${item.cb_idx}`, item });
  }
  for (const [parentIdx, children] of groups.entries()) {
    rows.push({
      kind: 'group',
      key: `group-${parentIdx}`,
      group: buildUploadGroup(parentIdx, children),
    });
  }
  rows.sort((a, b) => {
    const aItem = a.kind === 'item' ? a.item : a.group.children[0];
    const bItem = b.kind === 'item' ? b.item : b.group.children[0];
    return compareSharedLedger(aItem, bItem);
  });
  return rows;
});

const sharedLedgerEmptyMessage = computed(() => {
  if (filterSplitOnly.value) {
    const hasAny = selectedBankCashbookList.value.some((item) =>
      (ledgerListTab.value === 'in' ? item.cb_gubun === 'IN' : item.cb_gubun === 'OUT')
      && isSplitChildItem(item)
    );
    if (!hasAny) return '분개된 항목이 없습니다.';
    return '검색 조건에 맞는 분개 항목이 없습니다.';
  }
  return ledgerListTab.value === 'in' ? '수입 내역이 없습니다.' : '지출 내역이 없습니다.';
});

const isSplitGroupExpanded = (parentIdx: number) => !!expandedSplitParents.value[parentIdx];

const toggleSplitGroup = (parentIdx: number) => {
  expandedSplitParents.value = {
    ...expandedSplitParents.value,
    [parentIdx]: !expandedSplitParents.value[parentIdx],
  };
};

const isSharedGroupPicked = (group: SharedLedgerGroup) => {
  if (!group.children.length) return false;
  return group.children.every((c) => c.cb_idx && ledgerSelectedIds.value.includes(c.cb_idx));
};

const onSharedGroupClick = (group: SharedLedgerGroup) => {
  if (workSection.value !== 'raw') return;
  const editable = group.children.filter((c) => c.cb_idx && isItemEditable(c));
  if (!editable.length) return;

  const wantTab = group.cb_gubun === 'IN' ? 'in' : 'out';
  if (ledgerListTab.value !== wantTab) {
    ledgerListTab.value = wantTab;
    ledgerSelectedIds.value = [];
    adjustWorkbenchRef.value?.switchTab(wantTab);
  }

  const ids = editable.map((c) => c.cb_idx as number);
  const allOn = ids.every((id) => ledgerSelectedIds.value.includes(id));

  if (rawWorkTab.value === 'account' || rawWorkTab.value === 'rel') {
    if (allOn) {
      // 선택 해제: 각 행 toggle
      for (const child of editable) {
        if (wantTab === 'in') adjustWorkbenchRef.value?.toggleIncomeRow(child);
        else adjustWorkbenchRef.value?.toggleRow(child);
      }
    } else {
      // 미선택 것만 토글해 모두 선택
      for (const child of editable) {
        if (child.cb_idx && ledgerSelectedIds.value.includes(child.cb_idx)) continue;
        if (wantTab === 'in') adjustWorkbenchRef.value?.toggleIncomeRow(child);
        else adjustWorkbenchRef.value?.toggleRow(child);
      }
    }
    return;
  }

  if (allOn) {
    const idSet = new Set(ids);
    ledgerSelectedIds.value = ledgerSelectedIds.value.filter((id) => !idSet.has(id));
  } else {
    const merged = new Set([...ledgerSelectedIds.value, ...ids]);
    ledgerSelectedIds.value = [...merged];
  }
};

watch(ledgerDisplayMode, () => {
  expandedSplitParents.value = {};
});

const fundingSelectedItems = computed(() =>
  selectedBankCashbookList.value.filter(
    (item) => item.cb_idx && ledgerSelectedIds.value.includes(item.cb_idx) && isItemEditable(item)
  )
);

const fundingSelectedSameCode = computed(() => {
  const codes = [...new Set(fundingSelectedItems.value.map((item) => item.cb_funding || ''))];
  if (codes.length !== 1 || !codes[0]) return null;
  return codes[0];
});

const hasLedgerScode = (item: CashbookData) =>
  !!(item.cb_scode && String(item.cb_scode).trim());

const hasLedgerRel = (item: CashbookData) =>
  !!(item.cb_rel_acc && String(item.cb_rel_acc).trim());

const ledgerScodeLabel = (item: CashbookData) => {
  if (!hasLedgerScode(item)) return '미설정';
  return getScodeDisplayName(item.cb_scode, item.cb_gubun) || item.cb_scode || '미설정';
};

const ledgerRelLabel = (item: CashbookData) => {
  if (item.cb_gubun === 'IN') return '-';
  if (!hasLedgerRel(item)) return '미설정';
  return getScodeDisplayName(item.cb_rel_acc, 'IN') || item.cb_rel_acc || '미설정';
};

const isSharedRowPicked = (item: CashbookData) =>
  !!item.cb_idx && ledgerSelectedIds.value.includes(item.cb_idx);

const syncWorkbenchSelection = () => {
  const wb = adjustWorkbenchRef.value;
  if (!wb) return;
  if (ledgerListTab.value === 'in') {
    wb.setIncomeSelection?.(ledgerSelectedIds.value);
  } else {
    wb.setExpenseSelection?.(ledgerSelectedIds.value);
  }
};

const setLedgerListTab = (tab: 'in' | 'out') => {
  if (ledgerListTab.value === tab) return;
  ledgerListTab.value = tab;
  ledgerSelectedIds.value = [];
  adjustWorkbenchRef.value?.switchTab(tab);
  syncWorkbenchSelection();
};

const onWorkbenchListTab = (tab: 'in' | 'out') => {
  if (ledgerListTab.value === tab) return;
  ledgerListTab.value = tab;
  ledgerSelectedIds.value = [];
};

const onAdjustSelectionChange = (payload: {
  listTab: 'in' | 'out';
  expenseIds: number[];
  incomeIds: number[];
}) => {
  // 목록의 수입/지출 탭이 기준. 워크벤치 내부 탭이 어긋나면 화면을 수입으로 되돌리지 않는다.
  if (payload.listTab !== ledgerListTab.value) {
    adjustWorkbenchRef.value?.switchTab(ledgerListTab.value);
    return;
  }
  ledgerSelectedIds.value = payload.listTab === 'in' ? payload.incomeIds : payload.expenseIds;
};

/** 계정 탭 — 지금 보고 있는 수입/지출 목록을 유지 */
const openLedgerAccountTab = () => {
  rawWorkTab.value = 'account';
  nextTick(() => {
    adjustWorkbenchRef.value?.switchTab(ledgerListTab.value);
    syncWorkbenchSelection();
  });
};

const clearLedgerSelection = () => {
  ledgerSelectedIds.value = [];
  syncWorkbenchSelection();
};

const selectableVisibleLedgerIds = computed(() =>
  sharedLedgerItems.value
    .filter((item) => item.cb_idx && isItemEditable(item))
    .map((item) => item.cb_idx as number)
);

const isAllVisibleLedgerSelected = computed(() => {
  const ids = selectableVisibleLedgerIds.value;
  return ids.length > 0 && ids.every((id) => ledgerSelectedIds.value.includes(id));
});

const isSomeVisibleLedgerSelected = computed(() => {
  const ids = selectableVisibleLedgerIds.value;
  return ids.some((id) => ledgerSelectedIds.value.includes(id));
});

const selectAllVisibleLedger = () => {
  ledgerSelectedIds.value = [...selectableVisibleLedgerIds.value];
  syncWorkbenchSelection();
};

const toggleSelectAllVisibleLedger = () => {
  if (isAllVisibleLedgerSelected.value) {
    clearLedgerSelection();
    return;
  }
  selectAllVisibleLedger();
};

const onSharedLedgerClick = (item: CashbookData) => {
  if (workSection.value !== 'raw') return;
  if (!item.cb_idx || !isItemEditable(item)) return;

  // 수입/지출 탭과 행 구분이 다르면 탭을 맞춘 뒤 선택
  const wantTab = item.cb_gubun === 'IN' ? 'in' : 'out';
  if (ledgerListTab.value !== wantTab) {
    ledgerListTab.value = wantTab;
    ledgerSelectedIds.value = [];
    adjustWorkbenchRef.value?.switchTab(wantTab);
  }

  // 계정·상대계정·자금원천 탭: 워크벤치 선택을 토글 (조정하기는 워크벤치 선택 기준,
  // 워크벤치가 selection-change를 emit해 부모 ledgerSelectedIds와 자동 동기화됨)
  if (
    rawWorkTab.value === 'account' ||
    rawWorkTab.value === 'rel' ||
    rawWorkTab.value === 'funding'
  ) {
    if (wantTab === 'in') {
      adjustWorkbenchRef.value?.toggleIncomeRow(item);
    } else {
      adjustWorkbenchRef.value?.toggleRow(item);
    }
    return;
  }

  // 그 외(분개 탭 등): 부모 선택만 토글
  if (ledgerSelectedIds.value.includes(item.cb_idx)) {
    ledgerSelectedIds.value = ledgerSelectedIds.value.filter((id) => id !== item.cb_idx);
  } else {
    ledgerSelectedIds.value = [...ledgerSelectedIds.value, item.cb_idx];
  }
};

watch(workSection, (section, prev) => {
  if (section === 'adjust' || section === 'funding') {
    rawWorkTab.value = section === 'adjust' ? 'account' : 'funding';
    workSection.value = 'raw';
    return;
  }
  // adjust/funding → raw 리다이렉트 직후에는 선택 유지
  if (prev === 'adjust' || prev === 'funding') return;
  ledgerSelectedIds.value = [];
});

watch(rawWorkTab, (tab) => {
  if (tab === 'account' || tab === 'rel') {
    nextTick(() => syncWorkbenchSelection());
  }
});

watch(ledgerListTab, (tab) => {
  // 수입 목록: 상대계정 숨김 → 계정으로
  if (tab === 'in' && rawWorkTab.value === 'rel') {
    rawWorkTab.value = 'account';
  }
});

watch(ledgerSelectedIds, (ids) => {
  // 2건 이상 선택 시 분개 탭 비활성 → 다른 탭으로 이동
  if (ids.length !== 1 && rawWorkTab.value === 'split') {
    rawWorkTab.value = 'account';
  }
});

const workbenchIncomes = computed(() =>
  adjustIncomes.value.map((item) => ({
    ...item,
    totalIncome: Number(item.totalIncome) || 0,
    usedExpense: Number(item.usedExpense) || 0,
  }))
);

const pendingAdjustList = computed(() =>
  selectedBankCashbookList.value.filter((item) => {
    if (item.cb_gubun !== 'OUT') return false;
    const noScode = !item.cb_scode || !String(item.cb_scode).trim();
    const noRel = !item.cb_rel_acc || !String(item.cb_rel_acc).trim();
    return noScode || noRel;
  })
);

const doneAdjustList = computed(() =>
  selectedBankCashbookList.value.filter((item) =>
    item.cb_gubun === 'OUT' && !!item.cb_scode && !!item.cb_rel_acc
  )
);

const pendingIncomeList = computed(() =>
  selectedBankCashbookList.value.filter((item) =>
    item.cb_gubun === 'IN' && (!item.cb_scode || !String(item.cb_scode).trim())
  )
);

const doneIncomeList = computed(() =>
  selectedBankCashbookList.value.filter((item) =>
    item.cb_gubun === 'IN' && !!item.cb_scode && String(item.cb_scode).trim() !== ''
  )
);

const adjustProgress = computed(() => {
  const total = selectedBankCashbookList.value.filter((item) => item.cb_gubun === 'OUT').length;
  const done = doneAdjustList.value.length;
  return {
    total,
    done,
    percent: total ? Math.round((done / total) * 100) : 0,
  };
});

const canOpenClosing = computed(() =>
  !!selectedBankCode.value
  && !closingStatus.value?.isClosed
  && selectedBankCashbookList.value.length > 0
);

const pendingAdjustCount = computed(() =>
  pendingAdjustList.value.length + pendingIncomeList.value.length
);

/** 계정 탭 완료: 수입·지출 계정(scode) 모두 지정 */
const isAccountTabDone = computed(() => {
  if (!selectedBankCashbookList.value.length) return false;
  const incomePending = pendingIncomeList.value.length > 0;
  const expensePending = selectedBankCashbookList.value.some(
    (item) => item.cb_gubun === 'OUT' && (!item.cb_scode || !String(item.cb_scode).trim())
  );
  return !incomePending && !expensePending;
});

/** 상대계정 탭 완료: 지출의 상대계정 모두 지정 */
const isRelTabDone = computed(() => {
  if (!selectedBankCashbookList.value.length) return false;
  return !selectedBankCashbookList.value.some(
    (item) => item.cb_gubun === 'OUT' && (!item.cb_rel_acc || !String(item.cb_rel_acc).trim())
  );
});

const isFundingTabDone = computed(() =>
  selectedBankCashbookList.value.length > 0 && unfundedCount.value === 0
);

// 스텝 클릭
//   raw(+adjust/funding 우측탭) / collection / receipt / analysis-reference
//   (구 summary 현금출납부 탭은 통장내역 보기「현금출납부」로 통합)
const goStep = (
  step: 'raw' | 'adjust' | 'rel' | 'funding' | 'collection' | 'receipt' | 'summary' | 'analysis-reference',
  opts?: { keepReceiptItem?: boolean; collectionSearch?: string | null; force?: boolean }
) => {
  // 통장내역이 없으면 raw 외에는 이동 불가 (가이드 force 제외)
  if (
    hasNoBankbookData.value
    && !opts?.force
    && step !== 'raw'
    && step !== 'adjust'
    && step !== 'rel'
    && step !== 'funding'
  ) return;

  // 구 현금출납부 탭 → 통장내역 + 현금출납부(분개 펼침) 보기
  if (step === 'summary') {
    workSection.value = 'raw';
    workQueue.value = null;
    activeMissingFilter.value = null;
    filterReceiptMatch.value = 'all';
    ledgerDisplayMode.value = 'cashbook';
    return;
  }

  // 계정·상대계정·자금원천 → 통장내역 + 우측 서브탭
  if (step === 'adjust' || step === 'rel' || step === 'funding') {
    workSection.value = 'raw';
    workQueue.value = null;
    activeMissingFilter.value = null;
    filterReceiptMatch.value = 'all';
    if (step === 'rel') {
      ledgerListTab.value = 'out';
      rawWorkTab.value = 'rel';
      nextTick(() => {
        adjustWorkbenchRef.value?.switchTab('out');
        syncWorkbenchSelection();
      });
      return;
    }
    rawWorkTab.value = step === 'adjust' ? 'account' : 'funding';
    nextTick(() => {
      adjustWorkbenchRef.value?.switchTab(ledgerListTab.value);
      syncWorkbenchSelection();
    });
    return;
  }

  if (step === 'collection' || step === 'receipt') {
    workSection.value = 'summary';
    workQueue.value = step;
    activeMissingFilter.value = null;
    filterReceiptMatch.value = 'all';
    if (step === 'collection') {
      if (opts && 'collectionSearch' in opts) {
        beneficiaryModalSearchName.value = opts.collectionSearch || '';
      } else {
        beneficiaryModalSearchName.value = '';
      }
      beneficiaryModalDepositData.value = collectDepositData();
    }
    if (step === 'receipt' && !opts?.keepReceiptItem) {
      selectedCashbookForMatching.value = null;
    }
  } else {
    workSection.value = step;
    workQueue.value = null;
    activeMissingFilter.value = null;
    filterReceiptMatch.value = 'all';
    if (step === 'raw') {
      nextTick(() => {
        adjustWorkbenchRef.value?.switchTab(ledgerListTab.value);
        syncWorkbenchSelection();
      });
    }
  }
};

usePageTabNav('bankbook', bankbookPageTabs, bankbookStepKey, (key) => {
  if (
    key === 'raw'
    || key === 'collection'
    || key === 'receipt'
    || key === 'analysis-reference'
  ) {
    goStep(key);
  }
});

// 데이터가 없어지면 자동으로 통장내역(raw) 탭으로 복귀
watch(hasNoBankbookData, (isEmpty) => {
  if (isEmpty && workSection.value !== 'raw') {
    workSection.value = 'raw';
    workQueue.value = null;
    activeMissingFilter.value = null;
    filterReceiptMatch.value = 'all';
  }
});

// 구 현금출납부(summary·큐없음) 잔존 상태 → 통장내역으로 복귀
watch(
  [workSection, workQueue],
  () => {
    if (workSection.value === 'summary' && !workQueue.value) {
      workSection.value = 'raw';
      ledgerDisplayMode.value = 'cashbook';
    }
  }
);

const bankNameMap = computed(() => {
  const map: Record<string, string> = {};
  for (const bank of bankList.value) {
    map[bank.mb_code] = bank.mb_alias;
  }
  return map;
});

// 요약 데이터 (선택된 통장 기준)
const summaryData = computed(() => {
  const incomeItems = selectedBankCashbookList.value.filter(item => item.cb_gubun === 'IN');
  const expenseItems = selectedBankCashbookList.value.filter(item => item.cb_gubun === 'OUT');
  const income = incomeItems.reduce((sum, item) => sum + (Number(item.cb_price) || 0), 0);
  const expense = expenseItems.reduce((sum, item) => sum + (Number(item.cb_price) || 0), 0);
  return { 
    income, 
    expense, 
    incomeCount: incomeItems.length, 
    expenseCount: expenseItems.length 
  };
});

// 누적 잔액
const totalBalance = computed(() => {
  return Number(prevMonthBalance.value) + summaryData.value.income - summaryData.value.expense;
});

/** 회계분석과 동일: 당해 1월~선택월 누적 인건비비율 */
const laborRatioYtd = ref<{
  requiredPercent: number;
  ratio: number;
  met: boolean;
  income: number;
  expense: number;
  needAmount: number;
  cumulativeLabel?: string;
  fromWage?: boolean;
} | null>(null);

const loadLaborBaseRatio = async () => {
  if (!currentYear.value || !memberCode.value || !currentMonth.value) return;
  try {
    const res = await bankbookApi.getLaborBaseRatio(
      String(currentYear.value),
      selectedBizGubun.value || undefined,
      {
        memberCode: String(memberCode.value),
        month: currentMonth.value,
        bankCode: selectedBankCode.value,
      }
    );
    const labor = res.data?.data?.labor;
    const pct = Number(labor?.requiredPercent ?? res.data?.data?.requiredPercent);
    const required = Number.isFinite(pct) ? pct : 60;
    if (labor) {
      laborRatioYtd.value = {
        requiredPercent: required,
        ratio: Number(labor.ratio) || 0,
        met: !!labor.met,
        income: Number(labor.income) || 0,
        expense: Number(labor.expense) || 0,
        needAmount: Number(labor.needAmount) || 0,
        cumulativeLabel: labor.cumulativeLabel || undefined,
        fromWage: !!labor.fromWage,
      };
    } else {
      laborRatioYtd.value = {
        requiredPercent: required,
        ratio: 0,
        met: false,
        income: 0,
        expense: 0,
        needAmount: 0,
      };
    }
  } catch (error) {
    console.error('인건비 기준비율 조회 오류:', error);
    laborRatioYtd.value = {
      requiredPercent: 60,
      ratio: 0,
      met: false,
      income: 0,
      expense: 0,
      needAmount: 0,
    };
  }
};

const laborRatioSummary = computed(() => {
  const data = laborRatioYtd.value;
  const required = data?.requiredPercent ?? 60;
  const income = data?.income ?? 0;
  const expense = data?.expense ?? 0;
  const hasIncome = income > 0;
  const ratio = data?.ratio ?? 0;
  const met = !!data?.met && hasIncome;
  const need = data?.needAmount ?? 0;
  const cumLabel = data?.cumulativeLabel
    || (currentMonth.value
      ? (Number(currentMonth.value) <= 1 ? '1월 누적' : `1~${Number(currentMonth.value)}월 누적`)
      : '');
  return {
    hasIncome,
    income,
    expense,
    ratio,
    required,
    met,
    need,
    cumulativeLabel: cumLabel,
    ratioText: hasIncome ? String(ratio) : '-',
    requiredText: String(required),
    barPct: hasIncome ? Math.min(100, Math.round((ratio / Math.max(required, 0.01)) * 100)) : 0,
    iconColor: !hasIncome ? '#94a3b8' : (met ? '#0d9488' : '#dc2626'),
    hint: !hasIncome
      ? `${cumLabel} 인건비수입(112·611·613)이 없어 비율을 계산하지 않습니다`
      : `${cumLabel} 인건비지출÷인건비수입×100 = ${ratio}% (기준 ${required}%)${met ? ' · 충족' : ` · 미충족 · 부족 ${need.toLocaleString()}원`}`,
  };
});

// 확정 여부 (선택된 통장 기준)
const isConfirmed = computed(() => {
  return selectedBankCashbookList.value.length > 0 && selectedBankCashbookList.value.every(item => item.cb_confirmed === 1);
});

// 모든 계정코드가 채워졌는지 (선택된 통장 기준)
const allScodesFilled = computed(() => {
  return selectedBankCashbookList.value.every(item => item.cb_scode && item.cb_scode.trim());
});

// 확인 필요 항목 수 (선택된 통장 기준)
const needReviewCount = computed(() => {
  return selectedBankCashbookList.value.filter(item => {
    if (!item.cb_need_check) return false;
    try {
      const info = JSON.parse(item.cb_need_check);
      return !!info.reviewReason;
    } catch {
      return false;
    }
  }).length;
});

// 영수증 매칭 카운트 (선택된 통장 기준 · 지출만)
const matchedCount = computed(() => {
  return selectedBankCashbookList.value.filter((item) =>
    item.cb_gubun === 'OUT' && Number(item.cb_receipt_matched) === 1
  ).length;
});

/** 수납 대상: 본인부담금수입(112) */
const isCollectionTarget = (item: CashbookData) =>
  item.cb_gubun === 'IN' && item.cb_scode === '112';

/** 수납(수급자) 매칭이 아직 안 된 본인부담금 */
const needsCollectionMatch = (item: CashbookData) =>
  isCollectionTarget(item) && !!item.cb_idx && !beneficiaryMatches.value[item.cb_idx];

/** 영수증 매칭이 필요한 지출 */
const needsReceiptMatch = (item: CashbookData) =>
  item.cb_gubun === 'OUT' && Number(item.cb_receipt_matched) !== 1;

const collectionPendingCount = computed(() =>
  selectedBankCashbookList.value.filter(needsCollectionMatch).length
);

const receiptPendingCount = computed(() =>
  selectedBankCashbookList.value.filter(needsReceiptMatch).length
);

// ─── 탭별 시작 가이드 (작업이 시작되지 않았을 때 먼저 표시) ───
// 수납 탭: 임베드된 수납관리가 비어 있으면 가이드 표시
const showCollectionGuide = computed(() =>
  isCollectionStep.value && beneficiaryEmbedRef.value?.isCollectionEmpty === true
);

// 영수증 탭: 이 달 지출(영수증 대상) / 매칭 완료 건수
const receiptExpenseCount = computed(() =>
  selectedBankCashbookList.value.filter(item => item.cb_gubun === 'OUT').length
);
const receiptMatchedCount = computed(() =>
  selectedBankCashbookList.value.filter(item => item.cb_gubun === 'OUT' && Number(item.cb_receipt_matched) === 1).length
);
const receiptGuideDismissed = ref(false);
watch([currentYear, currentMonth, selectedBankCode], () => { receiptGuideDismissed.value = false; });
const showReceiptGuide = computed(() =>
  isReceiptStep.value && !isLoading.value && receiptExpenseCount.value === 0
);

// 이미 매칭된 수급자 이름 목록 (수금관리에서 사용 중인 수급자)
const matchedBeneficiaryNames = computed(() => {
  const names = new Set<string>();
  Object.values(beneficiaryMatches.value).forEach(match => {
    if (match?.cb_name) {
      names.add(match.cb_name);
    }
  });
  return names;
});

// 자금원천 미설정 항목 수 (선택된 통장 기준)
const unfundedCount = computed(() => {
  return selectedBankCashbookList.value.filter(item => !item.cb_funding || item.cb_funding === '').length;
});

const unfundedItems = computed(() =>
  selectedBankCashbookList.value.filter(item => !item.cb_funding || item.cb_funding === '')
);

const fundingWorkList = computed(() =>
  unfundedItems.value.map((item) => ({
    item,
    suggested: inferFundingForItem(item),
  }))
);

const fundingInferableCount = computed(() =>
  fundingWorkList.value.filter((row) => !!row.suggested && isItemEditable(row.item)).length
);

watch(selectedBankCashbookList, (list) => {
  const valid = new Set(
    list
      .map((item) => item.cb_idx)
      .filter((id): id is number => typeof id === 'number')
  );
  ledgerSelectedIds.value = ledgerSelectedIds.value.filter((id) => valid.has(id));
});

// 통장별 항목 개수 맵
const bankCountMap = computed(() => {
  const map: Record<string, number> = {};
  for (const item of cashbookList.value) {
    const bankCode = item.cb_bank_code || '';
    if (!map[bankCode]) {
      map[bankCode] = 0;
    }
    map[bankCode]++;
  }
  return map;
});

// 특정 통장이 마감되었는지 확인 (전체 모드에서 사용)
const isBankClosed = (bankCode: string): boolean => {
  // 특정 통장 선택 시: closingStatus 사용
  if (selectedBankCode.value) {
    return closingStatus.value?.isClosed || false;
  }
  // 전체 모드: closingSummary에서 해당 통장 마감 상태 확인
  if (closingSummary.value?.banks) {
    const bank = closingSummary.value.banks.find(b => b.mb_code === bankCode);
    return bank?.isClosed || false;
  }
  return false;
};

// 특정 항목이 수정/삭제 가능한지 확인
const isItemEditable = (item: CashbookData): boolean => {
  return !isBankClosed(item.cb_bank_code || '');
};

/** 통장내역 업로드 차단: 선택 통장 마감, 또는 전체 모드에서 열린 통장이 없음 */
const isBankbookUploadDisabled = computed(() => {
  if (selectedBankCode.value) {
    return !!closingStatus.value?.isClosed;
  }
  const banks = bankList.value || [];
  if (!banks.length) return false;
  return banks.every((b: BankData) => isBankClosed(b.mb_code));
});

/** 수납 엑셀: 특정 통장 선택 중 마감이면 차단 (전체 모드는 허용) */
const isCollectionExcelDisabled = computed(() => {
  if (!selectedBankCode.value) return false;
  return !!closingStatus.value?.isClosed;
});

const markCashbookListUnconfirmed = () => {
  cashbookList.value.forEach((row) => {
    row.cb_confirmed = 0;
  });
};

/** 계정조정 저장 전: 마감·확정 상태를 확인하고 필요 시 확정 해제 */
const ensureBankbookAdjustable = async (items: CashbookData[]): Promise<boolean> => {
  if (!items.length) return false;

  if (items.some((item) => isBankClosed(item.cb_bank_code || ''))) {
    alert('마감된 통장 항목은 수정할 수 없습니다.');
    return false;
  }

  const hasConfirmed = items.some((item) => Number(item.cb_confirmed) === 1);
  if (!hasConfirmed) return true;

  if (closingStatus.value?.isClosed) {
    alert('확정된 항목은 수정할 수 없습니다. 마감 해제 후 다시 시도해 주세요.');
    return false;
  }

  try {
    await bankbookApi.unconfirm(
      memberCode.value,
      currentYear.value,
      currentMonth.value,
      selectedBizGubun.value || undefined
    );
    markCashbookListUnconfirmed();
    return true;
  } catch (error) {
    console.error(error);
    alert('확정 해제에 실패했습니다. 확정된 항목은 수정할 수 없습니다.');
    return false;
  }
};

const getWorkbenchSaveErrorMessage = (results: PromiseSettledResult<unknown>[]) => {
  const rejected = results.filter((result) => result.status === 'rejected') as PromiseRejectedResult[];
  if (!rejected.length) return '';
  const confirmed = rejected.some((result) => {
    const msg = String(
      (result.reason as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
      || (result.reason as Error)?.message
      || ''
    );
    return msg.includes('확정');
  });
  if (confirmed) return '확정된 항목은 수정할 수 없습니다.';
  return `${rejected.length}건 저장에 실패했습니다.`;
};

// 수급자별 매칭 건수 계산
const beneficiaryMatchCount = computed(() => {
  const countMap: Record<number, number> = {};
  Object.values(beneficiaryMatches.value).forEach(match => {
    if (match?.cb_idx) {
      countMap[match.cb_idx] = (countMap[match.cb_idx] || 0) + 1;
    }
  });
  return countMap;
});

// 매칭 가능한 수급자 목록 (퇴소자만 제외, 이미 매칭된 수급자도 포함 + 매칭 건수 표시)
const availableBeneficiaryList = computed(() => {
  return beneficiaryList.value
    .filter(b => b.cb_status !== '퇴소자')
    .map(b => ({
      ...b,
      matchCount: beneficiaryMatchCount.value[b.cb_idx] || 0,
      displayName: beneficiaryMatchCount.value[b.cb_idx] 
        ? `${b.cb_name} (${beneficiaryMatchCount.value[b.cb_idx]}건 매칭)` 
        : b.cb_name
    }));
});

// 필터링된 목록
const filteredCashbookList = computed(() => {
  let list = [...cashbookList.value];
  
  // 통장 필터 (선택된 통장이 있는 경우)
  if (selectedBankCode.value) {
    list = list.filter(item => item.cb_bank_code === selectedBankCode.value);
  }
  
  // 수입/지출 탭
  if (ledgerListTab.value === 'in') {
    list = list.filter(item => item.cb_gubun === 'IN');
  } else {
    list = list.filter(item => item.cb_gubun === 'OUT');
  }
  
  // 확인필요 필터
  if (filterNeedCheck.value) {
    list = list.filter(item => {
      if (!item.cb_need_check) return false;
      try {
        const info = JSON.parse(item.cb_need_check);
        return !!info.reviewReason;
      } catch {
        return false;
      }
    });
  }
  
  // 계정 검색
  if (searchScode.value) {
    const query = searchScode.value.toLowerCase();
    list = list.filter(item => {
      const scodeName = getScodeDisplayName(item.cb_scode, item.cb_gubun);
      return scodeName.toLowerCase().includes(query) || 
             (item.cb_scode && item.cb_scode.toLowerCase().includes(query));
    });
  }
  
  // 적요 검색
  if (searchMemo.value) {
    const query = searchMemo.value.toLowerCase();
    list = list.filter(item => item.cb_memo && item.cb_memo.toLowerCase().includes(query));
  }
  
  // 일자 기간 필터 (선택한 연월 내에서)
  if (filterDayStart.value || filterDayEnd.value) {
    const startDay = filterDayStart.value || 1;
    const endDay = filterDayEnd.value || lastDayOfMonth.value;
    list = list.filter(item => {
      const day = new Date(item.cb_date).getDate();
      return day >= startDay && day <= endDay;
    });
  }
  
  // 작업 큐 / 영수증 필터
  // collection: 본인부담금 수납 미매칭만
  // receipt 큐 / unmatched: 지출 영수증 미매칭만
  if (workQueue.value === 'collection') {
    list = list.filter(needsCollectionMatch);
  } else if (workQueue.value === 'receipt') {
    list = list.filter(needsReceiptMatch);
  } else if (filterReceiptMatch.value === 'matched') {
    list = list.filter((item) => item.cb_gubun === 'OUT' && item.cb_receipt_matched === 1);
  } else if (filterReceiptMatch.value === 'unmatched') {
    list = list.filter(needsReceiptMatch);
  }

  // 미설정 필터 (스텝별 진입 시)
  if (activeMissingFilter.value === 'scode') {
    list = list.filter(item => !item.cb_scode || !String(item.cb_scode).trim());
  } else if (activeMissingFilter.value === 'rel_acc') {
    list = list.filter(item => item.cb_gubun === 'OUT' && (!item.cb_rel_acc || !String(item.cb_rel_acc).trim()));
  } else if (activeMissingFilter.value === 'funding') {
    list = list.filter(item => !item.cb_funding || item.cb_funding === '');
  }

  if (selectedIncomeScode.value) {
    const incomeScode = selectedIncomeScode.value;
    list = list.filter((item) => {
      if (item.cb_gubun === 'IN') return item.cb_scode === incomeScode;
      const rel = item.cb_rel_acc || '';
      return rel === incomeScode || rel === '';
    });
  }
  
  // 정렬
  list.sort((a, b) => {
    // 날짜(cb_order) 컬럼 정렬은 cb_order 단독이 아니라
    // 실제 날짜(일 1→31) → cb_order → cb_idx 복합 기준으로 정렬 (백엔드 ORDER BY와 동일)
    // cb_order는 "같은 날짜 내 순서"이므로 단독 정렬 시 날짜가 뒤섞임
    // cb_date 문자열 형식에 좌우되지 않도록 타임스탬프로 비교
    if (sortKey.value === 'cb_order') {
      const aTime = a.cb_date ? new Date(a.cb_date).getTime() : 0;
      const bTime = b.cb_date ? new Date(b.cb_date).getTime() : 0;
      let comparison = (isNaN(aTime) ? 0 : aTime) - (isNaN(bTime) ? 0 : bTime);
      if (comparison === 0) {
        comparison = (Number(a.cb_order) || 0) - (Number(b.cb_order) || 0);
      }
      if (comparison === 0) {
        comparison = (Number(a.cb_idx) || 0) - (Number(b.cb_idx) || 0);
      }
      return sortOrder.value === 'asc' ? comparison : -comparison;
    }

    let aVal = a[sortKey.value as keyof CashbookData];
    let bVal = b[sortKey.value as keyof CashbookData];
    
    if (aVal === undefined || aVal === null) return 1;
    if (bVal === undefined || bVal === null) return -1;
    
    // cb_price, cb_idx 등 숫자 필드는 숫자로 변환하여 비교
    const numericFields = ['cb_order', 'cb_price', 'cb_idx', 'cb_bank_balance'];
    if (numericFields.includes(sortKey.value)) {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }
    
    let comparison = 0;
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }
    
    return sortOrder.value === 'asc' ? comparison : -comparison;
  });

  return list;
});

// 필터 빈 메시지
const getFilteredEmptyMessage = computed(() => {
  const filters = [];
  filters.push(ledgerListTab.value === 'in' ? '수입' : '지출');
  if (filterNeedCheck.value) filters.push('확인필요');
  if (filterDayStart.value || filterDayEnd.value) {
    const startDay = filterDayStart.value || 1;
    const endDay = filterDayEnd.value || lastDayOfMonth.value;
    filters.push(`기간: ${startDay}일 ~ ${endDay}일`);
  }
  if (searchScode.value) filters.push(`계정: ${searchScode.value}`);
  if (searchMemo.value) filters.push(`적요: ${searchMemo.value}`);
  
  return `${filters.join(', ')} 조건에 맞는 항목이 없습니다.`;
});

// 계정별 그룹화 인터페이스
interface GroupedAccount {
  scode: string;
  scodeName: string;
  gubun: 'IN' | 'OUT' | 'MIXED';
  items: CashbookData[];
  totalIncome: number;
  totalExpense: number;
  // 완료율 정보
  relAccFilledCount: number;  // 상대계정 입력된 수 (지출만 해당)
  relAccTotalCount: number;   // 상대계정 필요한 총 수 (지출 항목 수)
  memoFilledCount: number;    // 적요 입력된 수
}

// 계정별 그룹화된 목록
const groupedByAccount = computed((): GroupedAccount[] => {
  const groups: Record<string, GroupedAccount> = {};
  const UNSET_KEY = '__UNSET__';
  
  for (const item of filteredCashbookList.value) {
    const scode = item.cb_scode?.trim() || UNSET_KEY;
    
    if (!groups[scode]) {
      groups[scode] = {
        scode: scode,
        scodeName: scode === UNSET_KEY ? '계정조정 미완료' : getScodeDisplayName(scode, item.cb_gubun),
        gubun: item.cb_gubun as 'IN' | 'OUT',
        items: [],
        totalIncome: 0,
        totalExpense: 0,
        relAccFilledCount: 0,
        relAccTotalCount: 0,
        memoFilledCount: 0
      };
    }
    
    groups[scode].items.push(item);
    
    if (item.cb_gubun === 'IN') {
      groups[scode].totalIncome += Number(item.cb_price) || 0;
    } else {
      groups[scode].totalExpense += Number(item.cb_price) || 0;
      // 지출 항목은 상대계정 필요
      groups[scode].relAccTotalCount++;
      if (item.cb_rel_acc && item.cb_rel_acc.trim()) {
        groups[scode].relAccFilledCount++;
      }
    }
    
    // 적요 완료 체크
    if (item.cb_memo && item.cb_memo.trim()) {
      groups[scode].memoFilledCount++;
    }
    
    // 수입/지출이 섞여있으면 MIXED로 설정
    if (groups[scode].gubun !== item.cb_gubun && groups[scode].gubun !== 'MIXED') {
      groups[scode].gubun = 'MIXED';
    }
  }
  
  // 배열로 변환 후 정렬 (계정조정 미완료는 맨 위, 나머지는 계정명 순)
  return Object.values(groups).sort((a, b) => {
    if (a.scode === UNSET_KEY) return -1;
    if (b.scode === UNSET_KEY) return 1;
    return a.scodeName.localeCompare(b.scodeName);
  });
});

// 완료율 계산 헬퍼
const getCompletionPercent = (filled: number, total: number): number => {
  if (total === 0) return 100;
  return Math.round((filled / total) * 100);
};

// ============================================
// 연월 변경 시 저장 및 데이터 로드
// ============================================

watch([currentYear, currentMonth], () => {
  // 연월 변경 시 데이터 로드 및 일자 필터 초기화
  filterDayStart.value = null;
  filterDayEnd.value = null;
  
  // 선택된 통장이 현재 연월에 비활성화되면 "전체"로 변경
  if (selectedBankCode.value) {
    const selectedBank = bankList.value.find(b => b.mb_code === selectedBankCode.value);
    if (selectedBank && !isBankActiveForCurrentMonth(selectedBank)) {
      selectedBankCode.value = null;
    }
  }
  
  loadCashbookData();
  loadClosingStatus();
  loadHeumieumStatus();
  loadMonthMemo();
  loadLaborBaseRatio();
}, { immediate: false });

// 일자 필터는 프론트엔드 필터링이므로 API 호출 불필요 (filteredCashbookData에서 처리)

// ============================================
// 이달의 메모
// ============================================
const loadMonthMemo = async () => {
  if (!memberCode.value || !currentYear.value || !currentMonth.value) return;
  const key = `${memberCode.value}:${currentYear.value}-${currentMonth.value}`;
  if (monthMemoSaveTimer) {
    clearTimeout(monthMemoSaveTimer);
    monthMemoSaveTimer = null;
  }
  isMonthMemoLoading.value = true;
  monthMemoSaveState.value = 'idle';
  try {
    const res = await bankbookApi.getMonthMemo(
      memberCode.value,
      String(currentYear.value),
      String(currentMonth.value)
    );
    const memo = String(res.data?.data?.memo || '');
    monthMemoText.value = memo.slice(0, MONTH_MEMO_MAX);
    monthMemoLoadedKey.value = key;
    // 내용이 있으면 접힌 채로 점만 표시 (펼침은 사용자 클릭)
    if (!memo.trim()) monthMemoOpen.value = false;
  } catch (error) {
    console.error('이달의 메모 조회 오류:', error);
    monthMemoText.value = '';
    monthMemoLoadedKey.value = key;
  } finally {
    isMonthMemoLoading.value = false;
  }
};

const saveMonthMemoNow = async () => {
  if (!memberCode.value || !currentYear.value || !currentMonth.value) return;
  if (closingStatus.value?.isClosed) return;
  if (monthMemoSaveTimer) {
    clearTimeout(monthMemoSaveTimer);
    monthMemoSaveTimer = null;
  }
  const text = monthMemoText.value.slice(0, MONTH_MEMO_MAX);
  monthMemoText.value = text;
  monthMemoSaveState.value = 'saving';
  try {
    await bankbookApi.saveMonthMemo(memberCode.value, {
      year: String(currentYear.value),
      month: String(currentMonth.value),
      memo: text,
    });
    monthMemoSaveState.value = 'saved';
    if (monthMemoStatusTimer) clearTimeout(monthMemoStatusTimer);
    monthMemoStatusTimer = setTimeout(() => {
      if (monthMemoSaveState.value === 'saved') monthMemoSaveState.value = 'idle';
    }, 1800);
  } catch (error) {
    console.error('이달의 메모 저장 오류:', error);
    monthMemoSaveState.value = 'error';
  }
};

const onMonthMemoInput = () => {
  if (monthMemoText.value.length > MONTH_MEMO_MAX) {
    monthMemoText.value = monthMemoText.value.slice(0, MONTH_MEMO_MAX);
  }
  monthMemoSaveState.value = 'idle';
  if (monthMemoSaveTimer) clearTimeout(monthMemoSaveTimer);
  monthMemoSaveTimer = setTimeout(() => {
    saveMonthMemoNow();
  }, 800);
};

// ============================================
// 메서드
// ============================================

// 숫자 포맷
const formatNumber = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '0';
  return value.toLocaleString('ko-KR');
};


// 날짜 포맷
const formatDate = (value: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

// 계정명 가져오기
const getScodeDisplayName = (scode: string | undefined, gubun: string | undefined): string => {
  if (!scode) return '';
  const list = gubun === 'IN' ? incomeCodeList.value : expenseCodeList.value;
  const found = list.find(ac => ac.scode === scode);
  return found ? found.sname : scode;
};

// 상대계정이 없는 이유 반환
const getRelAccMissingReason = (item: CashbookData): string => {
  // 1. 지출이 아닌 경우
  if (item.cb_gubun !== 'OUT') {
    return '수입 항목은 상대계정이 필요하지 않습니다.';
  }
  
  // 2. 계정(cb_scode)이 지정되지 않은 경우
  if (!item.cb_scode) {
    return '지출 계정이 지정되지 않아 상대계정을 자동 추론할 수 없습니다.\n먼저 계정을 지정해주세요.';
  }
  
  // 3. 수입 데이터가 없는 경우 (이월금도 없는 경우)
  const incomeItems = cashbookList.value.filter(d => d.cb_gubun === 'IN' && d.cb_scode);
  const carryover = Number(prevMonthBalance.value) || 0;
  
  if (incomeItems.length === 0 && carryover <= 0) {
    return '수입 내역에 계정이 지정된 항목이 없고, 이월금도 없어 상대계정을 추론할 수 없습니다.\n수입 항목의 계정을 먼저 지정해주세요.';
  }
  
  // 4. 잔액 계산하여 구체적인 이유 제공
  const targetDate = item.cb_date;
  const targetIdx = item.cb_idx;
  const targetPrice = Math.abs(Number(item.cb_price) || 0);
  
  // 수입 계정별 잔액 계산
  const accountBalances = new Map<string, { totalIncome: number; usedExpense: number; sname: string }>();
  let totalIncome = 0;
  
  for (const incItem of cashbookList.value) {
    if (incItem.cb_gubun !== 'IN' || !incItem.cb_scode) continue;
    
    const isBeforeOrSame = incItem.cb_date < targetDate || 
      (incItem.cb_date === targetDate && (incItem.cb_idx || 0) <= (targetIdx || 0));
    if (!isBeforeOrSame) continue;
    
    const scode = incItem.cb_scode;
    const price = Number(incItem.cb_price) || 0;
    
    if (!accountBalances.has(scode)) {
      const sname = getScodeDisplayName(scode, 'IN') || scode;
      accountBalances.set(scode, { totalIncome: 0, usedExpense: 0, sname });
    }
    accountBalances.get(scode)!.totalIncome += price;
    totalIncome += price;
  }
  
  // 이월금 처리
  if (carryover > 0) {
    if (totalIncome > 0 && accountBalances.size > 0) {
      // 현재 월 수입이 있으면 비율에 따라 분배
      for (const [, balance] of accountBalances.entries()) {
        const ratio = balance.totalIncome / totalIncome;
        balance.totalIncome += carryover * ratio;
      }
    } else if (accountBalances.size > 0) {
      // 현재 월 수입이 없지만 계정이 있는 경우
      const firstAccount = accountBalances.entries().next().value;
      if (firstAccount) {
        firstAccount[1].totalIncome += carryover;
      }
    } else {
      // 현재 월에 수입 계정이 없는 경우, 기본 계정 사용
      const defaultIncomeCodes = ['111', '101', '102', '103'];
      let defaultCode = incomeCodeList.value.length > 0 ? incomeCodeList.value[0].scode : '111';
      
      for (const code of defaultIncomeCodes) {
        const found = incomeCodeList.value.find(c => c.scode === code);
        if (found) {
          defaultCode = found.scode;
          break;
        }
      }
      
      const sname = getScodeDisplayName(defaultCode, 'IN') || defaultCode;
      accountBalances.set(defaultCode, { totalIncome: carryover, usedExpense: 0, sname });
    }
  }
  
  for (const expItem of cashbookList.value) {
    if (expItem.cb_gubun !== 'OUT' || !expItem.cb_rel_acc) continue;
    if (expItem.cb_idx === targetIdx) continue;
    
    const isBefore = expItem.cb_date < targetDate || 
      (expItem.cb_date === targetDate && (expItem.cb_idx || 0) < (targetIdx || 0));
    if (!isBefore) continue;
    
    const relAcc = expItem.cb_rel_acc;
    if (accountBalances.has(relAcc)) {
      accountBalances.get(relAcc)!.usedExpense += Math.abs(Number(expItem.cb_price) || 0);
    }
  }
  
  // 잔액이 있는 계정이 있는지 확인
  let hasAvailableBalance = false;
  let maxRemaining = 0;
  let maxAccountName = '';
  
  for (const [, balance] of accountBalances.entries()) {
    const remaining = balance.totalIncome - balance.usedExpense;
    if (remaining > 0) {
      hasAvailableBalance = true;
      if (remaining > maxRemaining) {
        maxRemaining = remaining;
        maxAccountName = balance.sname;
      }
    }
  }
  
  if (!hasAvailableBalance) {
    return `이 날짜(${item.cb_date}) 기준 모든 수입 계정의 잔액(이월금 포함)이 이미 소진되었습니다.\n수입 내역을 확인하거나 상대계정을 직접 선택해주세요.`;
  }
  
  if (maxRemaining < targetPrice) {
    return `지출 금액(${formatNumber(targetPrice)}원)을 커버할 수 있는 수입 계정이 없습니다.\n가장 큰 잔액: ${maxAccountName} (${formatNumber(maxRemaining)}원, 이월금 포함)\n상대계정을 직접 선택해주세요.`;
  }
  
  // 5. 그 외의 경우 - 잔액은 충분하지만 자동 추론이 안 된 경우
  return `잔액이 충분한 수입 계정(${maxAccountName}: ${formatNumber(maxRemaining)}원, 이월금 포함)이 있습니다.\n계정을 다시 선택하거나, 상대계정을 직접 지정해주세요.`;
};

// 자금원천명 가져오기
const getFundingName = (code: string | undefined): string => {
  if (!code) return '';
  const found = fundingCodeList.find(f => f.code === code);
  return found ? found.name : code;
};

// 확인필요 이유 가져오기
const getReviewReason = (item: CashbookData): string | null => {
  if (!item.cb_need_check) return null;
  try {
    const info = JSON.parse(item.cb_need_check);
    return info.reviewReason || null;
  } catch {
    return null;
  }
};

// 정렬 토글
const toggleSort = (key: string) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

// 정렬 아이콘
const getSortIcon = (key: string): string => {
  if (sortKey.value !== key) return 'mdi-swap-vertical';
  return sortOrder.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
};

// 월 변경
const changeMonth = (delta: number) => {
  memberYearMonth.changeMonth(delta);
};

// 월 선택기 열기
const openMonthPicker = () => {
  monthPickerRef.value?.showPicker();
};

// 월 선택기 변경
const onMonthPickerChange = (e: Event) => {
  memberYearMonth.onMonthPickerChange(e);
};

// 필터 초기화
const resetFilters = () => {
  filterNeedCheck.value = false;
  filterReceiptMatch.value = 'all';
  filterSplitOnly.value = false;
  ledgerDisplayMode.value = 'cashbook';
  expandedSplitParents.value = {};
  searchScode.value = '';
  searchMemo.value = '';
  filterDayStart.value = null;
  filterDayEnd.value = null;
};

// 보기 모드 토글
const setViewMode = (mode: 'normal' | 'grouped') => {
  viewMode.value = mode;
  if (mode === 'grouped') {
    expandedScodes.value = {};
  }
};

const loadAdjustState = async () => {
  if (!memberCode.value) return;
  isAdjustStateLoading.value = true;
  try {
    const res = await bankbookApi.getAccountAdjustState(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      bizGubun: selectedBizGubun.value || undefined,
      bankCode: selectedBankCode.value || undefined,
    });
    const data = res.data?.data;
    adjustIncomes.value = (data?.incomes || []) as IncomeBalanceItem[];
    adjustExpenseCodes.value = (data?.expenseCodes || []).map((c: { scode: string; sname: string }) => ({
      scode: c.scode,
      sname: c.sname,
    }));
  } catch (error) {
    console.error('계정조정 상태 조회 오류:', error);
  } finally {
    isAdjustStateLoading.value = false;
  }
};

const onIncomeBalanceSelect = (scode: string | null) => {
  selectedIncomeScode.value = selectedIncomeScode.value === scode ? null : scode;
};

const onExpensePickerSelect = (payload: { scode: string; relAcc: string }) => {
  // 잔액이 없거나 부족한 수입 계정을 고르면 한 번 더 확인 (차단은 하지 않음)
  const income = adjustIncomes.value.find((inc) => inc.scode === payload.relAcc);
  if (income) {
    const remaining = Number(income.remaining) || 0;
    const amount = Math.abs(Number(inlineEditData.value.cb_price) || 0);
    let warnMsg = '';
    if (remaining <= 0) {
      warnMsg = `'${income.sname}' 계정의 잔액이 없습니다.\n지정하면 해당 계정 잔액이 마이너스로 표시됩니다.\n그래도 지정하시겠습니까?`;
    } else if (amount > remaining) {
      warnMsg = `지출 금액(${formatNumber(amount)}원)이 '${income.sname}' 계정의 잔액(${formatNumber(remaining)}원)을 초과합니다.\n그래도 지정하시겠습니까?`;
    }
    if (warnMsg && !window.confirm(warnMsg)) return;
  }
  inlineEditData.value.cb_scode = payload.scode;
  inlineEditData.value.cb_rel_acc = payload.relAcc;
};

/** 계정 지정 성공 건만 과거 적요→계정으로 학습 (추천용). 실패해도 본 저장은 유지. */
const learnMemoAccountsFromSaved = async (
  savedItems: CashbookData[],
  scode: string,
  gubun: string
) => {
  if (!memberCode.value || !scode || !savedItems.length) return;
  const learnItems = buildBankbookLearnItems({
    mCode: memberCode.value,
    bizGubun: selectedBizGubun.value || null,
    items: savedItems.map((item) => ({
      memo: item.cb_memo,
      gubun,
      scode,
    })),
  });
  if (!learnItems.length) return;
  try {
    await bankbookApi.learnBatch(learnItems);
  } catch (error) {
    console.error('적요→계정 학습 누적 오류:', error);
  }
};

/** 계정·상대계정을 넣을 때는 자금원천을 건드리지 않는다. 자금원천 버튼을 눌렀을 때만 넣는다. */
const explicitFunding = (picked?: string | null) => (picked ? String(picked).trim() : '');

const onWorkbenchAssignBatch = async (payload: {
  items: CashbookData[];
  expenseScode: string;
  incomeScode: string;
  funding?: string;
}) => {
  const { items, expenseScode, incomeScode, funding: pickedFunding } = payload;
  const targets = items.filter((item) => item.cb_idx && isItemEditable(item));
  if (!targets.length) return;
  if (!(await ensureBankbookAdjustable(targets))) return;
  try {
    const funding = explicitFunding(pickedFunding);
    const jobs = targets.map((item) => ({
      item,
      funding,
      promise: bankbookApi.update(item.cb_idx as number, {
        cb_scode: expenseScode,
        cb_rel_acc: incomeScode,
        ...(funding ? { cb_funding: funding } : {}),
      }),
    }));
    const results = await Promise.allSettled(jobs.map((job) => job.promise));
    const savedItems: CashbookData[] = [];
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const { item, funding } = jobs[index];
      savedItems.push(item);
      const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
      if (idx === -1) return;
      cashbookList.value[idx].cb_scode = expenseScode;
      cashbookList.value[idx].cb_rel_acc = incomeScode;
      if (funding) cashbookList.value[idx].cb_funding = funding;
    });
    await learnMemoAccountsFromSaved(savedItems, expenseScode, 'OUT');
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed) {
      alert(getWorkbenchSaveErrorMessage(results) || `${failed}건 저장에 실패했습니다. 나머지 항목은 반영되었습니다.`);
    } else {
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 1600);
    }
    await loadAdjustState();
  } catch (error) {
    console.error(error);
    alert('계정지정 저장에 실패했습니다.');
  }
};

/** 지출 계정만 저장 (상대계정 유지) */
const onWorkbenchAssignExpenseAccount = async (payload: {
  items: CashbookData[];
  expenseScode: string;
  funding?: string;
}) => {
  const { items, expenseScode, funding: pickedFunding } = payload;
  const targets = items.filter((item) => item.cb_idx && isItemEditable(item));
  if (!targets.length) return;
  if (!(await ensureBankbookAdjustable(targets))) return;
  try {
    const funding = explicitFunding(pickedFunding);
    const jobs = targets.map((item) => ({
      item,
      funding,
      promise: bankbookApi.update(item.cb_idx as number, {
        cb_scode: expenseScode,
        ...(funding ? { cb_funding: funding } : {}),
      }),
    }));
    const results = await Promise.allSettled(jobs.map((job) => job.promise));
    const savedItems: CashbookData[] = [];
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const { item, funding } = jobs[index];
      savedItems.push(item);
      const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
      if (idx === -1) return;
      cashbookList.value[idx].cb_scode = expenseScode;
      if (funding) cashbookList.value[idx].cb_funding = funding;
    });
    await learnMemoAccountsFromSaved(savedItems, expenseScode, 'OUT');
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed) {
      alert(getWorkbenchSaveErrorMessage(results) || `${failed}건 저장에 실패했습니다. 나머지 항목은 반영되었습니다.`);
    } else {
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 1600);
    }
    await loadAdjustState();
  } catch (error) {
    console.error(error);
    alert('지출 계정 저장에 실패했습니다.');
  }
};

/** 상대계정만 저장 (지출계정 유지, 자금원천은 상대계정 기준) */
const onWorkbenchAssignRel = async (payload: {
  items: CashbookData[];
  incomeScode: string;
  funding?: string;
}) => {
  const { items, incomeScode, funding: pickedFunding } = payload;
  const targets = items.filter((item) => item.cb_idx && isItemEditable(item));
  if (!targets.length) return;
  if (!(await ensureBankbookAdjustable(targets))) return;
  try {
    const funding = explicitFunding(pickedFunding);
    const jobs = targets.map((item) => ({
      item,
      funding,
      promise: bankbookApi.update(item.cb_idx as number, {
        cb_rel_acc: incomeScode,
        ...(funding ? { cb_funding: funding } : {}),
      }),
    }));
    const results = await Promise.allSettled(jobs.map((job) => job.promise));
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const { item, funding } = jobs[index];
      const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
      if (idx === -1) return;
      cashbookList.value[idx].cb_rel_acc = incomeScode;
      if (funding) cashbookList.value[idx].cb_funding = funding;
    });
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed) {
      alert(getWorkbenchSaveErrorMessage(results) || `${failed}건 저장에 실패했습니다. 나머지 항목은 반영되었습니다.`);
    } else {
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 1600);
    }
    await loadAdjustState();
  } catch (error) {
    console.error(error);
    alert('상대계정 저장에 실패했습니다.');
  }
};

const onWorkbenchAssignIncome = async (payload: {
  items: CashbookData[];
  incomeScode: string;
  funding?: string;
}) => {
  const { items, incomeScode, funding: pickedFunding } = payload;
  const targets = items.filter((item) => item.cb_idx && isItemEditable(item));
  if (!targets.length) return;
  if (!(await ensureBankbookAdjustable(targets))) return;
  try {
    const funding = explicitFunding(pickedFunding);
    const jobs = targets.map((item) => ({
      item,
      funding,
      promise: bankbookApi.update(item.cb_idx as number, {
        cb_scode: incomeScode,
        ...(funding ? { cb_funding: funding } : {}),
      }),
    }));
    const results = await Promise.allSettled(jobs.map((job) => job.promise));
    const savedItems: CashbookData[] = [];
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const { item, funding } = jobs[index];
      savedItems.push(item);
      const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
      if (idx === -1) return;
      cashbookList.value[idx].cb_scode = incomeScode;
      if (funding) cashbookList.value[idx].cb_funding = funding;
    });
    await learnMemoAccountsFromSaved(savedItems, incomeScode, 'IN');
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed) {
      alert(getWorkbenchSaveErrorMessage(results) || `${failed}건 저장에 실패했습니다. 나머지 항목은 반영되었습니다.`);
    } else {
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 1600);
    }
    await loadAdjustState();
  } catch (error) {
    console.error(error);
    alert('수입 계정 저장에 실패했습니다.');
  }
};

/** 자금원천만 저장 (조정하기에서 자금원천만 골랐을 때) */
const onWorkbenchAssignFunding = async (payload: {
  items: CashbookData[];
  funding: string;
}) => {
  const { items, funding } = payload;
  const targets = items.filter((item) => item.cb_idx && isItemEditable(item));
  if (!targets.length || !funding) return;
  if (!(await ensureBankbookAdjustable(targets))) return;
  await applyFundingToItems(targets, funding);
};

const onWorkbenchUnassignIncome = async (item: CashbookData) => {
  if (!item.cb_idx || !isItemEditable(item)) return;
  if (!(await ensureBankbookAdjustable([item]))) return;
  try {
    await bankbookApi.update(item.cb_idx, { cb_scode: '' });
    const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
    if (idx !== -1) cashbookList.value[idx].cb_scode = '';
    await loadAdjustState();
  } catch (error) {
    console.error(error);
    alert('되돌리기에 실패했습니다.');
  }
};

const onWorkbenchUnassign = async (item: CashbookData) => {
  if (!item.cb_idx || !isItemEditable(item)) return;
  if (!(await ensureBankbookAdjustable([item]))) return;
  try {
    await bankbookApi.update(item.cb_idx, { cb_scode: '', cb_rel_acc: '' });
    const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
    if (idx !== -1) {
      cashbookList.value[idx].cb_scode = '';
      cashbookList.value[idx].cb_rel_acc = '';
    }
    await loadAdjustState();
  } catch (error) {
    console.error(error);
    alert('되돌리기에 실패했습니다.');
  }
};

const runPairAutoAdjust = async () => {
  await openAutoDialog('account');
};

const beneficiaryAutoCandidates = computed(() =>
  cashbookList.value.filter(
    (item) =>
      item.cb_scode === '112' &&
      item.cb_idx &&
      item.cb_memo &&
      String(item.cb_memo).trim() &&
      !item.cb_matched_beneficiary_idx &&
      !beneficiaryMatches.value[item.cb_idx] &&
      isItemEditable(item)
  )
);

const autoDialogActions = computed<BankbookAutoActionState[]>(() => [
  {
    key: 'account',
    title: '계정 자동 넣기',
    desc: '비어 있는 계정을, 기억된 적요와 같은 계정으로 채웁니다.',
    emptyHint: autoAccountDisabled.value
      ? ''
      : '기억된 적요와 맞는 빈 계정이 없어요.',
    icon: 'mdi-book-check-outline',
    count: autoAccountReadyCount.value,
    countLoading: autoAccountCountLoading.value,
    busy: isAutoAccountProcessing.value,
    applyDisabled: autoAccountDisabled.value,
    applyDisabledReason: autoAccountDisabled.value ? autoAccountDisabledReason.value : undefined,
  },
  {
    key: 'rel',
    title: '상대계정 자동 넣기',
    desc: '계정은 있고 상대계정만 비어 있는 지출을, 잔액이 되는 수입으로 채웁니다.',
    emptyHint: '잔액으로 채울 수 있는 빈 상대계정이 없어요.',
    icon: 'mdi-link-variant',
    count: autoRelReadyCount.value,
    countLoading: autoRelCountLoading.value,
    busy: isAutoRelAccProcessing.value,
  },
  {
    key: 'funding',
    title: '자금원천 자동 넣기',
    desc: '비어 있는 자금원천을 규칙대로 채웁니다.',
    emptyHint: '규칙으로 채울 수 있는 빈 자금원천이 없어요.',
    icon: 'mdi-cash-multiple',
    count: fundingInferableCount.value,
    busy: isAutoFundingProcessing.value,
    applyDisabled: !fundingInferSettings.value.enabled,
    applyDisabledReason: !fundingInferSettings.value.enabled
      ? '자금원천 규칙이 꺼져 있어요. 통장내역 탭 → 자금원천 → 톱니에서 켜 주세요.'
      : undefined,
  },
  {
    key: 'beneficiary',
    title: '본인부담금 자동매칭',
    desc: '적요의 수급자 이름으로, 아직 연결되지 않은 입금만 연결합니다.',
    emptyHint: '적요에 수급자 이름이 보이는 미연결 입금이 없어요.',
    icon: 'mdi-account-multiple-check',
    count: beneficiaryAutoCandidates.value.length,
    busy: isAutoBeneficiaryMatching.value,
  },
]);

const clearAutoPreview = () => {
  autoPreviewKind.value = null;
  autoPreviewRows.value = [];
  autoPreviewLoading.value = false;
  autoPreviewError.value = '';
  autoFillPreviewPatches.value = [];
  autoBenPreviewMatches.value = {};
};

const autoFillQuery = () => ({
  year: String(currentYear.value),
  month: String(currentMonth.value),
  bizGubun: selectedBizGubun.value || undefined,
  bankCode: selectedBankCode.value || undefined,
});

type AutoFillUpdate = {
  cbIdx: number;
  date?: string;
  memo?: string;
  price?: number;
  afterName?: string;
  keyword?: string;
  balance?: number;
  patch: Record<string, string>;
};

const buildFillPreviewRows = (
  updates: AutoFillUpdate[],
  hintOf: (row: AutoFillUpdate) => string
): BankbookAutoPreviewRow[] =>
  updates.map((row) => ({
    id: row.cbIdx,
    date: formatDate(row.date || null),
    memo: String(row.memo || ''),
    priceLabel: `${formatNumber(row.price || 0)}원`,
    before: '비어 있음',
    after: row.afterName || '계정',
    hint: hintOf(row),
  }));

const refreshAutoAccountCount = async () => {
  if (!memberCode.value || closingStatus.value?.isClosed) {
    autoAccountReadyCount.value = 0;
    autoAccountDisabled.value = false;
    autoAccountDisabledReason.value = '';
    return;
  }
  autoAccountCountLoading.value = true;
  try {
    const res = await bankbookApi.autoFillAccounts(memberCode.value, {
      ...autoFillQuery(),
      dryRun: true,
    });
    const data = res.data?.data || {};
    autoAccountReadyCount.value = Number(data.updatedCount) || 0;
    autoAccountDisabled.value = !!data.disabled;
    autoAccountDisabledReason.value = String(data.disabledReason || '');
  } catch (error) {
    console.error('계정 자동화 건수 조회 오류:', error);
    autoAccountReadyCount.value = 0;
  } finally {
    autoAccountCountLoading.value = false;
  }
};

const refreshAutoRelCount = async () => {
  if (!memberCode.value || closingStatus.value?.isClosed) {
    autoRelReadyCount.value = 0;
    return;
  }
  autoRelCountLoading.value = true;
  try {
    const res = await bankbookApi.autoFillRelAccounts(memberCode.value, {
      ...autoFillQuery(),
      dryRun: true,
    });
    autoRelReadyCount.value = Number(res.data?.data?.updatedCount) || 0;
  } catch (error) {
    console.error('상대계정 자동화 건수 조회 오류:', error);
    autoRelReadyCount.value = 0;
  } finally {
    autoRelCountLoading.value = false;
  }
};

const openAutoDialog = async (focus?: BankbookAutoKind) => {
  autoDialogOpen.value = true;
  autoPendingFocus.value = focus || null;
  clearAutoPreview();
  await Promise.all([refreshAutoAccountCount(), refreshAutoRelCount()]);
  if (focus) {
    await previewAutoKind(focus);
  }
};

const previewAutoKind = async (kind: BankbookAutoKind) => {
  if (!memberCode.value || closingStatus.value?.isClosed) return;
  autoPreviewKind.value = kind;
  autoPreviewRows.value = [];
  autoPreviewLoading.value = true;
  autoPreviewError.value = '';
  autoFillPreviewPatches.value = [];
  autoBenPreviewMatches.value = {};

  try {
    if (kind === 'account' || kind === 'rel') {
      const res = kind === 'account'
        ? await bankbookApi.autoFillAccounts(memberCode.value, { ...autoFillQuery(), dryRun: true })
        : await bankbookApi.autoFillRelAccounts(memberCode.value, { ...autoFillQuery(), dryRun: true });
      const data = res.data?.data || {};
      const updates = (data.updates || []) as AutoFillUpdate[];
      if (kind === 'account') {
        autoAccountReadyCount.value = updates.length;
        autoAccountDisabled.value = !!data.disabled;
        autoAccountDisabledReason.value = String(data.disabledReason || '');
      } else {
        autoRelReadyCount.value = updates.length;
      }
      autoFillPreviewPatches.value = updates.map((row) => ({ cbIdx: row.cbIdx, patch: row.patch }));
      autoPreviewRows.value = buildFillPreviewRows(updates, (row) => {
        if (kind === 'account' && row.keyword) return `기억된 적요 「${row.keyword}」`;
        if (kind === 'rel' && row.balance != null) return `잔액 ${formatNumber(row.balance)}원`;
        return '';
      });
    } else if (kind === 'funding') {
      const rows = fundingWorkList.value.filter((row) => row.suggested && isItemEditable(row.item));
      autoPreviewRows.value = rows.map((row) => ({
        id: row.item.cb_idx,
        date: formatDate(row.item.cb_date || null),
        memo: String(row.item.cb_memo || ''),
        priceLabel: `${formatNumber(row.item.cb_price || 0)}원`,
        before: '비어 있음',
        after: getFundingName(row.suggested as string),
      }));
    } else if (kind === 'beneficiary') {
      const candidates = beneficiaryAutoCandidates.value;
      if (!candidates.length) {
        autoPreviewRows.value = [];
        return;
      }
      const payload = candidates.map((item) => ({
        idx: item.cb_idx!,
        memo: item.cb_memo || '',
        price: item.cb_price || 0,
      }));
      const res = await beneficiaryApi.matchFromMemo(memberCode.value, payload);
      const memoMatches = res.data?.success ? (res.data.data || {}) : {};
      autoBenPreviewMatches.value = memoMatches;
      autoPreviewRows.value = Object.entries(memoMatches).map(([cbIdxStr, match]) => {
        const item = cashbookList.value.find((row) => String(row.cb_idx) === cbIdxStr);
        return {
          id: cbIdxStr,
          date: formatDate(item?.cb_date || null),
          memo: String(item?.cb_memo || ''),
          priceLabel: `${formatNumber(item?.cb_price || 0)}원`,
          before: '미연결',
          after: (match as { cb_name?: string })?.cb_name || '수급자',
        };
      });
    }
  } catch (error) {
    console.error('자동화 미리보기 오류:', error);
    autoPreviewRows.value = [];
    autoPreviewError.value = '미리보기를 불러오지 못했습니다. 잠시 후 다시 눌러 주세요.';
  } finally {
    autoPreviewLoading.value = false;
  }
};

const applyPreviewPatches = async (label: string) => {
  const patches = autoFillPreviewPatches.value;
  if (!patches.length) {
    alert('적용할 항목이 없습니다. 미리보기를 먼저 확인해 주세요.');
    return;
  }
  const results = await Promise.allSettled(
    patches.map((row) => bankbookApi.update(row.cbIdx, row.patch))
  );
  const ok = results.filter((result) => result.status === 'fulfilled').length;
  await loadCashbookData();
  alert(ok > 0 ? `${label} ${ok}건을 채웠습니다. 틀린 항목은 해당 행에서 고치면 됩니다.` : '적용할 항목이 없습니다.');
};

const applyAutoKind = async (kind: BankbookAutoKind) => {
  if (!memberCode.value || closingStatus.value?.isClosed) return;
  if (autoPreviewKind.value !== kind) return;
  autoApplying.value = true;
  try {
    if (kind === 'account') {
      isAutoAccountProcessing.value = true;
      try {
        await applyPreviewPatches('계정');
        await refreshAutoAccountCount();
      } catch (error) {
        console.error('계정 자동 넣기 오류:', error);
        alert('계정 자동 넣기 중 오류가 발생했습니다.');
      } finally {
        isAutoAccountProcessing.value = false;
      }
    } else if (kind === 'rel') {
      isAutoRelAccProcessing.value = true;
      try {
        await applyPreviewPatches('상대계정');
        await refreshAutoRelCount();
      } catch (error) {
        console.error('상대계정 자동 넣기 오류:', error);
        alert('상대계정 자동 넣기 중 오류가 발생했습니다.');
      } finally {
        isAutoRelAccProcessing.value = false;
      }
    } else if (kind === 'funding') {
      await autoAssignFunding({ skipConfirm: true });
    } else if (kind === 'beneficiary') {
      await runBeneficiaryAutoMatchFromMemo({
        skipConfirm: true,
        previewMatches:
          Object.keys(autoBenPreviewMatches.value).length
            ? autoBenPreviewMatches.value
            : undefined,
      });
    }
    clearAutoPreview();
    if (kind === 'funding' || kind === 'beneficiary') {
      await Promise.all([refreshAutoAccountCount(), refreshAutoRelCount()]);
    }
  } finally {
    autoApplying.value = false;
  }
};

// 계정 그룹 펼치기/접기 토글
const toggleAccountGroup = (scode: string) => {
  expandedScodes.value[scode] = !expandedScodes.value[scode];
};

// 모든 계정 그룹 펼치기
const expandAllGroups = () => {
  for (const group of groupedByAccount.value) {
    expandedScodes.value[group.scode] = true;
  }
};

// 모든 계정 그룹 접기
const collapseAllGroups = () => {
  expandedScodes.value = {};
};

// 검색 초기화
const clearAllSearch = () => {
  searchScode.value = '';
  searchMemo.value = '';
  filterDayStart.value = null;
  filterDayEnd.value = null;
  filterSplitOnly.value = false;
  ledgerDisplayMode.value = 'cashbook';
  expandedSplitParents.value = {};
};

// 계정코드에 박혀 있는 자금원천 (caion_fn_code.cfc_funding) + 기관 오버라이드
const getAccountFunding = (scode: string | undefined, gubun: 'IN' | 'OUT'): string | null => {
  if (!scode) return null;
  if (gubun === 'IN') {
    const override = fundingInferSettings.value.incomeFunding[scode];
    if (override) return override;
  }
  const list = gubun === 'IN' ? incomeCodeList.value : expenseCodeList.value;
  const found = list.find((c) => c.scode === scode);
  const funding = found?.cfc_funding ? String(found.cfc_funding).trim() : '';
  return funding || null;
};

// 수입: 해당 계정(오버라이드 또는 코드표)의 자금원천
// 지출: 설정한 계정명 예외 → (옵션) 상대계정 자금원천 → 지출계정 기본
const inferFundingForItem = (item: CashbookData): string | null => {
  if (!fundingInferSettings.value.enabled) return null;
  if (item.cb_gubun === 'IN') {
    return getAccountFunding(item.cb_scode, 'IN');
  }
  const scodeName = item.cb_scode ? getScodeNameByCode(item.cb_scode, 'OUT') : null;
  if (scodeName) {
    const rule = fundingInferSettings.value.expenseNameRules.find(
      (r) => r.name.trim() === scodeName.trim()
    );
    if (rule?.funding) return rule.funding;
  }
  if (fundingInferSettings.value.useRelAccountFunding && item.cb_rel_acc) {
    return getAccountFunding(item.cb_rel_acc, 'IN');
  }
  return getAccountFunding(item.cb_scode, 'OUT');
};

const filteredFundingIncomeCodes = computed(() => {
  const q = fundingIncomeSearch.value.trim().toLowerCase();
  const list = incomeCodeList.value;
  if (!q) return list;
  return list.filter(
    (c) =>
      String(c.sname || '').toLowerCase().includes(q) ||
      String(c.scode || '').toLowerCase().includes(q)
  );
});

const effectiveIncomeFunding = (code: { scode: string; cfc_funding?: string }) => {
  const override = fundingInferSettings.value.incomeFunding[code.scode];
  if (override) return override;
  const base = code.cfc_funding ? String(code.cfc_funding).trim() : '';
  return base || '';
};

const applyFundingInferSettings = (setting: FundingInferSettings) => {
  fundingInferSettings.value = {
    enabled: setting.enabled !== false,
    useRelAccountFunding: setting.useRelAccountFunding !== false,
    expenseNameRules: (setting.expenseNameRules?.length
      ? setting.expenseNameRules
      : defaultFundingInferSettings().expenseNameRules
    ).map((r) => ({ name: r.name, funding: r.funding })),
    incomeFunding: { ...(setting.incomeFunding || {}) },
  };
};

const loadFundingInferSettings = async () => {
  if (!memberCode.value) {
    applyFundingInferSettings(defaultFundingInferSettings());
    return;
  }
  try {
    const res = await bankbookApi.getFundingInferSetting(memberCode.value);
    if (res.data?.success && res.data.data) {
      applyFundingInferSettings(res.data.data as FundingInferSettings);
    } else {
      applyFundingInferSettings(defaultFundingInferSettings());
    }
  } catch (error) {
    console.error('자금원천 규칙 조회 오류:', error);
    applyFundingInferSettings(defaultFundingInferSettings());
  }
};

const persistFundingInferSettings = () => {
  if (fundingSettingSaveTimer) clearTimeout(fundingSettingSaveTimer);
  fundingSettingSaveTimer = setTimeout(async () => {
    if (!memberCode.value) return;
    fundingSettingSaveState.value = 'saving';
    try {
      const res = await bankbookApi.saveFundingInferSetting(memberCode.value, {
        enabled: fundingInferSettings.value.enabled,
        useRelAccountFunding: fundingInferSettings.value.useRelAccountFunding,
        expenseNameRules: fundingInferSettings.value.expenseNameRules
          .map((r) => ({ name: r.name.trim(), funding: r.funding }))
          .filter((r) => r.name && r.funding),
        incomeFunding: { ...fundingInferSettings.value.incomeFunding },
      });
      if (res.data?.success && res.data.data) {
        applyFundingInferSettings(res.data.data as FundingInferSettings);
      }
      fundingSettingSaveState.value = 'saved';
      if (fundingSettingSavedTimer) clearTimeout(fundingSettingSavedTimer);
      fundingSettingSavedTimer = setTimeout(() => {
        fundingSettingSaveState.value = 'idle';
      }, 1600);
    } catch (error) {
      console.error('자금원천 규칙 저장 오류:', error);
      fundingSettingSaveState.value = 'error';
    }
  }, 280);
};

const onIncomeFundingPick = (scode: string, event: Event) => {
  setIncomeFundingOverride(scode, (event.target as HTMLSelectElement).value);
};

const onExpenseFundingPick = (idx: number, event: Event) => {
  setExpenseRuleFunding(idx, (event.target as HTMLSelectElement).value);
};

const setIncomeFundingOverride = (scode: string, funding: string) => {
  const next = { ...fundingInferSettings.value.incomeFunding };
  if (!funding) {
    delete next[scode];
  } else {
    next[scode] = funding;
  }
  fundingInferSettings.value.incomeFunding = next;
  persistFundingInferSettings();
};

const setExpenseRuleFunding = (idx: number, funding: string) => {
  const rules = fundingInferSettings.value.expenseNameRules.map((r, i) =>
    i === idx ? { ...r, funding } : r
  );
  fundingInferSettings.value.expenseNameRules = rules;
  persistFundingInferSettings();
};

const addExpenseNameRule = () => {
  fundingInferSettings.value.expenseNameRules = [
    ...fundingInferSettings.value.expenseNameRules,
    { name: '', funding: '04' },
  ];
};

const removeExpenseNameRule = (idx: number) => {
  if (fundingInferSettings.value.expenseNameRules.length <= 1) return;
  fundingInferSettings.value.expenseNameRules = fundingInferSettings.value.expenseNameRules.filter(
    (_, i) => i !== idx
  );
  persistFundingInferSettings();
};

const applyFundingToItems = async (items: CashbookData[], funding: string) => {
  const targets = items.filter((item) => item.cb_idx && isItemEditable(item) && item.cb_funding !== funding);
  if (!targets.length || !funding) return;
  isAutoFundingProcessing.value = true;
  try {
    const results = await Promise.allSettled(
      targets.map((item) => bankbookApi.update(item.cb_idx as number, { cb_funding: funding }))
    );
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const idx = cashbookList.value.findIndex((row) => row.cb_idx === targets[index].cb_idx);
      if (idx !== -1) cashbookList.value[idx].cb_funding = funding;
    });
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed) {
      alert(getWorkbenchSaveErrorMessage(results) || `${failed}건 저장에 실패했습니다. 나머지 항목은 반영되었습니다.`);
    } else {
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 1600);
    }
  } catch (error) {
    console.error('자금원천 저장 오류:', error);
    alert('자금원천 저장 중 오류가 발생했습니다.');
  } finally {
    isAutoFundingProcessing.value = false;
  }
};

const applyFundingToSelected = async (funding: string) => {
  const selectedIdSet = new Set(ledgerSelectedIds.value);
  const targets = fundingSelectedItems.value.filter(
    (item) => item.cb_idx != null && selectedIdSet.has(item.cb_idx)
  );
  if (!targets.length || !funding) {
    alert('바꿀 항목을 먼저 체크하세요.');
    return;
  }
  const name = fundingCodeList.find((row) => row.code === funding)?.name || '선택한 원천';
  const preview = targets
    .slice(0, 3)
    .map((item) => item.cb_memo || '적요 없음')
    .join(', ');
  const more = targets.length > 3 ? ` 외 ${targets.length - 3}건` : '';
  const ok = confirm(
    `체크한 ${targets.length}건의 자금원천을 「${name}」으로 바꿉니다.\n${preview}${more}`
  );
  if (!ok) return;
  await applyFundingToItems(targets, funding);
};

// 계정 기준으로 자금원천 일괄 부여 (자동화 패널에서 미리보기 후 적용)
const autoAssignFunding = async (opts?: { skipConfirm?: boolean }) => {
  const targets = fundingWorkList.value.filter((row) => row.suggested && isItemEditable(row.item));
  if (!targets.length) {
    alert('기준으로 넣을 자금원천 항목이 없습니다.');
    return;
  }
  if (!opts?.skipConfirm) {
    if (!confirm(`자금원천 ${targets.length}건을 규칙대로 채울까요?`)) return;
  }
  isAutoFundingProcessing.value = true;
  try {
    const results = await Promise.allSettled(
      targets.map((row) => bankbookApi.update(row.item.cb_idx as number, { cb_funding: row.suggested as string }))
    );
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      const item = targets[index].item;
      const idx = cashbookList.value.findIndex((row) => row.cb_idx === item.cb_idx);
      if (idx !== -1) cashbookList.value[idx].cb_funding = targets[index].suggested as string;
    });
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed) {
      alert(getWorkbenchSaveErrorMessage(results) || `${failed}건 저장에 실패했습니다. 나머지 항목은 반영되었습니다.`);
    } else {
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 1600);
      if (opts?.skipConfirm) {
        alert(`${targets.length - failed}건 자금원천을 넣었습니다.`);
      }
    }
  } catch (error) {
    console.error('자금원천 자동부여 오류:', error);
    alert('자금원천 자동부여 중 오류가 발생했습니다.');
  } finally {
    isAutoFundingProcessing.value = false;
  }
};

// 계정코드로 계정명 조회 (수입/지출 구분)
const getScodeNameByCode = (scode: string, gubun: 'IN' | 'OUT'): string | null => {
  const list = gubun === 'IN' ? incomeCodeList.value : expenseCodeList.value;
  const found = list.find(c => c.scode === scode);
  return found?.sname || null;
};

// 이월금 계산 (stepu 정책): 해당 연도 1월부터 (month-1)월까지의 (수입 - 지출) 누적
const calculateCarryover = async (year: number, month: number): Promise<number> => {
  if (month <= 1) return 0;
  if (!memberCode.value) return 0;
  
  // stepu 정책 누적 이월금(해당 연도 1월~전월 수입-지출)을 백엔드 단일 쿼리로 조회.
  // 기존 월별 반복 조회는 중간 요청 실패를 무시(catch)하여 잔액이 어긋나는 문제가 있었음.
  const res = await bankbookApi.getCumulativeCarryover(
    memberCode.value,
    String(year),
    String(month).padStart(2, '0'),
    selectedBizGubun.value || undefined,
    selectedBankCode.value || undefined
  );
  
  return Number(res.data?.data?.balance) || 0;
};

// 데이터 로드
const loadCashbookData = async () => {
  if (!memberCode.value) return;
  
  isLoading.value = true;
  try {
    // API 파라미터 구성 (항상 연월 기준으로 조회, 일자 필터는 프론트에서 처리)
    const params: any = {
      bizGubun: selectedBizGubun.value || undefined,
      year: currentYear.value,
      month: currentMonth.value
    };
    
    // 분개 개수 포함된 목록 조회 API 사용 (전체 데이터 조회, 통장 필터는 프론트에서 처리)
    const res = await bankbookApi.getListWithSplits(memberCode.value, params);
    // cb_price를 숫자로 변환
    const data = res.data?.data || [];
    cashbookList.value = data.map((item: CashbookData) => ({
      ...item,
      cb_price: Number(item.cb_price) || 0,
      split_count: Number(item.split_count) || 0,
      is_split_item: Number(item.is_split_item) || 0,
      parent_idx: item.parent_idx ?? item.cb_parent_idx ?? null,
      cb_parent_idx: item.cb_parent_idx ?? item.parent_idx ?? null,
      cb_receipt_matched: Number(item.cb_receipt_matched) === 1 ? 1 : 0,
    }));
    expandedSplitParents.value = {};
    
    // 이월금 계산 (stepu 정책)
    // - 1월: 이월금 = 0 (전년도 잔고는 "전년도이월금" 수입 계정으로 포함됨)
    // - 2월 이상: 해당 연도 1월부터 전월까지의 (수입 - 지출) 누적
    const currentMonthNum = parseInt(currentMonth.value);
    const currentYearNum = parseInt(currentYear.value);
    
    if (currentMonthNum === 1) {
      prevMonthBalance.value = 0;
    } else {
      // 이월금 조회 실패가 이미 로드된 목록을 지우지 않도록 개별 처리
      try {
        prevMonthBalance.value = await calculateCarryover(currentYearNum, currentMonthNum);
      } catch (carryoverError) {
        console.error('이월금 조회 오류:', carryoverError);
        prevMonthBalance.value = 0;
      }
    }
    
    // 본인부담금수입(112) 항목에 대해 수급자 매칭 수행
    await matchBeneficiaries();
    await loadAdjustState();
    await loadLaborBaseRatio();
  } catch (error) {
    console.error('통장내역 데이터 로드 오류:', error);
    cashbookList.value = [];
    prevMonthBalance.value = 0;
  } finally {
    isLoading.value = false;
  }
};

// 본인부담금수입: 저장된(수동/자동매칭 확정) 매칭만 복원 — 적요 자동매칭은 하지 않음
const matchBeneficiaries = async () => {
  if (!memberCode.value) return;
  
  const targetItems = cashbookList.value
    .filter(item => item.cb_scode === '112' && item.cb_idx)
    .map(item => ({
      idx: item.cb_idx!,
      matchedBeneficiaryIdx: item.cb_matched_beneficiary_idx
    }));
  
  if (targetItems.length === 0) {
    beneficiaryMatches.value = {};
    return;
  }
  
  try {
    const collectionRes = await collectionApi.getByMember(memberCode.value, {
      year: parseInt(currentYear.value),
      month: parseInt(currentMonth.value),
      bizGubun: selectedBizGubun.value || undefined
    });
    
    const collectionData = collectionRes.data?.success 
      ? (collectionRes.data.data?.list || collectionRes.data.data || [])
      : [];
    
    const beneficiaryNameMap = new Map<number, string>();
    collectionData.forEach((item: any) => {
      if (item.cb_idx && item.cb_name) {
        beneficiaryNameMap.set(item.cb_idx, item.cb_name);
      }
    });
    
    const finalMatches: Record<number, { cb_idx: number; cb_name: string; matchType: string }> = {};
    targetItems.forEach(item => {
      if (!item.matchedBeneficiaryIdx) return;
      finalMatches[item.idx] = {
        cb_idx: item.matchedBeneficiaryIdx,
        cb_name: beneficiaryNameMap.get(item.matchedBeneficiaryIdx) || '',
        matchType: 'manual'
      };
    });
    
    beneficiaryMatches.value = finalMatches;
    
    if (Object.keys(finalMatches).length > 0) {
      await syncDepositToCollection();
    }
  } catch (error) {
    console.error('수급자 매칭 복원 오류:', error);
    beneficiaryMatches.value = {};
  }
};

/** 본인부담금 자동매칭 (자동화 패널 미리보기 후 적용) */
const runBeneficiaryAutoMatchFromMemo = async (opts?: {
  skipConfirm?: boolean;
  previewMatches?: Record<string, { cb_idx: number; cb_name: string }>;
}) => {
  if (!memberCode.value || closingStatus.value?.isClosed) return;

  const candidates = beneficiaryAutoCandidates.value;

  if (candidates.length === 0) {
    alert('자동매칭할 본인부담금 항목이 없습니다.\n(이미 매칭됐거나 적요가 없는 항목은 제외됩니다)');
    return;
  }

  if (!opts?.skipConfirm) {
    if (!confirm(
      `적요에 수급자 이름이 포함된 본인부담금 ${candidates.length}건을 자동매칭할까요?\n이미 매칭된 항목은 그대로 둡니다.`
    )) {
      return;
    }
  }

  isAutoBeneficiaryMatching.value = true;
  try {
    let memoMatches = opts?.previewMatches;
    if (!memoMatches) {
      const payload = candidates.map((item) => ({
        idx: item.cb_idx!,
        memo: item.cb_memo || '',
        price: item.cb_price || 0,
      }));
      const res = await beneficiaryApi.matchFromMemo(memberCode.value, payload);
      memoMatches = res.data?.success ? (res.data.data || {}) : {};
    }

    const entries = Object.entries(memoMatches) as Array<[string, { cb_idx: number; cb_name: string }]>;

    if (entries.length === 0) {
      alert('적요에서 일치하는 수급자를 찾지 못했습니다.\n수급자가 등록돼 있고 적요에 이름이 포함돼 있는지 확인해 주세요.');
      return;
    }

    let saved = 0;
    for (const [cbIdxStr, match] of entries) {
      const cashbookIdx = parseInt(cbIdxStr, 10);
      if (!cashbookIdx || !match?.cb_idx) continue;

      await bankbookApi.update(cashbookIdx, {
        cb_matched_beneficiary_idx: match.cb_idx,
      });

      const itemIndex = cashbookList.value.findIndex((i) => i.cb_idx === cashbookIdx);
      if (itemIndex !== -1) {
        cashbookList.value[itemIndex].cb_matched_beneficiary_idx = match.cb_idx;
      }

      beneficiaryMatches.value[cashbookIdx] = {
        cb_idx: match.cb_idx,
        cb_name: match.cb_name,
        matchType: 'manual',
      };
      saved += 1;
    }

    if (saved > 0) {
      await syncDepositToCollection();
      beneficiaryModalDepositData.value = collectDepositData();
    }

    alert(`${saved}건을 수급자와 매칭했습니다.${candidates.length > saved ? `\n(대상 ${candidates.length}건 중 이름 미일치 ${candidates.length - saved}건)` : ''}`);
  } catch (error: any) {
    console.error('수급자 본인부담금 자동매칭 오류:', error);
    alert(`자동매칭에 실패했습니다: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
  } finally {
    isAutoBeneficiaryMatching.value = false;
  }
};

// 통장 본인부담금수입과 수납 데이터 동기화
const syncDepositToCollection = async () => {
  if (!memberCode.value) return;
  
  try {
    // 매칭된 수급자별로 입금 정보 수집 (날짜순 정렬 포함)
    const depositsMap = new Map<number, Array<{ amount: number; date: string; cashbookIdx: number }>>();
    
    // 본인부담금수입(112) 항목들을 날짜순으로 정렬
    const sortedItems = cashbookList.value
      .filter(item => item.cb_scode === '112' && item.cb_idx)
      .sort((a, b) => {
        // 날짜순 정렬, 같은 날짜면 idx순
        const dateCompare = (a.cb_date || '').localeCompare(b.cb_date || '');
        if (dateCompare !== 0) return dateCompare;
        return (a.cb_idx || 0) - (b.cb_idx || 0);
      });
    
    sortedItems.forEach(item => {
      const match = beneficiaryMatches.value[item.cb_idx!];
      if (match) {
        const cbIdx = match.cb_idx;
        if (!depositsMap.has(cbIdx)) {
          depositsMap.set(cbIdx, []);
        }
        depositsMap.get(cbIdx)!.push({
          amount: item.cb_price || 0,
          date: item.cb_date || '',
          cashbookIdx: item.cb_idx!
        });
      }
    });
    
    // 수급자별로 입금 정보 집계 (합산 + 개별 내역)
    const deposits: Array<{ 
      cb_idx: number; 
      amount: number; 
      date?: string;
      depositDetails?: Array<{ amount: number; date: string }>;
    }> = [];
    
    for (const [cbIdx, depositList] of depositsMap.entries()) {
      const totalAmount = depositList.reduce((sum, d) => sum + d.amount, 0);
      const firstDate = depositList[0]?.date;
      
      deposits.push({
        cb_idx: cbIdx,
        amount: totalAmount,
        date: firstDate || undefined,
        depositDetails: depositList.map(d => ({ amount: d.amount, date: d.date }))
      });
    }
    
    // 수납 데이터 동기화 API 호출 (deposits가 비어있어도 호출 - 이전 매칭 초기화 위해)
    await collectionApi.syncDeposit(memberCode.value, {
      year: parseInt(currentYear.value),
      month: parseInt(currentMonth.value),
      deposits,
      bizGubun: selectedBizGubun.value || undefined
    });
    
  } catch (error) {
    console.error('수납 데이터 동기화 오류:', error);
  }
};

// 계정코드 로드
const loadAccountCodes = async () => {
  try {
    const res = await bankbookApi.getAccountCodes(currentYear.value);
    accountCodes.value = (res.data?.data || []) as AccountCode[];
  } catch (error) {
    console.error('계정코드 로드 오류:', error);
  }
};

// 인라인 수정 시작
const startInlineEdit = (item: CashbookData) => {
  // 마감 상태에서는 수정 불가
  if (closingStatus.value?.isClosed) {
    alert('마감된 통장내역은 수정할 수 없습니다.\n마감 해제 후 수정해주세요.');
    return;
  }
  
  editingRowIdx.value = item.cb_idx || null;
  inlineEditData.value = { ...item };
};

// 인라인 수정 취소
const cancelInlineEdit = () => {
  editingRowIdx.value = null;
  inlineEditData.value = {
    cb_gubun: '',
    cb_scode: '',
    cb_funding: '',
    cb_memo: '',
    cb_price: 0,
    cb_date: '',
    cb_rel_acc: ''
  };
  newKeywordInput.value = '';
};

// 인라인 수정 저장
const saveInlineEdit = async () => {
  if (!editingRowIdx.value) return;
  
  isInlineSaving.value = true;
  try {
    await bankbookApi.update(editingRowIdx.value, {
      cb_scode: inlineEditData.value.cb_scode,
      cb_funding: inlineEditData.value.cb_funding,
      cb_memo: inlineEditData.value.cb_memo,
      cb_price: inlineEditData.value.cb_price,
      cb_rel_acc: inlineEditData.value.cb_rel_acc
    });
    
    // 로컬 데이터 업데이트
    const idx = cashbookList.value.findIndex(item => item.cb_idx === editingRowIdx.value);
    if (idx !== -1) {
      cashbookList.value[idx] = { ...cashbookList.value[idx], ...inlineEditData.value };
    }
    
    showSaveToast.value = true;
    setTimeout(() => { showSaveToast.value = false; }, 2000);
    
    cancelInlineEdit();
    await loadAdjustState();
  } catch (error) {
    console.error('저장 오류:', error);
    alert('저장에 실패했습니다.');
  } finally {
    isInlineSaving.value = false;
  }
};

// ============================================
// 자금원천 자동 부여 로직
// ============================================

// 계정코드로 계정명 조회
const getAccountNameByCode = (scode: string, gubun: string): string | null => {
  const list = gubun === 'IN' ? incomeCodeList.value : expenseCodeList.value;
  const found = list.find(item => item.scode === scode);
  return found?.sname || null;
};

// ============================================
// 상대계정 자동 추론 로직
// ============================================

/**
 * 지출 항목에 대해 사용 가능한 수입 계정(상대계정)을 자동으로 추론
 * 
 * 로직:
 * 1. 해당 지출의 날짜/idx 이전 데이터 기준으로 각 수입 계정별 잔액 계산
 * 2. 잔액 = (수입 합계) - (해당 계정을 상대계정으로 사용한 지출 합계)
 * 3. 이월금이 있으면 수입 비율에 따라 각 계정에 분배
 * 4. 지출 금액을 커버할 수 있는 수입 계정 중 잔액이 가장 많은 것을 선택
 * 
 * @param targetItem 상대계정을 찾을 지출 항목
 * @returns 추론된 수입 계정 코드 또는 null
 */
const inferRelativeAccount = (targetItem: CashbookData): string | null => {
  // 지출이 아니면 상대계정 불필요
  if (targetItem.cb_gubun !== 'OUT') return null;
  
  const targetDate = targetItem.cb_date;
  const targetIdx = targetItem.cb_idx;
  const targetPrice = Math.abs(Number(targetItem.cb_price) || 0);
  
  if (targetPrice <= 0) return null;
  
  // 수입 계정별 잔액 계산
  // Map<scode, { totalIncome: number, usedExpense: number }>
  const accountBalances = new Map<string, { totalIncome: number; usedExpense: number; sname: string }>();
  
  // 1. 수입 항목 집계 (targetItem 날짜/idx 이전 또는 같은 날짜라면 idx가 작은 것)
  let totalIncome = 0; // 전체 수입 합계 (이월금 분배용)
  
  for (const item of cashbookList.value) {
    if (item.cb_gubun !== 'IN' || !item.cb_scode) continue;
    
    // 날짜 비교: targetDate 이전이거나, 같은 날짜면 idx가 작거나 같아야 함
    const isBeforeOrSame = item.cb_date < targetDate || 
      (item.cb_date === targetDate && (item.cb_idx || 0) <= (targetIdx || 0));
    
    if (!isBeforeOrSame) continue;
    
    const scode = item.cb_scode;
    const price = Number(item.cb_price) || 0;
    
    if (!accountBalances.has(scode)) {
      const sname = getAccountNameByCode(scode, 'IN') || scode;
      accountBalances.set(scode, { totalIncome: 0, usedExpense: 0, sname });
    }
    
    accountBalances.get(scode)!.totalIncome += price;
    totalIncome += price;
  }
  
  // 2. 이월금 처리 - 현재 월에 실제 수입이 있는 계정에만 비율 배분
  const carryover = Number(prevMonthBalance.value) || 0;
  
  if (carryover > 0 && totalIncome > 0 && accountBalances.size > 0) {
    // 현재 월 수입이 있는 계정에만 이월금을 비율에 따라 분배
    for (const [scode, balance] of accountBalances.entries()) {
      const ratio = balance.totalIncome / totalIncome;
      balance.totalIncome += carryover * ratio;
    }
  }
  // 현재 월에 수입이 없으면 이월금만으로는 상대계정 추론하지 않음
  // (실제 수입이 있는 계정만 상대계정 후보가 됨)
  
  // 3. 지출에서 사용된 상대계정 집계 (targetItem 이전의 지출만)
  for (const item of cashbookList.value) {
    if (item.cb_gubun !== 'OUT' || !item.cb_rel_acc) continue;
    if (item.cb_idx === targetIdx) continue; // 자기 자신 제외
    
    // 날짜 비교: targetDate 이전이거나, 같은 날짜면 idx가 작아야 함
    const isBefore = item.cb_date < targetDate || 
      (item.cb_date === targetDate && (item.cb_idx || 0) < (targetIdx || 0));
    
    if (!isBefore) continue;
    
    const relAcc = item.cb_rel_acc;
    const price = Math.abs(Number(item.cb_price) || 0);
    
    if (accountBalances.has(relAcc)) {
      accountBalances.get(relAcc)!.usedExpense += price;
    }
  }
  
  // 4. 잔액이 충분한 수입 계정 찾기 (잔액이 가장 많은 것 선택)
  let bestAccount: string | null = null;
  let bestBalance = 0;
  
  for (const [scode, balance] of accountBalances.entries()) {
    const remaining = balance.totalIncome - balance.usedExpense;
    
    // 잔액이 지출 금액 이상이고, 가장 잔액이 많은 계정 선택
    if (remaining >= targetPrice && remaining > bestBalance) {
      bestBalance = remaining;
      bestAccount = scode;
    }
  }
  
  return bestAccount;
};

// 자금원천 자동 조회 (API)
const fetchFundingByAccount = async (sname: string, trcode: string): Promise<string | null> => {
  if (!sname || /^\d+$/.test(sname)) return null; // 숫자로만 구성된 계정명은 스킵
  
  try {
    const res = await bankbookApi.getFundingByAccount(sname, trcode, currentYear.value);
    if (res.data?.success && res.data.data?.funding) {
      return res.data.data.funding;
    }
  } catch (error) {
    console.error('자금원천 조회 오류:', error);
  }
  return null;
};

// 세입(수입) 계정 선택 시 자금원천 자동 설정
watch(() => inlineEditData.value.cb_scode, async (newScode, oldScode) => {
  if (!newScode || newScode === oldScode || !editingRowIdx.value) return;
  
  const gubun = inlineEditData.value.cb_gubun;
  
  // 수입(IN)인 경우: 계정의 funding 조회
  if (gubun === 'IN') {
    const sname = getAccountNameByCode(newScode, 'IN');
    if (sname) {
      const funding = await fetchFundingByAccount(sname, 'IN');
      if (funding) {
        inlineEditData.value.cb_funding = funding;
        // 후원금인 경우 사업명도 설정 (필요시)
      }
    }
  }
  
  if (gubun === 'OUT') {
    const paired = adjustIncomes.value.filter((inc) =>
      (inc.allowedExpenses || []).some((exp) => exp.scode === newScode) && inc.remaining > 0
    );
    const currentRel = inlineEditData.value.cb_rel_acc;
    const currentOk = currentRel && paired.some((inc) => inc.scode === currentRel);
    if (paired.length && !currentOk) {
      paired.sort((a, b) => b.remaining - a.remaining);
      inlineEditData.value.cb_rel_acc = paired[0].scode;
    }
    const relScode = inlineEditData.value.cb_rel_acc;
    const relName = relScode ? getAccountNameByCode(relScode, 'IN') : null;
    if (relName) {
      const funding = await fetchFundingByAccount(relName, 'IN');
      if (funding) inlineEditData.value.cb_funding = funding;
    }
  }
});

// 지출(OUT) 상대계정 선택 시 자금원천 자동 설정 (생계비 제외)
watch(() => inlineEditData.value.cb_rel_acc, async (newRelAcc, oldRelAcc) => {
  if (!newRelAcc || newRelAcc === oldRelAcc || !editingRowIdx.value) return;
  
  const gubun = inlineEditData.value.cb_gubun;
  if (gubun !== 'OUT') return;
  
  const scode = inlineEditData.value.cb_scode;
  if (!scode) return;
  
  // 상대계정(수입 계정)의 funding 조회
  const relAccName = getAccountNameByCode(newRelAcc, 'IN');
  if (relAccName) {
    const funding = await fetchFundingByAccount(relAccName, 'IN');
    if (funding) {
      inlineEditData.value.cb_funding = funding;
    }
  }
});

// ============================================
// 수급자관리 모달 (수금관리 탭) 관련
// ============================================

// 통장내역에서 본인부담금(112) 매칭 데이터 수집 (같은 수급자는 합산)
const collectDepositData = () => {
  const depositData: Record<number, { amount: number; date: string }> = {};
  
  // 현재 월의 통장내역에서 본인부담금(112) 항목 중 매칭된 것들 수집
  cashbookList.value.forEach((item: CashbookData) => {
    if (item.cb_scode === '112' && item.cb_idx) {
      const match = beneficiaryMatches.value[item.cb_idx];
      if (match) {
        // cb_idx(수급자 idx)를 키로 사용 - 같은 수급자는 합산
        if (depositData[match.cb_idx]) {
          // 기존 값이 있으면 합산
          depositData[match.cb_idx].amount += (item.cb_price || 0);
          // 날짜는 가장 빠른 날짜 유지
          if ((item.cb_date || '') < depositData[match.cb_idx].date) {
            depositData[match.cb_idx].date = item.cb_date || '';
          }
        } else {
          // 새로 추가
          depositData[match.cb_idx] = {
            amount: item.cb_price || 0,
            date: item.cb_date || ''
          };
        }
      }
    }
  });
  
  return depositData;
};

// 수급자 칩 → 수납 탭으로 이동 (해당 이름으로 검색)
const openCollectionForBeneficiary = (beneficiaryName: string) => {
  goStep('collection', { collectionSearch: beneficiaryName || '' });
};

// 본인부담금수입 클릭 시 분개 내역 토글
const toggleCollectionDetails = async (item: CashbookData) => {
  if (!item.cb_idx) return;
  
  // 이미 펼쳐져 있으면 접기
  if (expandedCollectionRows.value.has(item.cb_idx)) {
    expandedCollectionRows.value.delete(item.cb_idx);
    return;
  }
  
  // 매칭된 수급자 정보 확인
  const matchInfo = beneficiaryMatches.value[item.cb_idx];
  if (!matchInfo) return;
  
  // 분개 내역 조회
  try {
    const res = await collectionApi.getByBeneficiary(matchInfo.cb_idx, {
      year: parseInt(currentYear.value),
      month: parseInt(currentMonth.value)
    });
    
    const items = res.data?.data || [];
    // 모든 분개 항목 표시 (금액이 있는 항목만)
    collectionDetails.value[item.cb_idx] = items
      .filter((c: any) => c.cc_charge > 0 || c.cc_deposit > 0)
      .map((c: any) => ({
        scode: c.cc_scode,
        sname: c.cc_sname,
        charge: c.cc_charge || 0,
        memo: c.cc_memo || ''
      }));
    
    expandedCollectionRows.value.add(item.cb_idx);
  } catch (error) {
    console.error('분개 내역 조회 오류:', error);
  }
};

// 분개 내역이 펼쳐져 있는지 확인
const isCollectionExpanded = (cbIdx: number) => {
  return expandedCollectionRows.value.has(cbIdx);
};

// 분개 내역 합계 계산
const getCollectionTotal = (cbIdx: number) => {
  const details = collectionDetails.value[cbIdx];
  if (!details || details.length === 0) return 0;
  return details.reduce((sum, detail) => sum + (detail.charge || 0), 0);
};

// 인라인 금액 입력 처리
const handleInlinePriceInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  const value = input.value.replace(/[^0-9]/g, '');
  inlineEditData.value.cb_price = parseInt(value) || 0;
};

// 인라인 금액 키다운 처리
const handleInlinePriceKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    saveInlineEdit();
  } else if (e.key === 'Escape') {
    cancelInlineEdit();
  }
};

// 삭제 확인 (단건)
const confirmDelete = (item: CashbookData) => {
  if (closingStatus.value?.isClosed || !isItemEditable(item)) {
    alert('마감된 통장내역은 삭제할 수 없습니다.\n마감 해제 후 삭제해주세요.');
    return;
  }
  selectedItem.value = item;
  deleteTargets.value = [item];
  deleteDialogOpen.value = true;
};

// 선택 항목 삭제 확인 (다건)
const confirmDeleteSelected = () => {
  if (closingStatus.value?.isClosed) {
    alert('마감된 통장내역은 삭제할 수 없습니다.\n마감 해제 후 삭제해주세요.');
    return;
  }
  const items = selectedLedgerItems.value.filter((item) => isItemEditable(item));
  if (!items.length) {
    alert('삭제할 수 있는 항목이 없습니다.');
    return;
  }
  deleteTargets.value = items;
  selectedItem.value = items[0] || null;
  deleteDialogOpen.value = true;
};

// 항목 삭제 (단건·다건)
const deleteItem = async () => {
  const targets = (deleteTargets.value.length
    ? deleteTargets.value
    : (selectedItem.value ? [selectedItem.value] : [])
  ).filter((item) => item.cb_idx);

  if (!targets.length) return;

  if (closingStatus.value?.isClosed) {
    alert('마감된 통장내역은 삭제할 수 없습니다.\n마감 해제 후 삭제해주세요.');
    deleteDialogOpen.value = false;
    return;
  }

  isDeleting.value = true;
  try {
    const matchedCbIdxList: number[] = [];
    targets.forEach((deletedItem) => {
      if (deletedItem.cb_scode === '112' && deletedItem.cb_idx) {
        const match = beneficiaryMatches.value[deletedItem.cb_idx];
        if (match && !matchedCbIdxList.includes(match.cb_idx)) {
          matchedCbIdxList.push(match.cb_idx);
        }
      }
    });

    if (matchedCbIdxList.length > 0 && memberCode.value) {
      try {
        await collectionApi.clearDeposits(memberCode.value, {
          cbIdxList: matchedCbIdxList,
          year: parseInt(currentYear.value),
          month: parseInt(currentMonth.value),
          bizGubun: selectedBizGubun.value || undefined
        });
      } catch (clearError) {
        console.error('수납관리 입금액 초기화 오류:', clearError);
      }
    }

    targets.forEach((deletedItem) => {
      if (deletedItem.cb_idx && beneficiaryMatches.value[deletedItem.cb_idx]) {
        delete beneficiaryMatches.value[deletedItem.cb_idx];
      }
    });

    const deletedIds = new Set(targets.map((t) => t.cb_idx!));
    if (targets.length === 1) {
      await bankbookApi.delete(targets[0].cb_idx!);
    } else {
      await bankbookApi.deleteMultiple([...deletedIds]);
    }

    cashbookList.value = cashbookList.value.filter((item) => !deletedIds.has(item.cb_idx!));
    ledgerSelectedIds.value = ledgerSelectedIds.value.filter((id) => !deletedIds.has(id));
    deleteDialogOpen.value = false;
    selectedItem.value = null;
    deleteTargets.value = [];
    await loadAdjustState();
  } catch (error) {
    console.error('삭제 오류:', error);
    alert('삭제에 실패했습니다.');
  } finally {
    isDeleting.value = false;
  }
};

// 모두 삭제
const deleteAllItems = async () => {
  // 마감 상태에서는 삭제 불가
  if (closingStatus.value?.isClosed) {
    alert('마감된 통장내역은 삭제할 수 없습니다.\n마감 해제 후 삭제해주세요.');
    deleteAllDialogOpen.value = false;
    return;
  }
  
  isDeleting.value = true;
  try {
    const itemsToDelete = filteredCashbookList.value;
    const idxList = itemsToDelete.map(item => item.cb_idx).filter(Boolean) as number[];
    
    // 본인부담금(112) 항목 중 수급자 매칭된 것들의 수급자 ID 수집
    const matchedCbIdxList: number[] = [];
    itemsToDelete.forEach(item => {
      if (item.cb_scode === '112' && item.cb_idx) {
        const match = beneficiaryMatches.value[item.cb_idx];
        if (match && !matchedCbIdxList.includes(match.cb_idx)) {
          matchedCbIdxList.push(match.cb_idx);
        }
      }
    });
    
    // 수납관리의 입금액 초기화
    if (matchedCbIdxList.length > 0 && memberCode.value) {
      try {
        await collectionApi.clearDeposits(memberCode.value, {
          cbIdxList: matchedCbIdxList,
          year: parseInt(currentYear.value),
          month: parseInt(currentMonth.value),
          bizGubun: selectedBizGubun.value || undefined
        });
      } catch (clearError) {
        console.error('수납관리 입금액 초기화 오류:', clearError);
      }
    }
    
    // beneficiaryMatches에서 삭제되는 항목들 제거
    itemsToDelete.forEach(item => {
      if (item.cb_scode === '112' && item.cb_idx && beneficiaryMatches.value[item.cb_idx]) {
        delete beneficiaryMatches.value[item.cb_idx];
      }
    });
    
    await bankbookApi.deleteMultiple(idxList);
    
    cashbookList.value = cashbookList.value.filter(item => !idxList.includes(item.cb_idx!));
    deleteAllDialogOpen.value = false;
  } catch (error) {
    console.error('일괄 삭제 오류:', error);
    alert('삭제에 실패했습니다.');
  } finally {
    isDeleting.value = false;
  }
};

// 업로드 다이얼로그 열기
const openUploadDialog = () => {
  if (isBankbookUploadDisabled.value) {
    alert('마감된 통장내역에는 업로드할 수 없습니다.\n마감 해제 후 업로드해주세요.');
    return;
  }
  uploadDialogOpen.value = true;
};

// 업로드 완료 처리
const handleUploadSaved = (savedItems?: CashbookData[]) => {
  // 새로 저장된 항목들의 ID를 하이라이트 목록에 추가 (있는 경우에만)
  if (savedItems && Array.isArray(savedItems) && savedItems.length > 0) {
    highlightedIdxList.value = savedItems.map(item => item.cb_idx!).filter(Boolean);
    
    // 3초 후 하이라이트 해제
    setTimeout(() => {
      highlightedIdxList.value = [];
    }, 3000);
  }
  
  // 데이터 새로고침
  loadCashbookData();
};

// 업로드 완료 알림 처리 (빠른 업로드용)
interface UploadResultInfo {
  bankCode: string;
  bankAlias: string;
  fileName: string;
  count: number;
  success: boolean;
  message?: string;
}

const handleUploadComplete = (results: UploadResultInfo[]) => {
  // 기존 알림 초기화
  uploadNotifications.value = [];
  
  // 각 결과에 대해 알림 생성 (순차적으로 표시)
  results.forEach((result, index) => {
    const notification: UploadNotification = {
      id: ++notificationIdCounter,
      show: false,
      bankCode: result.bankCode,
      bankAlias: result.bankAlias,
      fileName: result.fileName,
      count: result.count,
      success: result.success,
      message: result.message
    };
    
    uploadNotifications.value.push(notification);
    
    // 순차적으로 알림 표시 (200ms 간격)
    setTimeout(() => {
      notification.show = true;
    }, index * 200);
    
    // 5초 후 알림 제거
    setTimeout(() => {
      notification.show = false;
      // 애니메이션 후 배열에서 제거
      setTimeout(() => {
        const idx = uploadNotifications.value.findIndex(n => n.id === notification.id);
        if (idx !== -1) {
          uploadNotifications.value.splice(idx, 1);
        }
      }, 300);
    }, 5000 + index * 200);
  });
  
  // 데이터 새로고침
  loadCashbookData();

  // 업로드된 통장으로 사이드바 전환 (업로드 결과 확인용)
  const firstSuccess = results.find(r => r.success && r.bankCode);
  if (firstSuccess?.bankCode) {
    selectedBankCode.value = firstSuccess.bankCode;
  }
};

// 커스텀 키워드 추가
const addCustomKeyword = async () => {
  if (!newKeywordInput.value.trim() || !inlineEditData.value.cb_scode) return;
  
  isAddingKeyword.value = true;
  try {
    await bankbookApi.learn({
      m_code: memberCode.value,
      biz_gubun: selectedBizGubun.value || undefined,
      gubun: inlineEditData.value.cb_gubun,
      keyword: newKeywordInput.value.trim(),
      scode: inlineEditData.value.cb_scode
    });
    
    newKeywordInput.value = '';
  } catch (error) {
    console.error('키워드 추가 오류:', error);
  } finally {
    isAddingKeyword.value = false;
  }
};

// 수급자 목록 로드 (매칭용) - 수납관리에 등록된 모든 수급자 (이미 매칭된 수급자 포함)
const loadBeneficiaryList = async () => {
  if (!memberCode.value) return;
  
  try {
    // 1. 해당 월 수납관리에 등록된 수급자 목록 조회
    const collectionRes = await collectionApi.getByMember(memberCode.value, {
      year: parseInt(currentYear.value),
      month: parseInt(currentMonth.value),
      bizGubun: selectedBizGubun.value || undefined
    });
    
    if (!collectionRes.data?.success) {
      beneficiaryList.value = [];
      return;
    }
    
    const collectionData = collectionRes.data.data?.list || collectionRes.data.data || [];
    
    // 2. 수급자별로 그룹핑하여 총 청구금액 계산 (본인부담금 + 비급여 합산)
    // 이미 매칭된 수급자도 포함 (같은 수급자를 여러 통장내역에 매칭 가능)
    const beneficiaryMap = new Map<number, {
      cb_idx: number;
      cb_name: string;
      cb_ltc_no: string;
      cc_idx: number;
      cc_charge: number;
      totalCharge: number;
    }>();
    
    collectionData.forEach((item: any) => {
      const cbIdx = item.cb_idx;
      if (!cbIdx) return;
      
      if (!beneficiaryMap.has(cbIdx)) {
        beneficiaryMap.set(cbIdx, {
          cb_idx: cbIdx,
          cb_name: item.cb_name || '',
          cb_ltc_no: item.cb_ltc_no || '',
          cc_idx: 0,
          cc_charge: 0,
          totalCharge: 0
        });
      }
      
      const entry = beneficiaryMap.get(cbIdx)!;
      const charge = item.cc_charge || 0;
      
      if (item.cc_scode === '112') {
        entry.cc_idx = item.cc_idx;
        entry.cc_charge = charge;
      }
      
      entry.totalCharge += charge;
    });
    
    beneficiaryList.value = Array.from(beneficiaryMap.values()).filter(b => b.cc_idx > 0);
      
  } catch (error) {
    console.error('수급자 목록 로드 오류:', error);
    beneficiaryList.value = [];
  }
};

// 수급자 매칭 팝오버 열기
const openBeneficiaryMatchPopover = async (item: CashbookData) => {
  matchTargetItem.value = item;
  selectedBeneficiaryForMatch.value = null;
  
  // 매칭되지 않은 수급자 목록 새로고침
  await loadBeneficiaryList();
  
  beneficiaryMatchDialogOpen.value = true;
};

// 수급자 매칭 팝오버 닫기
const closeBeneficiaryMatchDialog = () => {
  beneficiaryMatchDialogOpen.value = false;
  matchTargetItem.value = null;
  selectedBeneficiaryForMatch.value = null;
};

// 수급자 매칭 저장
const saveBeneficiaryMatch = async () => {
  if (!selectedBeneficiaryForMatch.value || !matchTargetItem.value?.cb_idx || !memberCode.value) {
    return;
  }
  
  const selectedBeneficiary = beneficiaryList.value.find(
    b => b.cb_idx === selectedBeneficiaryForMatch.value
  );
  
  if (!selectedBeneficiary || !selectedBeneficiary.cc_idx) {
    alert('수급자 정보를 찾을 수 없습니다.');
    return;
  }
  
  try {
    // 1. 통장내역에 매칭 수급자 정보 저장 (수동 매칭 유지용)
    await bankbookApi.update(matchTargetItem.value.cb_idx, {
      cb_matched_beneficiary_idx: selectedBeneficiary.cb_idx
    });
    
    // 2. 로컬 cashbookList 업데이트
    const itemIndex = cashbookList.value.findIndex(i => i.cb_idx === matchTargetItem.value?.cb_idx);
    if (itemIndex !== -1) {
      cashbookList.value[itemIndex].cb_matched_beneficiary_idx = selectedBeneficiary.cb_idx;
    }
    
    // 3. beneficiaryMatches에 매칭 정보 추가 (통장내역 idx → 수급자 정보)
    beneficiaryMatches.value[matchTargetItem.value.cb_idx] = {
      cb_idx: selectedBeneficiary.cb_idx,
      cb_name: selectedBeneficiary.cb_name,
      matchType: 'manual'
    };
    
    // 4. syncDepositToCollection을 호출하여 전체 매칭 정보 기반으로 합산 처리
    await syncDepositToCollection();
    
    closeBeneficiaryMatchDialog();
  } catch (error: any) {
    console.error('수급자 매칭 저장 오류:', error);
    alert(`수급자 매칭 저장에 실패했습니다: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
  }
};

// 수급자 매칭 변경 (기존 매칭 해제 후 새 매칭 다이얼로그 열기)
const changeBeneficiaryMatch = async (item: CashbookData) => {
  if (!item.cb_idx || !memberCode.value) return;
  
  const currentMatch = beneficiaryMatches.value[item.cb_idx];
  if (!currentMatch) return;
  
  try {
    // 같은 수급자에게 다른 통장내역도 매칭되어 있는지 확인
    const otherMatchCount = Object.entries(beneficiaryMatches.value)
      .filter(([cbIdx, match]) => 
        parseInt(cbIdx) !== item.cb_idx && match.cb_idx === currentMatch.cb_idx
      ).length;
    
    // 1. 통장내역의 매칭 정보 초기화
    await bankbookApi.update(item.cb_idx, {
      cb_matched_beneficiary_idx: null
    });
    
    // 2. 로컬 cashbookList 업데이트
    const itemIndex = cashbookList.value.findIndex(i => i.cb_idx === item.cb_idx);
    if (itemIndex !== -1) {
      cashbookList.value[itemIndex].cb_matched_beneficiary_idx = null;
    }
    
    // 3. 로컬 매칭 정보 삭제
    delete beneficiaryMatches.value[item.cb_idx];
    
    // 4. 수납 데이터 재계산 (이전 수급자 입금 초기화 + 남은 매칭 반영)
    // syncDepositToCollection()은 백엔드에서 매칭되지 않은 수급자의 입금을 자동 초기화함
    await syncDepositToCollection();
    
    // 5. 새 매칭 다이얼로그 열기
    openBeneficiaryMatchPopover(item);
    
  } catch (error: any) {
    console.error('수급자 매칭 변경 오류:', error);
    alert(`수급자 매칭 변경에 실패했습니다: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
  }
};

// 수급자 매칭 해제
const removeBeneficiaryMatch = async (item: CashbookData) => {
  if (!item.cb_idx || !memberCode.value) return;
  
  const currentMatch = beneficiaryMatches.value[item.cb_idx];
  if (!currentMatch) return;
  
  // 같은 수급자에게 다른 통장내역도 매칭되어 있는지 확인
  const otherMatchCount = Object.entries(beneficiaryMatches.value)
    .filter(([cbIdx, match]) => 
      parseInt(cbIdx) !== item.cb_idx && match.cb_idx === currentMatch.cb_idx
    ).length;
  
  const confirmMsg = otherMatchCount > 0
    ? `'${currentMatch.cb_name}' 수급자와의 이 통장내역 매칭을 해제하시겠습니까?\n(다른 ${otherMatchCount}건의 통장내역은 유지됩니다)`
    : `'${currentMatch.cb_name}' 수급자와의 매칭을 해제하시겠습니까?\n수납관리의 입금 정보도 초기화됩니다.`;
  
  if (!confirm(confirmMsg)) {
    return;
  }
  
  try {
    // 1. 통장내역의 매칭 정보 초기화
    await bankbookApi.update(item.cb_idx, {
      cb_matched_beneficiary_idx: null
    });
    
    // 2. 로컬 cashbookList 업데이트
    const itemIndex = cashbookList.value.findIndex(i => i.cb_idx === item.cb_idx);
    if (itemIndex !== -1) {
      cashbookList.value[itemIndex].cb_matched_beneficiary_idx = null;
    }
    
    // 3. 로컬 매칭 정보 삭제
    delete beneficiaryMatches.value[item.cb_idx];
    
    // 4. 수납 데이터 재계산 (이전 수급자 입금 초기화 + 남은 매칭 반영)
    // syncDepositToCollection()은 백엔드에서 매칭되지 않은 수급자의 입금을 자동 초기화함
    await syncDepositToCollection();
    
  } catch (error: any) {
    console.error('수급자 매칭 해제 오류:', error);
    alert(`수급자 매칭 해제에 실패했습니다: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
  }
};

// AI 학습 관리 모달 열기
const mapLearnRows = (rows: any[]): DictItem[] =>
  (rows || []).map((item: any) => ({
    cd_idx: item.cl_idx,
    m_code: item.m_code,
    cd_gubun: item.cl_gubun,
    cd_keyword: item.cl_keyword,
    cd_scode: item.cl_scode,
    cd_count: item.cl_count,
    ac_name: item.scode_name
  }));

const openDictionaryManageModal = async () => {
  dictionaryManageModalOpen.value = true;
  dictSearchQuery.value = '';
  selectedDictTable.value = null;
  dictDetailList.value = [];
  isDictDetailLoading.value = true;

  try {
    const [incomeRes, expenseRes] = await Promise.all([
      bankbookApi.getLearnList(memberCode.value, { gubun: 'IN' }),
      bankbookApi.getLearnList(memberCode.value, { gubun: 'OUT' })
    ]);
    const incomeRows = incomeRes.data?.data || [];
    const expenseRows = expenseRes.data?.data || [];
    dictionaryStats.value = {
      income: incomeRows.length,
      expense: expenseRows.length,
      relmap: 0
    };
    const preferExpense = expenseRows.length >= incomeRows.length;
    selectedDictTable.value = preferExpense ? 'expense' : 'income';
    dictDetailList.value = mapLearnRows(preferExpense ? expenseRows : incomeRows);
  } catch (error) {
    console.error('딕셔너리 통계 로드 오류:', error);
    dictionaryStats.value = { income: 0, expense: 0, relmap: 0 };
    selectedDictTable.value = 'expense';
    dictDetailList.value = [];
  } finally {
    isDictDetailLoading.value = false;
  }
};


// 딕셔너리 테이블 선택
const selectDictTable = async (table: string) => {
  if (selectedDictTable.value === table && dictDetailList.value.length) return;
  selectedDictTable.value = table;
  isDictDetailLoading.value = true;
  dictSearchQuery.value = '';

  try {
    if (table === 'relmap') {
      dictDetailList.value = [];
      return;
    }
    const gubun = table === 'income' ? 'IN' : 'OUT';
    const res = await bankbookApi.getLearnList(memberCode.value, { gubun });
    dictDetailList.value = mapLearnRows(res.data?.data || []);
  } catch (error) {
    console.error('딕셔너리 목록 로드 오류:', error);
    dictDetailList.value = [];
  } finally {
    isDictDetailLoading.value = false;
  }
};

// 딕셔너리 항목 삭제
const deleteDictItem = async (item: DictItem) => {
  if (!confirm('이 학습 데이터를 삭제하시겠습니까?')) return;
  
  try {
    await bankbookApi.deleteLearn(item.cd_idx);
    dictDetailList.value = dictDetailList.value.filter(d => d.cd_idx !== item.cd_idx);
    
    // 통계 업데이트
    if (dictionaryStats.value && selectedDictTable.value) {
      const key = selectedDictTable.value as keyof typeof dictionaryStats.value;
      dictionaryStats.value[key]--;
    }
  } catch (error) {
    console.error('딕셔너리 삭제 오류:', error);
    alert('삭제에 실패했습니다.');
  }
};

// 영수증 매칭: 편집·캔버스 탭으로 이동
const openReceiptMatchingDialog = (item: CashbookData) => {
  selectedCashbookForMatching.value = item;
  if (isReceiptStep.value) return;
  goStep('receipt', { keepReceiptItem: true });
};

// 영수증 매칭 저장 완료 처리
const handleMatchingSaved = (data: { crm_code: number | null; unmatched?: boolean; cb_idx?: number }) => {
  const targetIdx = data.cb_idx ?? selectedCashbookForMatching.value?.cb_idx;
  if (targetIdx) {
    const idx = cashbookList.value.findIndex(item => item.cb_idx === targetIdx);
    if (idx !== -1) {
      cashbookList.value[idx].cb_receipt_matched = data.unmatched ? 0 : 1;
    }
    if (selectedCashbookForMatching.value?.cb_idx === targetIdx) {
      selectedCashbookForMatching.value = {
        ...selectedCashbookForMatching.value,
        cb_receipt_matched: data.unmatched ? 0 : 1,
      };
    }
  }

  showSaveToast.value = true;
  setTimeout(() => { showSaveToast.value = false; }, 2000);
};

// ============================================
// 통장 관리 메서드
// ============================================

// 통장 목록 로드 (caion_member_bank.m_code = 시설 내부 PK)
const loadBankList = async () => {
  if (!memberCode.value) return;
  
  try {
    const res = await bankbookApi.getBankList(memberCode.value, true);
    let allBanks = (res.data?.data || []) as BankData[];
    
    // 사업구분 필터링: 선택된 사업구분에 해당하는 통장만 표시 (공통 통장 불허용)
    const currentBizGubun = selectedBizGubun.value;
    
    if (currentBizGubun) {
      allBanks = allBanks.filter(b => b.mb_biz_gubun === currentBizGubun);
    }
    
    bankList.value = allBanks;
    
    // 기본값: 전체 보기 (null)
    // selectedBankCode가 아직 설정되지 않았거나, 설정된 통장이 더 이상 존재하지 않으면 전체로 설정
    if (selectedBankCode.value !== null) {
      const exists = bankList.value.some(b => b.mb_code === selectedBankCode.value);
      if (!exists) {
        selectedBankCode.value = null;
      }
    }
  } catch (error) {
    console.error('통장 목록 로드 오류:', error);
    bankList.value = [];
  }
};

// 통장 선택 (null이면 전체 보기)
const selectBank = (bankCode: string | null) => {
  selectedBankCode.value = bankCode;
  // 통장 변경 시 마감/희망이음 상태만 다시 로드 (데이터는 이미 전체 로드됨)
  loadClosingStatus();
  loadHeumieumStatus();
  loadAdjustState();
};

// 통장 코드로 별칭 가져오기
const getBankAlias = (bankCode: string | null | undefined): string => {
  if (!bankCode) return '-';
  const bank = bankList.value.find(b => b.mb_code === bankCode);
  return bank?.mb_alias || bankCode;
};

// 계좌번호 포맷팅
const formatAccountNo = (accountNo: string): string => {
  if (!accountNo) return '';
  // 앞 4자리와 뒤 4자리만 표시, 중간은 *로 마스킹
  if (accountNo.length <= 8) return accountNo;
  const front = accountNo.slice(0, 4);
  const back = accountNo.slice(-4);
  const middle = '*'.repeat(Math.min(accountNo.length - 8, 6));
  return `${front}-${middle}-${back}`;
};

// 통장 사용기간 포맷 (YYYY-MM → YYYY.MM)
const formatBankPeriod = (bank: BankData): string => {
  const formatMonth = (monthStr: string | undefined | null) => {
    if (!monthStr) return '';
    return monthStr.replace('-', '.');
  };
  
  const start = formatMonth(bank.mb_start_date);
  const end = formatMonth(bank.mb_end_date);
  
  if (!start && !end) return '기간 미설정';
  if (start && !end) return `${start} ~ 사용중`;
  if (!start && end) return `~ ${end}`;
  return `${start} ~ ${end}`;
};

// 통장이 현재 연월에 활성화되어 있는지 확인
const isBankActiveForCurrentMonth = (bank: BankData): boolean => {
  const currentYM = `${currentYear.value}-${currentMonth.value}`;
  
  // 시작월이 없으면 항상 활성화
  if (!bank.mb_start_date) return true;
  
  // 현재 연월이 시작월보다 이전이면 비활성화
  if (currentYM < bank.mb_start_date) return false;
  
  // 종료월이 있고, 현재 연월이 종료월보다 이후면 비활성화
  if (bank.mb_end_date && currentYM > bank.mb_end_date) return false;
  
  return true;
};

// 비활성 통장의 툴팁 텍스트
const getBankPeriodTooltip = (bank: BankData): string => {
  const currentYM = `${currentYear.value}-${currentMonth.value}`;
  
  if (!bank.mb_start_date) return '';
  
  if (currentYM < bank.mb_start_date) {
    return `${bank.mb_start_date.replace('-', '.')}부터 사용 가능`;
  }
  
  if (bank.mb_end_date && currentYM > bank.mb_end_date) {
    return `${bank.mb_end_date.replace('-', '.')}에 사용 종료됨`;
  }
  
  return '';
};

// 통장 상태 텍스트 (모달용)
const getBankStatusText = (bank: BankData): string => {
  const currentYM = `${currentYear.value}-${currentMonth.value}`;
  
  if (!bank.mb_start_date) return '기간 미설정';
  
  if (currentYM < bank.mb_start_date) {
    return '예정';
  }
  
  if (bank.mb_end_date && currentYM > bank.mb_end_date) {
    return '종료';
  }
  
  return '사용중';
};

// 통장 관리 다이얼로그 열기
const openBankManageDialog = () => {
  bankManageDialogOpen.value = true;
  showBankForm.value = false;
  editingBank.value = null;
};

// 통장 폼 초기화
const resetBankForm = () => {
  editingBank.value = null;
  // 기본 시작월을 현재 연월로 설정
  const defaultStartMonth = `${currentYear.value}-${currentMonth.value}`;
  // 기본 사업구분: 첫 번째 사업구분 또는 빈 값
  const defaultBizGubun = facilityBizGubunList.value.length > 0 ? facilityBizGubunList.value[0] : '';
  bankFormData.value = {
    mb_alias: '',
    mb_bank_name: '',
    mb_account_no: '',
    mb_purpose: '',
    mb_biz_gubun: defaultBizGubun,
    mb_start_date: defaultStartMonth,
    mb_end_date: ''
  };
};

// 통장 추가 버튼 클릭 - 폼 표시 후 스크롤
const onAddBankClick = () => {
  showBankForm.value = true;
  resetBankForm();
  
  // nextTick으로 DOM 업데이트 후 스크롤
  nextTick(() => {
    if (bankFormSectionRef.value) {
      bankFormSectionRef.value.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  });
};

// 통장 수정 모드
const editBank = (bank: BankData) => {
  editingBank.value = bank;
  bankFormData.value = {
    mb_alias: bank.mb_alias,
    mb_bank_name: bank.mb_bank_name,
    mb_account_no: bank.mb_account_no,
    mb_purpose: bank.mb_purpose || '',
    mb_biz_gubun: bank.mb_biz_gubun || '',
    mb_start_date: bank.mb_start_date || '',
    mb_end_date: bank.mb_end_date || ''
  };
  showBankForm.value = true;
  
  // nextTick으로 DOM 업데이트 후 스크롤
  nextTick(() => {
    if (bankFormSectionRef.value) {
      bankFormSectionRef.value.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  });
};

// 통장 저장
const saveBank = async () => {
  if (!canSaveBank.value || isSavingBank.value) return;
  
  isSavingBank.value = true;
  try {
    // 사업구분 결정: 폼에서 선택된 값 또는 첫 번째 사업구분 (공통 불가)
    const bizGubun = bankFormData.value.mb_biz_gubun || 
      (facilityBizGubunList.value.length > 0 ? facilityBizGubunList.value[0] : undefined);
    
    if (editingBank.value) {
      // 수정
      await bankbookApi.updateBank(editingBank.value.mb_idx, {
        mb_alias: bankFormData.value.mb_alias,
        mb_bank_name: bankFormData.value.mb_bank_name,
        mb_account_no: bankFormData.value.mb_account_no,
        mb_purpose: bankFormData.value.mb_purpose || undefined,
        mb_biz_gubun: bizGubun,
        mb_start_date: bankFormData.value.mb_start_date || undefined,
        mb_end_date: bankFormData.value.mb_end_date || undefined
      });
    } else {
      // 등록 (m_code = 시설 내부 PK)
      await bankbookApi.createBank({
        m_code: memberCode.value,
        mb_alias: bankFormData.value.mb_alias,
        mb_bank_name: bankFormData.value.mb_bank_name,
        mb_account_no: bankFormData.value.mb_account_no,
        mb_purpose: bankFormData.value.mb_purpose || undefined,
        mb_biz_gubun: bizGubun,
        mb_start_date: bankFormData.value.mb_start_date || undefined,
        mb_end_date: bankFormData.value.mb_end_date || undefined
      });
    }
    
    // 목록 새로고침
    await loadBankList();
    
    showBankForm.value = false;
    editingBank.value = null;
    
    showSaveToast.value = true;
    setTimeout(() => { showSaveToast.value = false; }, 2000);
  } catch (error: any) {
    console.error('통장 저장 오류:', error);
    const errorMessage = error.response?.data?.message || '통장 저장에 실패했습니다.';
    alert(errorMessage);
  } finally {
    isSavingBank.value = false;
  }
};

// 통장 삭제 확인
const confirmDeleteBank = (bank: BankData) => {
  bankToDelete.value = bank;
  deleteBankDialogOpen.value = true;
};

// 통장 삭제
const deleteBank = async () => {
  if (!bankToDelete.value) return;
  
  isDeletingBank.value = true;
  try {
    const res = await bankbookApi.deleteBank(bankToDelete.value.mb_idx);
    
    if (res.data?.deactivated) {
      alert('연결된 통장내역 데이터가 있어 비활성화 처리되었습니다.');
    }
    
    await loadBankList();
    
    deleteBankDialogOpen.value = false;
    bankToDelete.value = null;
  } catch (error: any) {
    console.error('통장 삭제 오류:', error);
    const errorMessage = error.response?.data?.message || '통장 삭제에 실패했습니다.';
    alert(errorMessage);
  } finally {
    isDeletingBank.value = false;
  }
};

// ============================================
// 테스트 데이터 생성 (최고관리자 전용)
// ============================================

const generateTestData = async () => {
  if (!isSuperAdmin.value) {
    alert('최고관리자 권한이 필요합니다.');
    return;
  }
  
  if (!selectedBankCode.value) {
    alert('테스트 데이터를 생성할 통장을 먼저 선택해주세요.');
    return;
  }
  
  const selectedBank = bankList.value.find(b => b.mb_code === selectedBankCode.value);
  const bankAlias = selectedBank?.mb_alias || '';
  
  const confirmMsg = bankAlias.includes('보조금') 
    ? `[${bankAlias}] 통장에 보조금 관련 테스트 데이터 (수입 10건 + 지출 10건)를 생성합니다.\n\n계속하시겠습니까?`
    : `[${bankAlias}] 통장에 테스트 데이터 (수입 50건 + 지출 50건)를 생성합니다.\n\n계속하시겠습니까?`;
  
  if (!confirm(confirmMsg)) return;
  
  isGeneratingTestData.value = true;
  
  try {
    const response = await bankbookApi.generateTestData({
      memberCode: memberCode.value,
      year: currentYear.value,
      month: currentMonth.value,
      bankCode: selectedBankCode.value,
      bankAlias: bankAlias,
      bizGubun: selectedBizGubun.value || null
    });
    
    if (response.data?.success) {
      const { incomeCount, expenseCount } = response.data.data;
      alert(`테스트 데이터 생성 완료!\n수입: ${incomeCount}건, 지출: ${expenseCount}건`);
      await loadCashbookData();
    } else {
      throw new Error(response.data?.message || '테스트 데이터 생성 실패');
    }
  } catch (error: any) {
    console.error('테스트 데이터 생성 오류:', error);
    const errorMessage = error.response?.data?.message || error.message || '테스트 데이터 생성에 실패했습니다.';
    alert(`오류: ${errorMessage}`);
  } finally {
    isGeneratingTestData.value = false;
  }
};

// ============================================
// 기능정의서 가이드: 탭·모달 전환 + 테스트 자료
// ============================================

const waitGuide = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type GuideToastTone = 'info' | 'success' | 'warn';
const guideToast = ref({
  show: false,
  message: '',
  color: 'info' as string,
});

const notifyGuide = (message: string, tone: GuideToastTone = 'info') => {
  const color =
    tone === 'success' ? 'success' : tone === 'warn' ? 'warning' : 'primary';
  guideToast.value = { show: true, message, color };
};

const closeGuideOverlays = () => {
  bankManageDialogOpen.value = false;
  uploadDialogOpen.value = false;
  dictionaryManageModalOpen.value = false;
  accountPairModalOpen.value = false;
  collectionExcelModalOpen.value = false;
  settlementModalOpen.value = false;
  laborRatioModalOpen.value = false;
  closingDialogOpen.value = false;
  splitModalOpen.value = false;
  fundingSettingsOpen.value = false;
  matchDialogOpen.value = false;
  autoDialogOpen.value = false;
  clearAutoPreview();
  adjustWorkbenchRef.value?.closeSettings?.();
};

const selectOneLedgerItemForGuide = async () => {
  const item =
    selectedBankCashbookList.value.find(
      (row) => row.cb_idx != null && Number(row.is_split_item) !== 1
    ) || selectedBankCashbookList.value.find((row) => row.cb_idx != null);
  if (!item?.cb_idx) return;
  const tab: 'in' | 'out' = item.cb_gubun === 'IN' ? 'in' : 'out';
  ledgerListTab.value = tab;
  ledgerSelectedIds.value = [item.cb_idx];
  await nextTick();
  adjustWorkbenchRef.value?.switchTab(tab);
  if (tab === 'in') {
    adjustWorkbenchRef.value?.setIncomeSelection?.([item.cb_idx]);
  } else {
    adjustWorkbenchRef.value?.setExpenseSelection?.([item.cb_idx]);
  }
};

const ensureGuideTestData = async (): Promise<{
  ok: boolean;
  message?: string;
  cancelled?: boolean;
}> => {
  closeGuideOverlays();
  goStep('raw');

  if (!selectedBankCode.value) {
    const first =
      activeBankListForModal.value[0] ||
      bankList.value[0] ||
      null;
    if (!first?.mb_code) {
      window.alert(
        '아직 등록된 통장이 없습니다.\n\n' +
          '1) 위쪽 톱니(통장 관리)에서 통장을 만든 뒤\n' +
          '2) 다시 「기능정의서」→「처음부터 자동으로 보기」를 눌러 주세요.'
      );
      return {
        ok: false,
        cancelled: true,
        message: '통장이 없습니다. 통장 관리에서 먼저 등록해 주세요.',
      };
    }
    const pickOk = window.confirm(
      `안내를 위해 통장 하나를 골라 둘까요?\n\n` +
        `통장 이름: ${first.mb_alias || first.mb_bank_name || first.mb_code}\n` +
        `보는 달: ${currentYear.value}년 ${currentMonth.value}월\n\n` +
        '확인을 누르면 이 통장 기준으로 화면을 보여 줍니다.'
    );
    if (!pickOk) {
      return { ok: false, cancelled: true, message: '통장 선택을 취소했습니다.' };
    }
    selectBank(first.mb_code);
    notifyGuide('통장을 골랐습니다.', 'info');
    await waitGuide(50);
  }

  if (!hasNoBankbookData.value) {
    notifyGuide('이미 있는 내역으로 안내를 이어갑니다.', 'info');
    return { ok: true, message: '이미 있는 통장내역으로 안내합니다.' };
  }

  if (!isSuperAdmin.value) {
    window.alert(
      '이 달 통장내역이 비어 있습니다.\n\n' +
        '그래도 「어디에 뭐가 있는지」는 보여 드릴게요.\n' +
        '나중에 엑셀을 올린 뒤 다시 안내를 실행하면 더 실감 납니다.'
    );
    notifyGuide('빈 화면이어도 위치는 보여 드립니다.', 'warn');
    return {
      ok: true,
      message: '내역이 없어도 위치 안내로 진행합니다.',
    };
  }

  if (!selectedBankCode.value) {
    window.alert('통장을 먼저 선택한 뒤 다시 시도해 주세요.');
    return { ok: false, cancelled: true, message: '통장을 먼저 선택해 주세요.' };
  }

  const selectedBank = bankList.value.find((b) => b.mb_code === selectedBankCode.value);
  const bankAlias = selectedBank?.mb_alias || '';
  const createOk = window.confirm(
    `연습용 데이터가 없습니다.\n\n` +
      `「${bankAlias || '선택한 통장'}」에 샘플 수입·지출을 만들어 볼까요?\n\n` +
      '· 확인: 샘플을 넣고 실제 화면처럼 안내\n' +
      '· 취소: 샘플 없이 「버튼·탭 위치」만 안내'
  );
  if (!createOk) {
    notifyGuide('샘플 없이 위치만 안내합니다.', 'warn');
    return {
      ok: true,
      message: '샘플 없이 위치 안내로 진행합니다.',
    };
  }

  isGeneratingTestData.value = true;
  notifyGuide('연습용 샘플을 만드는 중…', 'info');
  try {
    const response = await bankbookApi.generateTestData({
      memberCode: memberCode.value,
      year: currentYear.value,
      month: currentMonth.value,
      bankCode: selectedBankCode.value,
      bankAlias,
      bizGubun: selectedBizGubun.value || null,
    });
    if (response.data?.success) {
      await loadCashbookData();
      const { incomeCount, expenseCount } = response.data.data || {};
      window.alert(
        `연습용 샘플을 만들었습니다.\n\n` +
          `수입 ${incomeCount ?? '-'}건 · 지출 ${expenseCount ?? '-'}건\n\n` +
          '이제 실제 작업하는 것처럼 화면을 보여 드릴게요.'
      );
      notifyGuide('연습용 샘플 준비 완료', 'success');
      return {
        ok: true,
        message: `연습용 샘플 준비 (수입 ${incomeCount ?? '-'} · 지출 ${expenseCount ?? '-'})`,
      };
    }
    window.alert(response.data?.message || '연습용 샘플을 만들지 못했습니다.');
    return { ok: false, message: response.data?.message || '연습용 샘플을 만들지 못했습니다.' };
  } catch (error: any) {
    console.error('가이드 테스트 자료 생성 오류:', error);
    const msg =
      error?.response?.data?.message || error?.message || '연습용 샘플을 만들지 못했습니다.';
    window.alert(`오류가 났습니다.\n${msg}`);
    return { ok: false, message: msg };
  } finally {
    isGeneratingTestData.value = false;
  }
};

const GUIDE_STEP_NOTIFY: Record<string, string> = {
  'bb-test-ym': '어느 달을 볼지 고르는 곳입니다.',
  'bb-test-banks': '어느 통장을 볼지 고르는 곳입니다.',
  'bb-test-bank-manage': '통장을 새로 등록·수정하는 창을 엽니다.',
  'bb-test-steps': '일 순서를 나타내는 1~4단계 탭입니다.',
  'bb-test-summary': '왼쪽에서 합계(이월·수입·지출·잔액)를 봅니다.',
  'bb-test-conditions': '목록을 좁혀 보는 조건·검색입니다.',
  'bb-test-upload-quick': '엑셀로 통장내역을 올리는 창을 엽니다.',
  'bb-test-ledger': '올린 거래 목록(원장)입니다.',
  'bb-test-account': '오른쪽에서 「계정」을 지정하는 곳입니다.',
  'bb-test-recommend-settings': '이 기관의 계정 연결을 보고, 빈칸에 넣습니다.',
  'bb-test-patterns': '예전에 지정해 둔 적요·계정 목록입니다.',
  'bb-test-pairs': '이 수입으로 나갈 수 있는 지출을 연결해 둡니다.',
  'bb-test-rel': '지출의 「어디서 나왔는지」(상대계정)를 고릅니다.',
  'bb-test-funding': '돈의 출처(자금원천)를 지정합니다.',
  'bb-test-funding-settings': '이 기관의 계정과 자금원천 연결을 보고, 빈칸에 넣습니다.',
  'bb-test-auto': '톱니에서 연결을 보고, 이번 달 빈칸을 미리본 뒤 넣습니다.',
  'bb-test-quick-edit': '항목 하나를 고르면 오른쪽 「적요 수정」에서 적요·금액과 수입/지출을 고칩니다.',
  'bb-test-split': '한 건을 여러 계정으로 나누는 분개입니다.',
  'bb-test-collection': '수급자 청구·입금을 다루는 수납 화면입니다.',
  'bb-test-collection-excel': '수납 내용을 엑셀로 올리는 창입니다.',
  'bb-test-receipt': '통장 지출과 영수증 사진을 맞추는 화면입니다.',
  'bb-test-analysis': '계정별로 모아 보는 참고 화면입니다.',
  'bb-test-closing': '이 달 작업을 확정(마감)하는 곳입니다.',
};

const prepareGuideStep = async (target: string): Promise<BankbookGuidePrepareResult> => {
  closeGuideOverlays();
  await nextTick();

  const ahead = GUIDE_STEP_NOTIFY[target];
  if (ahead) notifyGuide(ahead, 'info');

  let status = '';

  switch (target) {
    case 'bb-test-ym':
    case 'bb-test-banks':
    case 'bb-test-steps':
    case 'bb-test-closing':
    case 'bb-test-more':
    case 'bb-test-summary':
    case 'bb-test-conditions':
    case 'bb-test-auto-ben':
    case 'bb-test-auto':
    case 'bb-test-delete-all':
    case 'bb-test-ledger':
      goStep('raw');
      if (target === 'bb-test-auto' || target === 'bb-test-auto-ben') {
        openMatchDialog('account');
        status = '톱니에서 연결을 보고, 빈칸을 미리본 뒤 넣습니다.';
      } else if (target === 'bb-test-closing' && !canOpenClosing.value && !closingStatus.value?.isClosed) {
        status = '내역이 준비되면 여기서 「이 달 마감」을 누릅니다.';
      }
      break;

    case 'bb-test-heumieum':
      goStep('raw');
      if (!(closingStatus.value?.isClosed && selectedBankCode.value)) {
        status = '마감 후에 쓰는 기능입니다. 지금은 마감 버튼 자리를 보여 드립니다.';
      }
      break;

    case 'bb-test-bank-manage':
      goStep('raw');
      bankManageDialogOpen.value = true;
      status = '통장 이름·계좌를 등록하는 창입니다.';
      break;

    case 'bb-test-memo':
      goStep('raw');
      monthMemoOpen.value = true;
      status = '이달에 남길 메모 칸입니다.';
      break;

    case 'bb-test-labor':
      goStep('raw');
      laborRatioModalOpen.value = true;
      status = '인건비 비율을 자세히 보는 창입니다.';
      break;

    case 'bb-test-upload-quick':
    case 'bb-test-upload-manual':
      goStep('raw');
      if (!isBankbookUploadDisabled.value) {
        uploadDialogOpen.value = true;
        status = '은행 엑셀을 올리는 창입니다.';
      } else {
        status = '마감된 달이라 업로드가 잠겨 있어요. 버튼 위치만 기억해 주세요.';
      }
      break;

    case 'bb-test-settlement':
      goStep('raw');
      settlementModalOpen.value = true;
      status = '예산과 실제를 비교하는 결산표입니다.';
      break;

    case 'bb-test-account':
      goStep('adjust');
      status = '오른쪽에서 거래의 「계정」을 고릅니다.';
      break;

    case 'bb-test-recommend-settings':
      goStep('adjust');
      await nextTick();
      openMatchDialog('account');
      status = '이 기관의 적요와 계정 연결입니다. 빈 계정만 넣습니다.';
      break;

    case 'bb-test-patterns':
      goStep('adjust');
      dictionaryManageModalOpen.value = true;
      status = '예전에 지정해 둔 적요·계정 목록입니다.';
      break;

    case 'bb-test-pairs':
      goStep('adjust');
      accountPairModalOpen.value = true;
      status = '이 수입으로 나갈 수 있는 지출을 미리 짝지어 둡니다.';
      break;

    case 'bb-test-rel':
      goStep('rel');
      status = '지출이 「어느 수입에서 나왔는지」 연결하는 곳입니다.';
      break;

    case 'bb-test-funding':
      goStep('funding');
      status = '돈이 어디서 왔는지(자금원천)를 표시합니다.';
      break;

    case 'bb-test-funding-settings':
      goStep('funding');
      await nextTick();
      openMatchDialog('funding');
      status = '이 기관의 계정과 자금원천 연결입니다. 빈 자금원천만 넣습니다.';
      break;

    case 'bb-test-split':
    case 'bb-test-quick-edit':
      goStep('raw');
      await selectOneLedgerItemForGuide();
      rawWorkTab.value = target === 'bb-test-split' ? 'split' : 'account';
      if (target === 'bb-test-quick-edit') {
        quickEditOpen.value = true;
        status = '항목을 하나 고르면 오른쪽 「적요 수정」에서 적요·금액과 수입/지출을 고칠 수 있습니다.';
      } else if (target === 'bb-test-split') {
        status = ledgerSelectedIds.value.length === 1
          ? '한 건을 여러 계정으로 나누는 분개입니다.'
          : '항목을 하나만 고르면 분개할 수 있어요. 분개 탭 위치입니다.';
      }
      break;

    case 'bb-test-collection':
    case 'bb-test-arrears':
      goStep('collection', { force: true });
      status = hasNoBankbookData.value
        ? '내역이 없어도 수납 탭 자리를 열어 두었습니다.'
        : '수급자 청구·입금을 다루는 수납 화면입니다.';
      break;

    case 'bb-test-collection-excel':
      goStep('collection', { force: true });
      await nextTick();
      if (!isCollectionExcelDisabled.value) {
        collectionExcelModalOpen.value = true;
        status = '수납 내용을 엑셀로 한꺼번에 올리는 창입니다.';
      } else {
        status = '지금은 엑셀 올리기가 잠겨 있어요. 수납 탭·엑셀 버튼 위치입니다.';
      }
      break;

    case 'bb-test-receipt':
      goStep('receipt', { force: true });
      status = hasNoBankbookData.value
        ? '내역이 없어도 영수증 탭을 열어 두었습니다. 나중에 사진과 통장을 맞춥니다.'
        : '통장 지출과 영수증 사진을 같은 날짜로 맞추는 화면입니다.';
      break;

    case 'bb-test-analysis':
      goStep('analysis-reference', { force: true });
      status = hasNoBankbookData.value
        ? '내역이 없어도 계정별내역 탭을 열어 두었습니다.'
        : '계정별로 모아 보는 참고 화면입니다. 여기서는 수정하지 않습니다.';
      break;

    default:
      goStep('raw');
      break;
  }

  await nextTick();
  return { status: status || undefined };
};

const bankbookGuideHost: BankbookGuideHost = {
  ensureData: ensureGuideTestData,
  prepare: prepareGuideStep,
  cleanup: closeGuideOverlays,
  notify: notifyGuide,
};

// ============================================
// 마감 관리 기능
// ============================================

// 마감 상태 조회
const loadClosingStatus = async () => {
  // 통장이 선택되지 않으면 마감 상태 초기화하고 요약 조회
  if (!memberCode.value || !selectedBankCode.value) {
    closingStatus.value = null;
    // 전체 선택 시 마감 요약 상태 조회
    await loadClosingSummary();
    return;
  }
  
  // 특정 통장 선택 시 요약 초기화
  closingSummary.value = null;
  
  try {
    const response = await closingApi.getStatus(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      type: 'bankbook',
      bizGubun: selectedBizGubun.value || undefined,
      bankCode: selectedBankCode.value
    });
    
    if (response.data?.success) {
      closingStatus.value = response.data.data;
    }
  } catch (error) {
    console.error('마감 상태 조회 오류:', error);
  }
};

// 마감 요약 상태 조회 (전체 통장 선택 시)ㅋ
const loadClosingSummary = async () => {
  if (!memberCode.value) {
    closingSummary.value = null;
    return;
  }
  
  try {
    const response = await closingApi.getBankClosingSummary(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      type: 'bankbook',
      bizGubun: selectedBizGubun.value || undefined
    });
    
    if (response.data?.success) {
      closingSummary.value = response.data.data;
    }
  } catch (error) {
    console.error('마감 요약 상태 조회 오류:', error);
    closingSummary.value = null;
  }
};

// 마감 다이얼로그 열기
const openClosingDialog = () => {
  validationResult.value = null;
  closingDialogOpen.value = true;
  runValidation();
};

// 유효성 검사 실행
const runValidation = async () => {
  if (!memberCode.value) return;
  
  isValidating.value = true;
  validationResult.value = null;
  
  try {
    const response = await closingApi.validate(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      type: 'bankbook',
      status: 'account_adjust',
      bizGubun: selectedBizGubun.value || undefined,
      bankCode: selectedBankCode.value || undefined,
      checkPairs: true
    });
    
    if (response.data?.success) {
      validationResult.value = response.data.data;
    }
  } catch (error: any) {
    console.error('유효성 검사 오류:', error);
    validationResult.value = {
      valid: false,
      message: error.response?.data?.message || '유효성 검사 중 오류가 발생했습니다.'
    };
  } finally {
    isValidating.value = false;
  }
};

// 마감 처리 실행
const executeClosing = async () => {
  if (!memberCode.value || !validationResult.value?.valid) return;
  
  isClosing.value = true;
  
  try {
    const response = await closingApi.close(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      type: 'bankbook',
      status: 'account_adjust',
      bizGubun: selectedBizGubun.value || undefined,
      bankCode: selectedBankCode.value || undefined,
      checkPairs: true
    });
    
    if (response.data?.success) {
      closingDialogOpen.value = false;
      await loadClosingStatus();
      
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 2000);
    } else {
      throw new Error(response.data?.message || '마감 처리 실패');
    }
  } catch (error: any) {
    console.error('마감 처리 오류:', error);
    const errorMessage = error.response?.data?.message || error.message || '마감 처리에 실패했습니다.';
    alert(`오류: ${errorMessage}`);
  } finally {
    isClosing.value = false;
  }
};

// 마감 해제 확인
const confirmUnclose = async () => {
  if (!isAdmin.value) {
    alert('마감 해제는 관리자 이상만 가능합니다.');
    return;
  }
  
  // 희망이음 업로드 요청 중일 때는 마감해제 불가
  if (heumieumStatus.value?.status === 'requested') {
    alert('희망이음 업로드 요청이 대기중입니다.\n\n요청을 먼저 취소한 후 마감을 해제해주세요.');
    return;
  }
  
  // 희망이음 업로드 완료 상태 확인
  let confirmMessage = '마감을 해제하시겠습니까?\n마감 해제 후 통장내역을 수정할 수 있습니다.';
  
  if (heumieumStatus.value?.status === 'completed') {
    confirmMessage = '주의: 희망이음 업로드가 완료된 상태입니다.\n\n마감을 해제하면 희망이음 업로드 기록도 함께 삭제됩니다.\n이미 업로드된 데이터는 희망이음에서 별도로 삭제해야 합니다.\n\n계속하시겠습니까?';
  }
  
  if (!confirm(confirmMessage)) return;
  
  isClosing.value = true;
  
  try {
    const response = await closingApi.unclose(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      type: 'bankbook',
      status: 'account_adjust',
      bizGubun: selectedBizGubun.value || undefined,
      bankCode: selectedBankCode.value || undefined
    });
    
    if (response.data?.success) {
      await loadClosingStatus();
      await loadHeumieumStatus();
      
      showSaveToast.value = true;
      setTimeout(() => { showSaveToast.value = false; }, 2000);
    } else {
      throw new Error(response.data?.message || '마감 해제 실패');
    }
  } catch (error: any) {
    console.error('마감 해제 오류:', error);
    const errorMessage = error.response?.data?.message || error.message || '마감 해제에 실패했습니다.';
    alert(`오류: ${errorMessage}`);
  } finally {
    isClosing.value = false;
  }
};

// 마감일시 포맷
const formatClosingDate = (dateStr: string | undefined) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getFullYear()}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

// ============================================
// 희망이음 업로드 관련
// ============================================

// 희망이음 업로드 상태 조회
const loadHeumieumStatus = async () => {
  if (!memberCode.value || !selectedBankCode.value) {
    heumieumStatus.value = null;
    return;
  }
  
  try {
    const response = await closingApi.heumieumStatus(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      bizGubun: selectedBizGubun.value || undefined
    });
    
    if (response.data?.success) {
      const items = response.data.data?.items || [];
      const currentBankItem = items.find((item: any) => item.cc_bank_code === selectedBankCode.value);
      
      if (currentBankItem) {
        heumieumStatus.value = {
          status: currentBankItem.cc_request_status,
          requestAt: currentBankItem.cc_request_at,
          completedAt: currentBankItem.cc_closed_at,
          completeMemo: currentBankItem.cc_complete_memo,
          displayName: currentBankItem.cc_display_name
        };
      } else {
        heumieumStatus.value = null;
      }
    }
  } catch (error) {
    console.error('희망이음 상태 조회 오류:', error);
    heumieumStatus.value = null;
  }
};

// 희망이음 업로드 요청
const requestHeumieumUpload = async () => {
  if (!memberCode.value || !selectedBankCode.value) return;
  
  const bankAlias = getBankAlias(selectedBankCode.value);
  if (!confirm(`${bankAlias} 통장의 희망이음 업로드를 요청하시겠습니까?`)) return;
  
  isRequestingHeumieum.value = true;
  
  try {
    const response = await closingApi.heumieumRequest(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      bizGubun: selectedBizGubun.value || undefined,
      bankCode: selectedBankCode.value,
      bankAlias: bankAlias
    });
    
    if (response.data?.success) {
      await loadHeumieumStatus();
      alert('희망이음 업로드가 요청되었습니다.');
    } else {
      throw new Error(response.data?.message || '요청 실패');
    }
  } catch (error: any) {
    console.error('희망이음 업로드 요청 오류:', error);
    const errorMessage = error.response?.data?.message || error.message || '요청에 실패했습니다.';
    alert(`오류: ${errorMessage}`);
  } finally {
    isRequestingHeumieum.value = false;
  }
};

// 희망이음 업로드 요청 취소
const cancelHeumieumUpload = async () => {
  if (!memberCode.value || !selectedBankCode.value) return;
  
  if (!confirm('희망이음 업로드 요청을 취소하시겠습니까?')) return;
  
  isRequestingHeumieum.value = true;
  
  try {
    const response = await closingApi.heumieumCancel(memberCode.value, {
      year: currentYear.value,
      month: currentMonth.value,
      bankCode: selectedBankCode.value
    });
    
    if (response.data?.success) {
      await loadHeumieumStatus();
      alert('요청이 취소되었습니다.');
    } else {
      throw new Error(response.data?.message || '취소 실패');
    }
  } catch (error: any) {
    console.error('희망이음 업로드 취소 오류:', error);
    const errorMessage = error.response?.data?.message || error.message || '취소에 실패했습니다.';
    alert(`오류: ${errorMessage}`);
  } finally {
    isRequestingHeumieum.value = false;
  }
};

// ============================================
// 라이프사이클
// ============================================

// 초기 데이터 로드 여부
const initialDataLoaded = ref(false);

// 초기 데이터 로드
const loadInitialData = async () => {
  if (initialDataLoaded.value) return;
  initialDataLoaded.value = true;
  
  await loadBankList();
  await loadAccountCodes();
  await loadFundingInferSettings();
  await loadBeneficiaryList();
  await loadCashbookData();
  await loadClosingStatus();
  await loadHeumieumStatus();
  await loadMonthMemo();
  await loadLaborBaseRatio();
};

// 사업구분 변경 감지 → 통장 목록 및 데이터 다시 로드
watch(selectedBizGubun, async (newBizGubun, oldBizGubun) => {
  // 사업구분이 설정되면 초기 데이터 로드 (첫 로드)
  if (newBizGubun && !initialDataLoaded.value) {
    await loadInitialData();
    return;
  }
  
  // 이미 로드된 상태에서 사업구분 변경 시 다시 로드
  if (newBizGubun !== oldBizGubun && initialDataLoaded.value) {
    await loadBankList();
    await loadCashbookData();
    await loadLaborBaseRatio();
  }
}, { immediate: true });

watch(selectedBankCode, () => {
  if (!initialDataLoaded.value) return;
  loadLaborBaseRatio();
});

onMounted(async () => {
  // 단일 사업구분 시설이거나 이미 selectedBizGubun이 설정된 경우 바로 로드
  // (watch에서 처리되지 않은 경우를 대비)
  if (selectedBizGubun.value && !initialDataLoaded.value) {
    await loadInitialData();
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/pages/bankbook';
@import '@/assets/styles/pages/bankbook-test';
</style>
